/**
 * ═══════════════════════════════════════════════════════════════
 * HOME.JS — LOGIMES Platform
 * Description : Interface principale de la plateforme LOGIMES.
 *               Gestion complète du workflow de facturation électronique
 *               conforme au standard TEIF (Tunisie Electronic Invoice Format).
 * Workflow    : DAS ERP → Format TEIF → Signature → ANCE → TTN → Archivage
 * Auth        : Cookies (token, role, name, societe_id)
 * ═══════════════════════════════════════════════════════════════
 */

import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import MesProduits from "./MesProduits";

// ─────────────────────────────────────────────────────────────
// URLS — Points d'entrée des services backend
// ─────────────────────────────────────────────────────────────
const MIDDLEWARE_URL = process.env.REACT_APP_API_URL;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// HELPER — Lecture des cookies d'authentification
// Utilisé pour récupérer : token, role, name, societe_id
// ─────────────────────────────────────────────────────────────
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

// ─────────────────────────────────────────────────────────────
// WORKFLOW STEPS — Les 6 étapes du parcours TEIF
// Chaque étape est associée à un ou plusieurs statuts PostgreSQL
// ─────────────────────────────────────────────────────────────
const WORKFLOW_STEPS = [
  { id: "creation", icon: "📝", label: "Créée",             sublabel: "DAS ERP / Manuelle", color: "#6c63ff", desc: "Facture créée — DAS ERP, saisie manuelle ou import",         statuts: ["C"]                                  },
  { id: "format",   icon: "🔍", label: "Validée ERP",          sublabel: "TEIF",               color: "#0EA5E9", desc: "Transformée au format électronique TEIF",                    statuts: ["V"]                                  },
  { id: "sign",     icon: "🔏", label: "Signée",            sublabel: "ANCE",               color: "#7c3aed", desc: "Signature électronique — Intégration ANCE (ID TRUST / DIGIGO)", statuts: ["SIGNED", "SUBMITTED"]              },
  { id: "final",    icon: "🏛️", label: "Validée / Rejetée", sublabel: "TTN",                color: "#16a34a", desc: "Réponse TTN — Intégration TTN (validation finale)",          statuts: ["ACCEPTED", "ARCHIVED", "REJECTED"] },
];
// ─────────────────────────────────────────────────────────────
// STATUT CONFIG — Mapping statut PostgreSQL → affichage UI
// Chaque statut a : libellé, couleur, fond, icône
// ─────────────────────────────────────────────────────────────
const STATUT_CONFIG = {
  C:         { label: "Créée",         color: "#2563eb", bg: "#dbeafe", icon: "📝" },
  V:         { label: "Validée ERP",       color: "#16a34a", bg: "#dcfce7", icon: "✅" },
  SIGNED:    { label: "Signée",        color: "#7c3aed", bg: "#ede9fe", icon: "🔏" },
  SUBMITTED: { label: "Soumise ANCE",  color: "#0891b2", bg: "#cffafe", icon: "📤" },
  ACCEPTED:  { label: "Acceptée ANCE", color: "#16a34a", bg: "#dcfce7", icon: "🏛️" },
  REJECTED:  { label: "Rejetée",       color: "#dc2626", bg: "#fee2e2", icon: "❌" },
  PENDING:   { label: "En attente",    color: "#d97706", bg: "#fef3c7", icon: "⏳" },
  ARCHIVED:  { label: "Archivée",      color: "#e11d48", bg: "#ffe4e6", icon: "🗄️" },
};
const CERT_STATUS_UI = {
  VALID:     { label: "Certificat valide",          color: "#16a34a", bg: "#dcfce7", icon: "✅" },
  WARNING:   { label: "Certificat bientôt expiré",  color: "#d97706", bg: "#fef3c7", icon: "⚠️" },
  EXPIRED:   { label: "Certificat expiré",          color: "#dc2626", bg: "#fee2e2", icon: "❌" },
  INVALID:   { label: "Certificat invalide",        color: "#dc2626", bg: "#fee2e2", icon: "❌" },
  NOT_FOUND: { label: "Aucun certificat installé",  color: "#dc2626", bg: "#fee2e2", icon: "🔒" },
};
const getCertUI = (status) =>
  CERT_STATUS_UI[status] || { label: status || "Statut inconnu", color: "#64748b", bg: "#f1f5f9", icon: "❓" };
const DAYS_SCALE = 90;
// Retourne la config d'un statut — fallback si statut inconnu
const getStatut = (s) => STATUT_CONFIG[s] || { label: s || "—", color: "#64748b", bg: "#f1f5f9", icon: "❓" };
const AVOIR_STATUT_CONFIG = {
  DRAFT:     { label: "Brouillon",     color: "#64748b", bg: "#f1f5f9", icon: "📝" },
  SUBMITTED: { label: "Soumis ANCE",   color: "#0891b2", bg: "#cffafe", icon: "📤" },
  ACCEPTED:  { label: "Accepté ANCE",  color: "#7c3aed", bg: "#ede9fe", icon: "🏛️" },
  ARCHIVED:  { label: "Archivé",       color: "#16a34a", bg: "#dcfce7", icon: "🗄️" },
  REJECTED:  { label: "Rejeté",        color: "#dc2626", bg: "#fee2e2", icon: "❌" },
};
const getAvoirStatut = (s) => AVOIR_STATUT_CONFIG[s] || { label: s || "Créé", color: "#d97706", bg: "#fef3c7", icon: "📝" };
// ─────────────────────────────────────────────────────────────
// HELPERS AFFICHAGE
// ─────────────────────────────────────────────────────────────

// Formate un montant en 4 décimales avec séparateur français
const formatMontant = (val) => {
  if (val == null || val === "") return "—";
  const num = Number(val);
  if (isNaN(num)) return "—";
  const parts = num.toFixed(3).split(".");
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return parts.join(".");
};
// Retourne l'abréviation devise depuis les données facture
const getDevise = (f) => {
  const d = f?.DeviseAbrege;
  return !d || d === "DTN" ? "TND" : d;
};
// ─────────────────────────────────────────────────────────────
// CLIENTS — Score de complétude + niveau (Gold / Silver / Standard)
// ─────────────────────────────────────────────────────────────
const LETTER_COLORS = {
  A:{bg:"#fce7f3",color:"#be185d"}, B:{bg:"#ede9fe",color:"#7c3aed"},
  C:{bg:"#dbeafe",color:"#1d4ed8"}, D:{bg:"#dcfce7",color:"#15803d"},
  E:{bg:"#fef3c7",color:"#d97706"}, F:{bg:"#fee2e2",color:"#dc2626"},
  G:{bg:"#f0fdf4",color:"#16a34a"}, H:{bg:"#f0f9ff",color:"#0891b2"},
  I:{bg:"#fdf4ff",color:"#9333ea"}, J:{bg:"#fff7ed",color:"#ea580c"},
  K:{bg:"#ecfdf5",color:"#059669"}, L:{bg:"#eff6ff",color:"#2563eb"},
  M:{bg:"#fdf2f8",color:"#c026d3"}, N:{bg:"#f0fdfa",color:"#0d9488"},
  O:{bg:"#fff1f2",color:"#e11d48"}, P:{bg:"#f5f3ff",color:"#7c3aed"},
  Q:{bg:"#fefce8",color:"#ca8a04"}, R:{bg:"#fef2f2",color:"#ef4444"},
  S:{bg:"#f0f9ff",color:"#0284c7"}, T:{bg:"#f7fee7",color:"#65a30d"},
  U:{bg:"#ede9fe",color:"#6d28d9"}, V:{bg:"#fdf4ff",color:"#a21caf"},
  W:{bg:"#fff7ed",color:"#c2410c"}, X:{bg:"#ecfeff",color:"#0e7490"},
  Y:{bg:"#fefce8",color:"#a16207"}, Z:{bg:"#fdf2f8",color:"#9d174d"},
};

const getClientScore = (c) => {
  let score = 0;
  if (c.CodeFiscale || c.MatriculeFiscal) score += 40;
  if (c.Email)                            score += 20;
  if (c.Tel || c.Telephone)               score += 15;
  if (c.Ville)                            score += 15;
  if (c.EORI)                             score += 10;
  return score;
};

const getClientTier = (c) => {
  const score = getClientScore(c);
  const tier = score >= 80
    ? { label:"Gold",     icon:"★", color:"#d97706", bg:"#fef3c7", gradient:"linear-gradient(90deg,#f59e0b,#d97706)" }
    : score >= 50
    ? { label:"Silver",   icon:"◆", color:"#0891b2", bg:"#e0f2fe", gradient:"linear-gradient(90deg,#38bdf8,#0891b2)" }
    : { label:"Standard", icon:"○", color:"#64748B", bg:"#F8FAFC", gradient:"linear-gradient(90deg,#94A3B8,#64748B)" };

  const nom = c.Nom || c.NomClient || c.RaisonSociale || "?";
  const av  = LETTER_COLORS[nom.charAt(0).toUpperCase()] || { bg:"#F1F5F9", color:"#64748B" };
  return { ...tier, score, avatarBg: av.bg, avatarColor: av.color };
};
// Construit les lignes du détail facture (remise, timbre, DC, FODEC...)
const buildDetailRows = (F, getNomClient) => {
  const dev = getDevise(F);
  const n = (v) => (v == null || v === "" || isNaN(Number(v)) ? null : Number(v));
  const money = (v) => (n(v) == null ? "—" : `${formatMontant(n(v))} ${dev}`);

  const ht       = n(F.MontantTotalHT);
  const htRemise = n(F.MontantHTRemise);
  const ttc      = n(F.MontantTotalTTC);
  const timbre   = n(F.MontantTimbre ?? F.MontantTotalFett);
  const taux     = n(F.TauxRemise);
  const tauxArr  = taux != null ? Math.round(taux * 100) / 100 : null;   // 2.9999999 -> 3
  const montantRemise = ht != null && htRemise != null
    ? Math.round((ht - htRemise) * 1000) / 1000 : null;

  let remise = "—";
  if (tauxArr != null && tauxArr > 0) {
    remise = `${tauxArr} %` + (montantRemise ? ` (−${formatMontant(montantRemise)} ${dev})` : "");
  } else if (tauxArr === 0) {
    remise = "0 %";
  }

  // TVA fiable = déduite (TTC - HT - Timbre) ; on ne fait confiance aux
  // composantes MontantTVA1..4 que si leur somme correspond à ce total déduit.
  const tvaDeduite = (ttc != null && htRemise != null)
    ? Math.round((ttc - htRemise - (timbre || 0)) * 1000) / 1000
    : (ttc != null && ht != null)
      ? Math.round((ttc - ht - (timbre || 0)) * 1000) / 1000
      : null;

  const tvaRows = (() => {
    const comps = [1, 2, 3, 4]
      .map(k => ({ taux: n(F[`TauxTVA${k}`]), montant: n(F[`MontantTVA${k}`]) }))
      .filter(c => c.montant != null && c.montant !== 0);
    const sumComps = comps.reduce((a, c) => a + c.montant, 0);
    const compsReliable = comps.length > 0 && tvaDeduite != null &&
      Math.abs(sumComps - tvaDeduite) < 0.05;

    if (compsReliable) {
      return comps.map((c, i) => [
        `TVA${comps.length > 1 ? i + 1 : ""}${c.taux != null ? ` (${c.taux}%)` : ""}`,
        money(c.montant),
      ]);
    }
    return [["TVA", tvaDeduite != null ? money(tvaDeduite) : money(F.MontantTVA ?? F.MontantTVA1)]];
  })();

  const st = getStatut(F.Statut);

  return [
    ["N° Facture",      F.NumfactAffich || F.NumFacture],
    ["N° Comptable",    F.NumFactureComptable],
    ["Client",          F.NomClient || getNomClient(F.CodeClient)],
    ["Date",            F.DateFacture ? new Date(F.DateFacture).toLocaleDateString("fr-FR") : "—"],
    ["Montant HT",      money(F.MontantTotalHT)],
    ["Remise",          remise],
    ["HT après remise", money(F.MontantHTRemise)],
    ...tvaRows,
    ["Timbre",          money(F.MontantTimbre ?? F.MontantTotalFett)],
    ...(n(F.AutreFrais) != null ? [["Autres frais", money(F.AutreFrais)]] : []),
    ["DC",              money(F.MontantDC)],
    ["FODEC",           money(F.MontantFodec)],
    ["Montant TTC",     money(F.MontantTotalTTC)],
    ["Statut",          `${st.icon} ${st.label}`],
  ];
};

// ─────────────────────────────────────────────────────────────
// NAVIGATION SIDEBAR
// Structure des sections et éléments du menu latéral
// ─────────────────────────────────────────────────────────────
const NAV_SECTIONS = [
  {
    id: "factures", label: "Mes Factures", color: "#6c63ff",
    items: [
      { icon: "✨", label: "Nouvelle Facture", path: "/facture/create", hideIfDas: true },
      { icon: "🧾", label: "Mes Factures",     tab: "factures"      },
      { icon: "👥", label: "Mes Clients",      tab: "clients"       },
      { icon: "📦", label: "Mes Produits",     tab: "produits"      },
      { icon: "🔍", label: "Traçabilité",      tab: "trace"         },
    ],
  },
  {
    id: "monitoring", label: "Monitoring", color: "#5c8df6",
    items: [
      { icon: "📡", label: "Dashboard",        tab: "dashboard"     },
    ],
  },
  {
    id: "avoirs", label: "Avoirs", color: "#e11d48",
    items: [
      { icon: "↩️", label: "Avoirs / Crédits", tab: "avoirs"        },
    ],
  },
  {
    id: "offline", label: "Sync & Offline", color: "#0891b2",
    items: [
      { icon: "📡", label: "Sync Status",      tab: "offline"       },
      { icon: "🔒", label: "Certificat ANCE",  tab: "certificat"    },
    ],
  },
  {
    id: "societe", label: "Ma Société", color: "#f59e0b",
    items: [
      { icon: "🏢", label: "Modifier société", tab: "societe_modal" },
    ],
  },
  {
    id: "settings", label: "Paramètres", color: "#9b59b6",
    items: [
      { icon: "👤", label: "Mon Profil",        tab: "profile_modal" },
    ],
  },
];

// ─────────────────────────────────────────────────────────────
// TABS BAR — Onglets de navigation principale
// ─────────────────────────────────────────────────────────────
const TABS = [
  { id: "accueil",    label: "Accueil",      icon: "🏠" },
  { id: "factures",   label: "Mes Factures", icon: "🧾" },
  { id: "clients",    label: "Mes Clients",  icon: "👥" },
  { id: "trace",      label: "Traçabilité",  icon: "🔍" },
  { id: "dashboard",  label: "Dashboard",    icon: "📡" },
  { id: "avoirs",     label: "Avoirs",       icon: "↩️" },
  { id: "offline",    label: "Offline/Sync", icon: "📡" },
  { id: "certificat", label: "Certificat",   icon: "🔒" },
];

// ─────────────────────────────────────────────────────────────
// EXPORT HELPERS — Export CSV et Excel des factures
// ─────────────────────────────────────────────────────────────

const exportCSV = (factures) => {
  const headers = ["N° Facture", "N° Comptable", "Code Client", "Date", "HT (TND)", "TVA (TND)", "TTC (TND)", "Statut"];
  const rows    = factures.map(f => [
    f.NumFacture,
    f.NumFactureComptable || "",
    f.CodeClient          || "",
    f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "",
    f.MontantTotalHT  != null ? Number(f.MontantTotalHT).toFixed(3)  : "",
    f.MontantTVA1     != null ? Number(f.MontantTVA1).toFixed(3)     : "",
    f.MontantTotalTTC != null ? Number(f.MontantTotalTTC).toFixed(3) : "",
    getStatut(f.Statut).label,
  ]);
  const csv  = [headers, ...rows].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
  const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url;
  a.download = `factures_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const exportExcel = (factures) => {
  const headers = ["N° Facture", "N° Comptable", "Code Client", "Date", "HT (TND)", "TVA (TND)", "TTC (TND)", "Statut"];
  const rows    = factures.map(f => [
    f.NumFacture,
    f.NumFactureComptable || "—",
    f.CodeClient          || "—",
    f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—",
    f.MontantTotalHT  != null ? Number(f.MontantTotalHT).toFixed(3)  : "—",
    f.MontantTVA1     != null ? Number(f.MontantTVA1).toFixed(3)     : "—",
    f.MontantTotalTTC != null ? Number(f.MontantTotalTTC).toFixed(3) : "—",
    getStatut(f.Statut).label,
  ]);
  const html = `<table>
    <tr>${headers.map(h => `<th>${h}</th>`).join("")}</tr>
    ${rows.map(r => `<tr>${r.map(v => `<td>${v}</td>`).join("")}</tr>`).join("")}
  </table>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8;" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href = url;
  a.download = `factures_${new Date().toISOString().slice(0, 10)}.xls`;
  a.click();
  URL.revokeObjectURL(url);
};

// ─────────────────────────────────────────────────────────────
// HOOK — Compteur animé (utilisé pour les KPIs du dashboard)
// Anime un nombre de 0 jusqu'à la valeur cible en 1500ms
// ─────────────────────────────────────────────────────────────
const useCounter = (target, duration = 1500) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!target) return;
    let start    = 0;
    const step   = target / (duration / 16);
    const timer  = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 16);
    return () => clearInterval(timer);
    }, [target]);
  return count;
};

// Composant wrapper pour le compteur animé
const AnimCount = ({ n }) => { const c = useCounter(n); return c; };

// Petit bloc skeleton réutilisable — largeur/hauteur personnalisables
const Skeleton = ({ width = "60%", height = 14 }) => (
  <span className="skeleton" style={{ width, height, verticalAlign: "middle" }} />
);


// ═════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL — Home
// ═════════════════════════════════════════════════════════════
  function Home() {
  const navigate = useNavigate();
  const location = useLocation();

  // ── État UI ───────────────────────────────────────────────
  const [activeTab,        setActiveTab]        = useState("accueil");
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen,   setMobileMenuOpen]   = useState(false);
  const [toast,            setToast]            = useState(null);

  // ── Utilisateur connecté ──────────────────────────────────
  const [userName,    setUserName]    = useState("");
  const [userRole,    setUserRole]    = useState("user");
  const [societeNom,  setSocieteNom]  = useState("LOGIMES");
  const [societeLogo, setSocieteLogo] = useState(null);

  // ── Factures ──────────────────────────────────────────────
  const [factures,    setFactures]    = useState([]);
  const [factLoading, setFactLoading] = useState(true);

  // ── Filtres avancés ───────────────────────────────────────
  const [search,           setSearch]           = useState("");
  const [filterStatut,     setFilterStatut]     = useState("all");
  const [filterReg, setFilterReg] = useState("all");
  const [filterDateFrom,   setFilterDateFrom]   = useState("");
  const [filterDateTo,     setFilterDateTo]     = useState("");
  const [filterMontantMin, setFilterMontantMin] = useState("");
  const [filterMontantMax, setFilterMontantMax] = useState("");
  const [filterClient,     setFilterClient]     = useState("");
  const [showAdvFilters,   setShowAdvFilters]   = useState(false);
  const [exportMenuOpen,   setExportMenuOpen]   = useState(false);
  const exportMenuRef = useRef(null);

  // ── Notifications ─────────────────────────────────────────
  const [notifications, setNotifications] = useState([]);
  const [notifOpen,     setNotifOpen]     = useState(false);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const notifRef        = useRef(null);
  const pollingRef      = useRef(null);
  const prevFacturesRef = useRef([]);

  // ── Traçabilité ───────────────────────────────────────────
  const [selectedFact, setSelectedFact] = useState(null);
  const [traceData,    setTraceData]    = useState(null);
  const [traceLoading, setTraceLoading] = useState(false);
  const [traceSearch,  setTraceSearch]  = useState("");

  // ── Vérification intégrité SHA-256 ───────────────────────
  const [verifyResults, setVerifyResults] = useState({});

  // ── Retry manuel ─────────────────────────────────────────
  const [retryLoading, setRetryLoading] = useState({});

  // ── Téléchargement XML ────────────────────────────────────
  const [xmlLoading, setXmlLoading] = useState({});

  // ── Dashboard ─────────────────────────────────────────────
  const [dashData,    setDashData]    = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [allYearsFactures, setAllYearsFactures] = useState([]);
  const [allYearsLoading,  setAllYearsLoading]  = useState(false);
    const [clientRepYear, setClientRepYear] = useState(new Date().getFullYear());

  // ── PDF Fiscal ────────────────────────────────────────────
  const [pdfLoading, setPdfLoading] = useState({});

  // ── Modal détail facture ──────────────────────────────────
  const [detailModal, setDetailModal] = useState(null);
  const [pinModal, setPinModal] = useState(null);
  const [detailData,    setDetailData]    = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showLignes, setShowLignes] = useState(false);

  // ── Avoirs ───────────────────────────────────────────────
  const [avoirs,        setAvoirs]        = useState([]);
  const [avoirsLoading, setAvoirsLoading] = useState(false);
  const [avoirModal,    setAvoirModal]    = useState(null);
  const [avoirSearch,       setAvoirSearch]       = useState("");
  const [avoirFilterStatut, setAvoirFilterStatut] = useState("all");

  // ── Clients ───────────────────────────────────────────────
  const [clients,        setClients]        = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch,   setClientSearch]   = useState("");
  const [clientSort, setClientSort] = useState("nom");
    const [clientsFactOnly, setClientsFactOnly] = useState(true);


  // ── Mode Offline / Sync ───────────────────────────────────
  const [offlineStatus,  setOfflineStatus]  = useState(null);
  const [offlineQueue,   setOfflineQueue]   = useState([]);
  const [offlineLoading, setOfflineLoading] = useState(false);

  // ── Certificat ANCE ───────────────────────────────────────
  const [certInfo,    setCertInfo]    = useState(null);
  const [certLoading, setCertLoading] = useState(false);
  const [certUploadModal, setCertUploadModal] = useState(null);
  const [certTesting, setCertTesting] = useState(false);
  const [certHistory, setCertHistory] = useState([]);
  const [certHistoryLoading, setCertHistoryLoading] = useState(false);

  // ── Période affichée ──────────────────────────────────────
  const [periode, setPeriode] = useState("");
  const [userExercice, setUserExercice] = useState(
  parseInt(localStorage.getItem("user_bridge_exercice")) || new Date().getFullYear()
  );

  // ── Alertes système ───────────────────────────────────────
  const [alertes, setAlertes] = useState([]);

  // ── Modal société ─────────────────────────────────────────
  const [societeModalOpen,   setSocieteModalOpen]   = useState(false);
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileForm, setProfileForm] = useState({ tel: "", nom: "" });
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);
  const [profileSaving, setProfileSaving] = useState(false);
  const [societeForm, setSocieteForm] = useState({ adresse: "", telephone: "" });
  const [societeMf, setSocieteMf] = useState({ mf: "", editable: false, source: "", loading: false });
  const [societeLogoFile,    setSocieteLogoFile]    = useState(null);
  const [societeLogoPreview, setSocieteLogoPreview] = useState(null);
  const [societeSaving,      setSocieteSaving]      = useState(false);

  // ── Modal test TEIF ───────────────────────────────────────
  const [teifModal,   setTeifModal]   = useState(null);
  const [teifLoading, setTeifLoading] = useState(false);
  const [teifFixes,   setTeifFixes]   = useState({});
  // ── Recherche rapide (Ctrl+K) ─────────────────────────────
  const [quickSearchOpen,  setQuickSearchOpen]  = useState(false);
  const [quickSearchQuery, setQuickSearchQuery] = useState("");
  const quickSearchInputRef = useRef(null);
  // ─────────────────────────────────────────────────────────
  // INITIALISATION AU MONTAGE DU COMPOSANT
  // Lecture des cookies, chargement des données initiales,
  // démarrage du polling et des listeners d'événements
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    const name    = getCookie("name");
    const role    = getCookie("role");
    const token   = getCookie("token");
    const sid     = getCookie("societe_id");
    const societe = getCookie("societe");

    if (name)    setUserName(name);
    if (role)    setUserRole(role);
    if (!token)  { navigate("/"); return; }

        // Nom société — affichage rapide depuis cookie en attendant l'API
    if (societe) setSocieteNom(societe);

    // Toujours rafraîchir depuis l'API pour garantir un nom à jour
    if (sid) {
      fetch(`${MIDDLEWARE_URL}/companies/${sid}`, {
        headers: { Authorization: `Bearer ${token}` }
      })
        .then(r => r.json())
        .then(data => {
          const nom = data.name || data.nom;
          if (nom) setSocieteNom(nom);
        })
        .catch(() => {});
    }

    // Logo société depuis localStorage
    const savedLogo = localStorage.getItem(`logo_${sid}`);
    if (savedLogo) setSocieteLogo(savedLogo);

    // Restaurer les notifications depuis localStorage
    const savedNotifs = localStorage.getItem(`logimes_notifications_${getCookie("societe_id") || "default"}`);
    if (savedNotifs) {
      try {
        const parsed = JSON.parse(savedNotifs);
        setNotifications(parsed);
        setUnreadCount(parsed.filter(n => !n.read).length);
      } catch {}
    }

    // Chargement initial des données
    setTimeout(() => { fetchFactures(); fetchDashboard(); }, 100);
    fetchAvoirs();
    fetchClients();
    fetchOfflineStatus();
    fetchCertificat();
    fetchCertHistory();

    // Fermer les menus déroulants au clic extérieur
    const handleClick = (e) => {
      if (notifRef.current      && !notifRef.current.contains(e.target))      setNotifOpen(false);
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) setExportMenuOpen(false);
    };
        document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // ─────────────────────────────────────────────────────────
  // RECHERCHE RAPIDE — Ctrl+K / Cmd+K pour ouvrir, Echap pour fermer
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    const handleKeyDown = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        console.log("Ctrl+K détecté !");
        setQuickSearchOpen(true);
        setQuickSearchQuery("");
      }
      if (e.key === "Escape") {
        setQuickSearchOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Focus automatique sur le champ de recherche à l'ouverture
  useEffect(() => {
    if (quickSearchOpen && quickSearchInputRef.current) {
      setTimeout(() => quickSearchInputRef.current?.focus(), 50);
    }
  }, [quickSearchOpen]);
  // ─────────────────────────────────────────────────────────
  // RECHARGEMENT AUTOMATIQUE — quand l'utilisateur change d'exercice
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchFactures();
  }, [userExercice]);

// ═════════════════════════════════════════════════════════
  // HELPERS — Fonctions utilitaires internes
  // ═════════════════════════════════════════════════════════

  // Construit les headers d'authentification pour chaque requête API
  const authHeaders = () => ({
    "Content-Type": "application/json",
    Authorization: `Bearer ${getCookie("token")}`,
  });

  // Affiche un message toast temporaire (4 secondes)
  // type : "success" | "error" | "info"
  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };
useEffect(() => {
  if (!societeModalOpen) return;
  setSocieteMf(p => ({ ...p, loading: true }));
  fetch(`${MIDDLEWARE_URL}/societes/${getCookie("societe_id")}/mf`, { headers: authHeaders() })
    .then(async r => {
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || `Erreur ${r.status}`);
      }
      return r.json();
    })
    .then(data => {
      setSocieteMf({ mf: data.mf || "", editable: !!data.editable, source: data.source || "", loading: false });
    })
    .catch(e => {
      setSocieteMf({ mf: "", editable: false, source: "", loading: false });
      showToast(`Matricule fiscal : ${e.message}`, "error");
    });
}, [societeModalOpen]);

  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const st = p.get("digigo");
    if (!st) return;
    const num = p.get("facture");
    if (st === "ok") {
      showToast(`✅ Facture #${num} signée Digigo (XAdES) — DSS ACCEPTED`);
      if (num) setFactures(prev => prev.map(f => String(f.NumFacture) === String(num) ? { ...f, Statut: "SIGNED" } : f));
    } else {
      showToast(`❌ Signature Digigo : ${p.get("message") || p.get("error") || "erreur"}`);
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, []);



  // Ajoute une nouvelle notification dans la liste
  // Les 50 dernières notifications sont conservées dans localStorage
  const addNotification = useCallback((notif) => {
    setNotifications(prev => {
      const updated = [{ ...notif, id: Date.now(), time: new Date(), read: false }, ...prev].slice(0, 50);
      localStorage.setItem(`logimes_notifications_${getCookie("societe_id") || "default"}`, JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(prev => prev + 1);
  }, []);

  // Marque toutes les notifications comme lues
  const markAllRead = () => {
    setNotifications(prev => {
      const updated = prev.map(n => ({ ...n, read: true }));
      localStorage.setItem(`logimes_notifications_${getCookie("societe_id") || "default"}`, JSON.stringify(updated));
      return updated;
    });
    setUnreadCount(0);
  };

  // Supprime toutes les notifications
  const clearNotifications = () => {
    setNotifications([]);
    setUnreadCount(0);
    localStorage.removeItem(`logimes_notifications_${getCookie("societe_id") || "default"}`);
  };

  // ═════════════════════════════════════════════════════════
  // POLLING — Vérification automatique des mises à jour
  // Appelé toutes les 30 secondes pour détecter les
  // changements de statut et les nouvelles factures
  // ═════════════════════════════════════════════════════════
    const checkForUpdates = useCallback(async () => {
    try {
      const societeId  = getCookie("societe_id");
      const clientType = getCookie("client_type"); 
      const exercice = userExercice;
      const params    = new URLSearchParams();
      if (societeId) params.append("societe_id", societeId);
      params.append("exercice", exercice);

      const endpoint     = clientType === "standalone" ? "/factures/manuelles" : "/factures";
      const res          = await fetch(`${MIDDLEWARE_URL}${endpoint}?${params}`, { headers: authHeaders() });
      const data        = await res.json();
      const newFactures = (data.factures || data || []).map(f => ({
        ...f,
        Statut: f.Statut || "CREATED"
      }));

      const prev = prevFacturesRef.current;

      // Notifier les changements de statut
      newFactures.forEach(nf => {
        const old = prev.find(p => p.NumFacture === nf.NumFacture);
        if (old && old.Statut !== nf.Statut) {
          addNotification({
            type:       nf.Statut === "REJECTED" ? "error" : nf.Statut === "ACCEPTED" ? "success" : "info",
            title:      `Facture #${nf.NumfactAffich || nf.NumFacture} mise à jour`,
            message:    `${getStatut(old.Statut).icon} → ${getStatut(nf.Statut).icon} ${getStatut(nf.Statut).label}`,
            factureNum: nf.NumFacture,
          });
        }
      });

      // Notifier les nouvelles factures reçues depuis DAS ERP
      newFactures.forEach(nf => {
        if (!prev.find(p => p.NumFacture === nf.NumFacture)) {
          addNotification({
            type:       "info",
            title:      `Nouvelle facture #${nf.NumfactAffich || nf.NumFacture}`,
            message:    "Facture reçue depuis DAS ERP Paradox",
            factureNum: nf.NumFacture,
          });
        }
      });

      prevFacturesRef.current = newFactures;
      setFactures(prev => {
          return newFactures.map(nf => ({
              ...nf,
              Statut: nf.Statut || 
                      prev.find(p => p.NumFacture === nf.NumFacture)?.Statut || 
                      "CREATED"
          }));
      });
    } catch {}
  }, [addNotification, userExercice]);

  // ─────────────────────────────────────────────────────────
  // POLLING AUTOMATIQUE — Rafraîchissement toutes les 30s
  // Met à jour les statuts des factures depuis le middleware
  // ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    pollingRef.current = setInterval(() => checkForUpdates(), 30000);
    return () => clearInterval(pollingRef.current);
  }, [checkForUpdates]);

  // ═════════════════════════════════════════════════════════
  // FETCH — Chargement des factures
  // Récupère les factures depuis le middleware (Bridge Paradox)
  // enrichies avec le statut TEIF depuis l'archive PostgreSQL
  // ═════════════════════════════════════════════════════════
    const fetchFactures = useCallback(async () => {
    setFactLoading(true);
    try {
      const societeId = getCookie("societe_id");
      const clientType = getCookie("client_type"); // "erp" | "standalone"
      const exercice = userExercice;
      const params    = new URLSearchParams();
      if (societeId) params.append("societe_id", societeId);
      params.append("exercice", exercice);

      // standalone (pas d'ERP) → lit depuis PostgreSQL (factures_manuelles)
      // erp → lit depuis le Bridge DAS ERP (comportement d'origine)
      const endpoint = clientType === "standalone" ? "/factures/manuelles" : "/factures";
      const res  = await fetch(`${MIDDLEWARE_URL}${endpoint}?${params}`, { headers: authHeaders() });
      const data = await res.json();

      const facturesWithStatut = (data.factures || data || []).map(f => ({
        ...f,
        Statut: f.Statut || f.StatutCommande || null
      }));

      prevFacturesRef.current = facturesWithStatut;
      setFactures(facturesWithStatut);

      // Afficher la période dans le header si disponible
      if (data.periode?.libelle) setPeriode(data.periode.libelle);

    } catch { showToast("Erreur chargement factures", "error"); }
    setFactLoading(false);
    }, [userExercice]);

  // ═════════════════════════════════════════════════════════
  // FETCH — Dashboard (données KPIs)
  // ═════════════════════════════════════════════════════════
    const fetchDashboard = async () => {
    setDashLoading(false);
    setDashData(null);
  };

  // ═════════════════════════════════════════════════════════
  // FETCH — Factures de toutes les années (pour Évolution CA)
  // Appelé à chaque ouverture du tab Dashboard
  // ═════════════════════════════════════════════════════════
  const fetchAllYearsFactures = async () => {
    setAllYearsLoading(true);
    try {
      const societeId  = getCookie("societe_id");
      const clientType = getCookie("client_type");
      const endpoint    = clientType === "standalone" ? "/factures/manuelles" : "/factures";
      const years       = [2022, 2023, 2024, 2025, 2026];

      const results = await Promise.all(
        years.map(y => {
          const params = new URLSearchParams();
          if (societeId) params.append("societe_id", societeId);
          params.append("exercice", y);
          return fetch(`${MIDDLEWARE_URL}${endpoint}?${params}`, { headers: authHeaders() })
            .then(r => r.json())
            .then(data => data.factures || data || [])
            .catch(() => []);
        })
      );

      setAllYearsFactures(results.flat());
    } catch {
      setAllYearsFactures([]);
    }
    setAllYearsLoading(false);
  };

  // ═════════════════════════════════════════════════════════
  // FETCH — Avoirs (notes de crédit)
  // ═════════════════════════════════════════════════════════
  const fetchAvoirs = async () => {
    setAvoirsLoading(true);
    try {
      const societeId = getCookie("societe_id");
      const res  = await fetch(`${MIDDLEWARE_URL}/avoirs?societe_id=${societeId}`, { headers: authHeaders() });
      const data = await res.json();
      setAvoirs(data.avoirs || []);
    } catch { showToast("Erreur chargement avoirs", "error"); }
    setAvoirsLoading(false);
  };

  // ═════════════════════════════════════════════════════════
  // FETCH — Clients depuis SaiClie.DB via Bridge
  // ═════════════════════════════════════════════════════════
  const fetchClients = useCallback(async () => {
    setClientsLoading(true);
    try {
      const societeId = getCookie("societe_id");
      const url  = societeId
        ? `${MIDDLEWARE_URL}/clients?societe_id=${societeId}`
        : `${MIDDLEWARE_URL}/clients`;
      const res  = await fetch(url, { headers: authHeaders() });
      const data = await res.json();
      setClients(Array.isArray(data.clients) ? data.clients : Array.isArray(data) ? data : []);
    } catch { showToast("Erreur chargement clients", "error"); }
    setClientsLoading(false);
  }, []);

  // ═════════════════════════════════════════════════════════
  // FETCH — Statut offline + file d'attente + alertes
  // Récupère en parallèle les 3 endpoints avec Promise.all
  // ═════════════════════════════════════════════════════════
  const fetchOfflineStatus = async () => {
    setOfflineLoading(true);
    try {
      const societeId = getCookie("societe_id");
      const [statusRes, queueRes, alertesRes] = await Promise.all([
        fetch(`${MIDDLEWARE_URL}/offline/status?societe_id=${societeId}`,     { headers: authHeaders() }),
        fetch(`${MIDDLEWARE_URL}/offline/queue?societe_id=${societeId}`,      { headers: authHeaders() }),
        fetch(`${MIDDLEWARE_URL}/tracabilite/alerts?societe_id=${societeId}`, { headers: authHeaders() }),
      ]);
      setOfflineStatus(await statusRes.json());
      setOfflineQueue((await queueRes.json()).queue   || []);
      setAlertes((await alertesRes.json()).alerts     || []);
    } catch {}
    setOfflineLoading(false);
  };

  // ═════════════════════════════════════════════════════════
  // FETCH — État du certificat électronique ANCE
  // ═════════════════════════════════════════════════════════
  const fetchCertificat = async () => {
    setCertLoading(true);
    try {
      const societeId = getCookie("societe_id");
      const res = await fetch(`${MIDDLEWARE_URL}/certificate/status?societe_id=${societeId}`, { headers: authHeaders() });
      setCertInfo(await res.json());
    } catch {}
    setCertLoading(false);
  };
  const fetchCertHistory = async () => {
  setCertHistoryLoading(true);
  try {
    const societeId = getCookie("societe_id");
    const res = await fetch(`${MIDDLEWARE_URL}/certificate/history?societe_id=${societeId}`, { headers: authHeaders() });
    const data = await res.json();
    setCertHistory(data.history || []);
  } catch {}
  setCertHistoryLoading(false);
};

const uploadCertificate = async () => {
  if (!certUploadModal?.file || !certUploadModal?.pin) {
    showToast("❌ Fichier et code PIN requis", "error");
    return;
  }
  setCertUploadModal(prev => ({ ...prev, saving: true }));
  try {
    const societeId = getCookie("societe_id");
    const formData = new FormData();
    formData.append("certificate", certUploadModal.file);
    formData.append("pin", certUploadModal.pin);
    const res = await fetch(`${MIDDLEWARE_URL}/certificate/upload?societe_id=${societeId}`, {
      method: "POST",
      headers: { Authorization: `Bearer ${getCookie("token")}` },
      body: formData,
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.detail || "Erreur upload");
    showToast("✅ Certificat installé avec succès");
    setCertUploadModal(null);
    fetchCertificat();
  } catch (e) {
    showToast(`❌ ${e.message}`, "error");
    setCertUploadModal(prev => ({ ...prev, saving: false }));
  }
};

const testCertificateSignature = async () => {
  setCertTesting(true);
  try {
    const societeId = getCookie("societe_id");
    const res = await fetch(`${MIDDLEWARE_URL}/certificate/test?societe_id=${societeId}`, {
      method: "POST", headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok || !data.success) throw new Error(data.message || data.detail || "Test échoué");
    showToast(`✅ ${data.message || "Signature testée avec succès"}`);
  } catch (e) {
    showToast(`❌ ${e.message}`, "error");
  }
  setCertTesting(false);
};

  // ═════════════════════════════════════════════════════════
  // FETCH — Traçabilité d'une facture spécifique
  // Récupère en parallèle : statut actuel + historique complet
  // ═════════════════════════════════════════════════════════
  const fetchTrace = async (numFacture) => {
    setSelectedFact(numFacture);
    setTraceLoading(true);
    setTraceData(null);
    setActiveTab("trace");
    try {
      const [statusRes, histRes] = await Promise.all([
        fetch(`${MIDDLEWARE_URL}/tracabilite/${numFacture}/status?societe_id=${getCookie("societe_id") || ""}`,  { headers: authHeaders() }),
        fetch(`${MIDDLEWARE_URL}/tracabilite/${numFacture}/history?societe_id=${getCookie("societe_id") || ""}`, { headers: authHeaders() }),
      ]);
      setTraceData({
        status:  await statusRes.json(),
        history: (await histRes.json()).history || []
      });
    } catch { setTraceData({ error: "Erreur traçabilité" }); }
    setTraceLoading(false);
  };

  // ═════════════════════════════════════════════════════════
  // ACTION — Synchronisation manuelle des factures offline
  // ═════════════════════════════════════════════════════════
  const handleManualSync = async () => {
    try {
      const societeId = getCookie("societe_id");
      showToast("⏳ Synchronisation en cours...");
      const res  = await fetch(`${MIDDLEWARE_URL}/offline/sync?societe_id=${societeId}`, { method: "POST", headers: authHeaders() });
      const data = await res.json();
      showToast(`✅ Sync terminée — ${data.synced || 0} facture(s) envoyée(s)`);
      fetchOfflineStatus();
    } catch { showToast("Erreur synchronisation", "error"); }
  };

  // ═════════════════════════════════════════════════════════
  // ACTION — Créer un avoir pour une facture
  // ═════════════════════════════════════════════════════════
  const handleCreateAvoir = async (numFacture, reason) => {
    try {
      const societeId = getCookie("societe_id");
      const res  = await fetch(
        `${MIDDLEWARE_URL}/avoirs/create/${numFacture}?reason=${encodeURIComponent(reason)}&societe_id=${societeId}`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur");
      showToast(`✅ Avoir créé — ${data.avoir_number}`);
      fetchAvoirs();
      setAvoirModal(null);
    } catch (e) { showToast(`❌ ${e.message || String(e) || "Erreur création avoir"}`, "error"); }
  };
  // ═════════════════════════════════════════════════════════
  // ACTION — Supprimer une facture manuelle (standalone, BROUILLON)
  // ═════════════════════════════════════════════════════════
  const handleDeleteFacture = async (facture) => {
    if (!window.confirm(`Supprimer la facture #${facture.NumfactAffich || facture.NumFacture} ? Cette action est irréversible.`)) return;
    try {
      const societeId = getCookie("societe_id");
      const res = await fetch(
        `${MIDDLEWARE_URL}/factures/manuelles/${facture.id || facture.NumFacture}?societe_id=${societeId}`,
        { method: "DELETE", headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur suppression");
      showToast(`🗑️ Facture #${facture.NumfactAffich || facture.NumFacture} supprimée`);
      fetchFactures();
    } catch (e) {
      showToast(`❌ ${e.message}`, "error");
    }
  };

  // ═════════════════════════════════════════════════════════
  // ACTION — Finaliser un brouillon (BROUILLON → Créée)
  // Fait entrer la facture manuelle dans le workflow TEIF officiel
  // ═════════════════════════════════════════════════════════
  const finaliserBrouillon = async (facture) => {
    if (!window.confirm(`Finaliser la facture #${facture.NumfactAffich || facture.NumFacture} ? Elle entrera dans le workflow TEIF officiel et ne pourra plus être modifiée ou supprimée directement.`)) return;
    try {
      const societeId = getCookie("societe_id");
      const res = await fetch(
        `${MIDDLEWARE_URL}/factures/manuelles/${facture.id || facture.NumFacture}/finalize?societe_id=${societeId}`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Erreur finalisation");
      showToast(`✅ Facture #${facture.NumfactAffich || facture.NumFacture} finalisée — entrée dans le workflow`);
      fetchFactures();
    } catch (e) {
      showToast(`❌ ${e.message}`, "error");
    }
  };
  // ═════════════════════════════════════════════════════════
  // ACTION — Télécharger le PDF fiscal d'une facture
  // ═════════════════════════════════════════════════════════
  const downloadPDF = async (numFacture) => {
    setPdfLoading(prev => ({ ...prev, [numFacture]: true }));
    try {
      const societeId = getCookie("societe_id");
      const res  = await fetch(
        `${MIDDLEWARE_URL}/factures/${numFacture}/pdf?societe_id=${societeId}`,
        { headers: authHeaders() }
      );
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href = url;
      a.download = `facture_${numFacture}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      showToast(`PDF téléchargé — Facture #${numFacture}`);
    } catch { showToast("Erreur téléchargement PDF", "error"); }
    setPdfLoading(prev => ({ ...prev, [numFacture]: false }));
  };

  // ═════════════════════════════════════════════════════════
  // ACTION — Télécharger le XML TEIF brut (sans signature)
  // Endpoint : GET /factures/{num}/xml-raw
  // ═════════════════════════════════════════════════════════
  const downloadXMLRaw = async (numFacture) => {
    setXmlLoading(prev => ({ ...prev, [`raw_${numFacture}`]: true }));
    try {
      const societeId = getCookie("societe_id");
      const res = await fetch(
        `${MIDDLEWARE_URL}/factures/${numFacture}/xml-raw?societe_id=${societeId}`,
        { headers: authHeaders() }
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob    = await res.blob();
      const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: "application/xml" }));
      const a       = document.createElement("a");
      a.href = blobUrl;
      a.download = `facture_${numFacture}_TEIF.xml`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      showToast(` XML TEIF téléchargé — #${numFacture}`);
    } catch (err) { showToast(` Erreur XML: ${err.message}`, "error"); }
    setXmlLoading(prev => ({ ...prev, [`raw_${numFacture}`]: false }));
  };

  // ═════════════════════════════════════════════════════════
  // ACTION — Télécharger le XML signé archivé
  // Endpoint : GET /archive/download/{num}
  // ═════════════════════════════════════════════════════════
const downloadXML = async (numFacture) => {
    setXmlLoading(prev => ({ ...prev, [numFacture]: true }));
    const affich = factures.find(f => String(f.NumFacture) === String(numFacture))?.NumfactAffich || numFacture;
    try {
      const societeId = getCookie("societe_id");
      const qs = societeId ? `?societe_id=${societeId}` : "";
      // 1) XML signé Digigo (sans re-signature locale)
      let res = await fetch(`${MIDDLEWARE_URL}/factures/${numFacture}/xml-signed${qs}`, { headers: authHeaders() });
      // 2) Sinon : XML archivé
      if (res.status === 404) {
        res = await fetch(`${MIDDLEWARE_URL}/archive/download/${numFacture}${qs}`, { headers: authHeaders() });
      }
      // 3) Rien : pas encore signée / archivée
      if (res.status === 404) {
        showToast(`⏳ Facture #${affich} pas encore signée Digigo / pas encore archivée`, "info");
        return;
      }
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const blob = await res.blob();
      if (blob.size === 0) throw new Error("XML vide");
      const cd = res.headers.get("Content-Disposition") || "";
      const m  = cd.match(/filename="?([^";]+)"?/);
      const fileName = m ? m[1] : `facture_${affich}_TEIF_SIGNED.xml`;
      const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: "application/xml" }));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      showToast(`✅ XML signé téléchargé — #${affich}`);
    } catch (err) {
      showToast(`❌ Erreur XML #${affich}: ${err.message}`, "error");
    } finally {
      setXmlLoading(prev => ({ ...prev, [numFacture]: false }));
    }
  };


// ═════════════════════════════════════════════════════════
  // ACTION — Vérification intégrité SHA-256 d'une facture
  // Endpoint : GET /archive/invoices/{num}/integrity
  // Confirme que le XML archivé n'a pas été modifié
  // ═════════════════════════════════════════════════════════
const verifyIntegrity = async (numFacture) => {
    setVerifyResults(prev => ({ ...prev, [numFacture]: { loading: true } }));
    const affich = factures.find(f => String(f.NumFacture) === String(numFacture))?.NumfactAffich || numFacture;
    try {
      const societeId = getCookie("societe_id");
      const res = await fetch(
        `${MIDDLEWARE_URL}/archive/invoices/${numFacture}/integrity${societeId ? `?societe_id=${societeId}` : ""}`,
        { headers: authHeaders() }
      );
      const data = await res.json().catch(() => ({}));
      const msg  = String(data.detail || data.message || "").toLowerCase();
      const hash = data.stored_hash || data.hash_sha256 || "";
      const validFlag = data.valid ?? data.integrity_valid ?? false;

      // Pas encore archivée (pas de soumission ANCE) → ce n'est PAS un hash invalide
      const pending =
        res.status === 404 ||
        msg.includes("non trouv") ||
        msg.includes("not found") ||
        msg.includes("introuvable") ||
        (!hash && !validFlag);

      if (pending) {
        setVerifyResults(prev => ({
          ...prev,
          [numFacture]: {
            loading: false,
            valid:   false,
            pending: true,
            hash:    "—",
            message: "⏳ Pas encore archivée — soumission ANCE requise",
          }
        }));
        showToast(`⏳ Facture #${affich} pas encore archivée — soumission ANCE requise`, "info");
        return;
      }

      if (!res.ok) throw new Error(data.detail || `Erreur ${res.status}`);

      setVerifyResults(prev => ({
        ...prev,
        [numFacture]: {
          loading: false,
          valid:   validFlag,
          hash:    hash || data.current_hash || "—",
          message: data.message || (validFlag ? "Intégrité confirmée" : "Hash invalide — XML modifié après archivage"),
        }
      }));
      showToast(
        validFlag ? `✅ Intégrité vérifiée — #${affich}` : `❌ Hash invalide — #${affich}`,
        validFlag ? "success" : "error"
      );
    } catch (err) {
      setVerifyResults(prev => ({ ...prev, [numFacture]: { loading: false, error: err.message } }));
      showToast(`❌ Erreur vérification #${affich}: ${err.message}`, "error");
    }
  };


  // ═════════════════════════════════════════════════════════
  // ACTION — Retry manuel d'une facture en échec
  // Endpoint : POST /retry/manual/{num}
  // ═════════════════════════════════════════════════════════
  const handleRetry = async (numFacture) => {
    setRetryLoading(prev => ({ ...prev, [numFacture]: true }));
    try {
      const societeId = getCookie("societe_id");
      showToast(`⏳ Retry en cours — #${numFacture}...`);
      const res  = await fetch(
        `${MIDDLEWARE_URL}/retry/manual/${numFacture}${societeId ? `?societe_id=${societeId}` : ""}`,
        { method: "POST", headers: authHeaders() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || "Erreur retry");

      // Mise à jour du statut local
      const newStatut = data.new_status || data.statut || "SUBMITTED";
      setFactures(prev =>
        prev.map(f => f.NumFacture === numFacture ? { ...f, Statut: newStatut } : f)
      );
      addNotification({
        type:       "success",
        title:      `Retry réussi — #${numFacture}`,
        message:    "Facture resoumise à l'ANCE avec succès",
        factureNum: numFacture,
      });
      showToast(`✅ Retry réussi — Facture #${numFacture} resoumise`);
    } catch (err) {
      showToast(`❌ Retry échoué: ${err.message}`, "error");
    }
    setRetryLoading(prev => ({ ...prev, [numFacture]: false }));
  };

  // ═════════════════════════════════════════════════════════
  // ACTION — Validation format TEIF (Test étape 2)
  // Vérifie que tous les champs obligatoires TEIF sont présents
  // Sources : SaiFact.DB + SaiClie.DB + SaiSoc.DB via Bridge
  // ═════════════════════════════════════════════════════════
  const validateTeifFormat = async (f) => {

    // Données client depuis SaiClie (déjà chargé en mémoire)
    const clientInfo = clients.find(c => (c.Code || c.CodeClient) === f.CodeClient);
    const nomClient  = clientInfo?.Nom || clientInfo?.NomClient || null;
    const mfClient   = clientInfo?.CodeFiscale || clientInfo?.MatriculeFiscal || null;

    // Données société depuis SaiSoc via Bridge
    let nomSociete = societeNom || null;
    let mfSociete  = null;
    try {
      const societeId = getCookie("societe_id");
      const res  = await fetch(`${MIDDLEWARE_URL}/companies/${societeId}`, { headers: authHeaders() });
      const data = await res.json();
      nomSociete = data.name || data.nom || societeNom;
      mfSociete  = data.tax_id || data.matricule_fiscal || null;
    } catch {}

    // Liste des champs obligatoires TEIF avec leur source Paradox
    return [
      // Identification facture
      { label: "N° Facture (DocumentIdentifier)",        value: f.NumfactAffich || f.NumFacture, champ: "NumfactAffich", source: "SaiFact" },
      { label: "Date Facture (DateText I-31)",            value: f.DateFacture,                   champ: "DateFacture",   source: "SaiFact" },
      // Vendeur
      { label: "Nom Société Vendeur (PartnerName I-62)", value: nomSociete,  champ: "NomSociete",       source: "SaiSoc" },
      { label: "Matricule Fiscal Vendeur (I-62)",        value: mfSociete,   champ: "MatriculeFiscal",  source: "SaiSoc" },
      // Client
      { label: "Code Client (PartnerIdentifier I-61)",   value: f.CodeClient, champ: "CodeClient",      source: "SaiFact" },
      { label: "Nom Client (PartnerName I-61)",          value: nomClient,    champ: "NomClient",        source: "SaiClie" },
      { label: "Matricule Fiscal Client",                value: mfClient,     champ: "MFClient",         source: "SaiClie" },
      // Montants
      { label: "Montant HT (I-176)",                     value: f.MontantTotalHT,  champ: "MontantTotalHT",  source: "SaiFact" },
      { label: "Montant TVA (I-181)",                    value: f.MontantTVA1,     champ: "MontantTVA1",     source: "SaiFact" },
      { label: "Montant TTC (I-180)",                    value: f.MontantTotalTTC, champ: "MontantTotalTTC", source: "SaiFact" },
      // Devise et TVA
      { label: "Devise (currencyIdentifier)",            value: f.DeviseAbrege,    champ: "DeviseAbrege",    source: "SaiFact" },
      { label: "Taux TVA (TaxRate)",                     value: f.TauxTVA1 !== undefined ? f.TauxTVA1 : f.AssietTVA1, champ: "TauxTVA1", source: "SaiFact" },
    ].map(c => ({ ...c, ok: c.value !== null && c.value !== undefined && c.value !== "" }));
  };

  // ═════════════════════════════════════════════════════════
  // ACTION — Workflow TEIF
  // Gère les actions : validate / sign / submit-dss / submit-ttn
  // Chaque action correspond à une étape du parcours TEIF
  // ═════════════════════════════════════════════════════════
  const handleAction = async (numFacture, action, label) => {
    const societeId = getCookie("societe_id");
    try {
      showToast(`⏳ ${label} facture #${numFacture}...`);
      if (action === "sign") {
        const r = await fetch(
          `${MIDDLEWARE_URL}/factures/${numFacture}/digigo/start?societe_id=${societeId}`,
          { method: "POST", headers: authHeaders() }
        );
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.detail || d.message || "Erreur Digigo");
        window.open(d.authorize_url, "_blank", "noopener");
        showToast(`🔏 Digigo ouvert — saisissez PIN + OTP (${d.signer})`);
        return;
      }
      const res  = await fetch(
        `${MIDDLEWARE_URL}/factures/${numFacture}/${action}?societe_id=${societeId}`,
        { method: "POST", headers: authHeaders() }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.message || `Erreur ${label}`);

      // Mapping action → nouveau statut TEIF
      const newStatut = {
        "validate":   "V",
        "sign":       "SIGNED",
        "submit-dss": "SUBMITTED",
        "submit-ttn": "ARCHIVED"
      }[action];

      // Mise à jour du statut local immédiatement
      setFactures(prev => prev.map(f =>
        f.NumFacture === numFacture ? { ...f, Statut: newStatut } : f
      ));

      // Message toast selon l'action
      let toastMsg = `✅ Facture #${data.facture_affich || numFacture} — ${label} réussi !`;
      let notifMsg = `Action "${label}" effectuée avec succès`;

      if (action === "submit-dss") {
        const refTTN = data.reference_ttn || data.uuid_fiscal || "";
        if (refTTN) {
          toastMsg = `✅ Facture #${data.facture_affich || numFacture} soumise ANCE — TTN: ${refTTN.slice(0, 20)}...`;
          notifMsg = `Référence TTN: ${refTTN}`;
        }
      }

      addNotification({
        type:       "success",
        title: `Facture #${data.facture_affich || numFacture} — ${label}`,
        message:    notifMsg,
        factureNum: numFacture
      });
      showToast(toastMsg);
    } catch (e) { showToast(`❌ ${e.message}`, "error"); }
  };

  // ═════════════════════════════════════════════════════════
  // COMPUTED — Filtrage des factures
  // Applique tous les filtres actifs sur la liste des factures
  // ═════════════════════════════════════════════════════════

  // Retourne le statut TEIF d'une facture (priorité archive > bridge)
 const getStatutField = (f) => {
    const s = f.Statut || f.status || f.StatutTeif || "";
    // Normaliser les statuts
    if (s === "CREATED" || s === null || s === "") return "C";
    return s;
};

  const STATUT_GROUPS = {
  SIGNED:   ["SIGNED", "SUBMITTED"],
  ACCEPTED: ["ACCEPTED", "ARCHIVED"],
  };
    const facturesAvantReg = factures.filter(f => {
    const q       = search.toLowerCase();
    const clientQ = filterClient.toLowerCase();
    const montant = f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : null;

    // Comparaison par string ISO (YYYY-MM-DD) — évite tout bug de timezone
    const dateFStr = f.DateFacture ? String(f.DateFacture).slice(0, 10) : null;

    // Nom du client associé à cette facture (pour recherche par nom)
    const clientInfo = clients.find(c => (c.Code || c.CodeClient) === f.CodeClient);
    const nomClient   = (clientInfo?.Nom || clientInfo?.NomClient || clientInfo?.RaisonSociale || "").toLowerCase();

        return (
      (!q            || String(f.NumfactAffich || "").toLowerCase().includes(q) || String(f.NumFacture).includes(q) || String(f.CodeClient || "").toLowerCase().includes(q) || nomClient.includes(q)) &&
      (!clientQ      || String(f.CodeClient || "").toLowerCase().includes(clientQ) || nomClient.includes(clientQ)) &&
      (filterStatut === "all" || (STATUT_GROUPS[filterStatut] ? STATUT_GROUPS[filterStatut].includes(getStatutField(f)) : getStatutField(f) === filterStatut)) &&
      (!filterDateFrom   || (dateFStr && dateFStr >= filterDateFrom)) &&
      (!filterDateTo     || (dateFStr && dateFStr <= filterDateTo)) &&
      (!filterMontantMin || (montant != null && montant >= Number(filterMontantMin))) &&
      (!filterMontantMax || (montant != null && montant <= Number(filterMontantMax)))
    );
  });
    const filteredFactures = facturesAvantReg.filter(f => filterReg === "all" || String(f.Reg || "").trim().toUpperCase() === filterReg);


  // Retourne le nom du client à partir de son code — fallback sur le code si non trouvé
  const getNomClient = (codeClient) => {
    const c = clients.find(cl => (cl.Code || cl.CodeClient) === codeClient);
    return c?.Nom || c?.NomClient || c?.RaisonSociale || codeClient || "—";
  };

  // Indique si des filtres avancés sont actifs
  const hasAdvancedFilters = filterDateFrom || filterDateTo || filterMontantMin || filterMontantMax || filterClient;

  // Réinitialise tous les filtres
  const resetFilters = () => {
    setFilterReg("all");
    setSearch(""); setFilterStatut("all"); setFilterDateFrom(""); setFilterDateTo("");
    setFilterMontantMin(""); setFilterMontantMax(""); setFilterClient("");
  };

  // Retourne l'index de l'étape workflow correspondant au statut
  const getWorkflowStep = (statut) => {
    const idx = WORKFLOW_STEPS.findIndex(s => s.statuts.includes(statut));
    return idx >= 0 ? idx : 0;
  };

  // Déconnexion — supprime les cookies et redirige vers login
  const handleLogout = () => {
    ["token", "role", "name", "societe_id", "societe_folder", "societe", "client_type"].forEach(k =>
      document.cookie = `${k}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
    );
    navigate("/login");
};

const openDetail = async (f) => {
  setShowLignes(false);
  setDetailModal(f);
  setDetailData(null);
  setDetailLoading(true);
  try {
    const societeId = getCookie("societe_id");
    const qs = societeId ? `?societe_id=${societeId}` : "";

    // Lignes (/detail) + en-tête complet (remise, timbre, DC, FODEC) en parallèle
    const [detailRes, headerRes] = await Promise.all([
      fetch(`${MIDDLEWARE_URL}/factures/${f.NumFacture}/detail${qs}`, { headers: authHeaders() }),
      fetch(`${MIDDLEWARE_URL}/factures/${f.NumFacture}${qs}`,        { headers: authHeaders() }),
    ]);

    const data   = await detailRes.json();
    const header = headerRes.ok ? await headerRes.json() : null;

    setDetailData({ ...data, ...(header ? { facture: header } : {}) });
  } catch {
    setDetailData({ error: "Erreur chargement détail" });
  }
  setDetailLoading(false);
};

  // Navigation sidebar — gère l'ouverture du modal société
  const handleNavClick = (item) => {
    setMobileMenuOpen(false);
    if (item.tab === "societe_modal") { setSocieteModalOpen(true); return; }
    if (item.tab === "profile_modal") {
      const sid = getCookie("societe_id");
      setProfileForm({
        tel: localStorage.getItem(`tel_${sid}`) || "",
        nom: getCookie("name") || userName || ""
      });
      setProfilePhotoPreview(localStorage.getItem(`avatar_${sid}`) || null);
      setProfileModalOpen(true);
      return;
    }
    if (item.tab === "dashboard") { fetchAllYearsFactures(); }
    if (item.tab)       setActiveTab(item.tab);
    else if (item.path) navigate(item.path);
  };
  // ═════════════════════════════════════════════════════════
  // COMPUTED — Tendance mensuelle (mois courant vs mois précédent)
  // ═════════════════════════════════════════════════════════
    const trends = useMemo(() => {
  const now       = new Date();
  const thisMonth = now.getMonth();
  const thisYear  = now.getFullYear();
  const lastDate  = new Date(thisYear, thisMonth - 1, 1);
  const lastMonth = lastDate.getMonth();
  const lastYear  = lastDate.getFullYear();

  // CA (somme MontantTotalTTC) au lieu du nombre de factures
  const sumInMonth = (month, year, statutFilter) => factures
    .filter(f => {
      if (!f.DateFacture) return false;
      const d = new Date(f.DateFacture);
      if (isNaN(d.getTime()) || d.getMonth() !== month || d.getFullYear() !== year) return false;
      if (!statutFilter) return true;
      return statutFilter.includes(getStatutField(f));
    })
    .reduce((acc, f) => acc + (Number(f.MontantTotalTTC) || 0), 0);

  const calcPct = (statutFilter) => {
    const cur  = sumInMonth(thisMonth, thisYear, statutFilter);
    const prev = sumInMonth(lastMonth, lastYear, statutFilter);
    if (prev === 0) return cur > 0 ? 100 : 0;
    return Math.round(((cur - prev) / prev) * 100);
  };

  return {
    total:    calcPct(null),
    accepted: calcPct(["ACCEPTED", "ARCHIVED", "CREDITED"]),
    pending:  calcPct(["C", "V", "SIGNED", "SUBMITTED", "PENDING"]),
    rejected: calcPct(["REJECTED"]),
  };
}, [factures]);

  // ═════════════════════════════════════════════════════════
  // COMPUTED — Factures nécessitant une attention
  // Rejetées + Créées depuis plus de 7 jours sans avancement
  // ═════════════════════════════════════════════════════════
  const alertFactures = useMemo(() => {
    const now = new Date();
    const rejected = factures.filter(f => getStatutField(f) === "REJECTED");
    const stale = factures.filter(f => {
      if (getStatutField(f) !== "C" || !f.DateFacture) return false;
      const d = new Date(f.DateFacture);
      if (isNaN(d.getTime())) return false;
      const diffDays = (now - d) / (1000 * 60 * 60 * 24);
      return diffDays > 25;
    });
    return { rejected, stale, total: rejected.length + stale.length };
  }, [factures]);

    // ═════════════════════════════════════════════════════════
  // COMPUTED — Facture la plus urgente (la plus ancienne en attente)
  // ═════════════════════════════════════════════════════════
  const mostUrgentFacture = useMemo(() => {
    if (alertFactures.stale.length === 0) return null;
    return [...alertFactures.stale].sort((a, b) => new Date(a.DateFacture) - new Date(b.DateFacture))[0];
  }, [alertFactures]);

  // Facture de la liste + champs complets renvoyés par /detail
  const factureFull = useMemo(() => {
    if (!detailModal) return null;
    const extra  = detailData?.facture || detailData?.header || detailData || {};
    const merged = { ...detailModal };
    Object.entries(extra).forEach(([k, v]) => {
      if (k !== "lignes" && k !== "error" && v !== null && v !== undefined) merged[k] = v;
    });
    return merged;
  }, [detailModal, detailData]);

   // Devise de la facture (depuis la base) — utilisée dans le détail
  const devFact = getDevise(factureFull || detailModal || {});

  // ═════════════════════════════════════════════════════════
  // COMPUTED — Statistiques rapides pour le tableau de bord
  // ═════════════════════════════════════════════════════════
  const quickStats = {
    total:    factures.length,
    accepted: factures.filter(f => ["ACCEPTED", "ARCHIVED", "CREDITED"].includes(getStatutField(f))).length,
    pending:  factures.filter(f => ["C", "V", "SIGNED", "SUBMITTED", "PENDING"].includes(getStatutField(f))).length,
    rejected: factures.filter(f => getStatutField(f) === "REJECTED").length,
  };

  // Données middleware pour le dashboard (depuis PostgreSQL archive)
  const mwStats        = dashData?.stats || {};
  const byStatus       = mwStats.by_status || {};
  const mwTotal        = mwStats.total || 0;
  const mwAccepted     = (byStatus.ACCEPTED || 0) + (byStatus.CREDITED || 0);
  const mwRejected     = byStatus.REJECTED || 0;
  const mwPending      = Math.max(0, mwTotal - mwAccepted - mwRejected);
  const recentInvoices = dashData?.recent_invoices || [];
  const retryData      = dashData?.retry || {};

  // Total TTC des factures filtrées (affiché dans la barre de filtres)
  const totalMontantFiltre = filteredFactures.reduce(
    (acc, f) => acc + (f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : 0), 0
  );
  // Total HT net des factures filtrées (même logique que les cartes)
  const totalHTFiltre = filteredFactures.reduce((acc, f) => {
    const t  = Number(f.MontantTotalTTC) || 0;
    const tb = Number(f.MontantTimbre ?? f.MontantTotalFett) || 0;
    const hb = Number(f.MontantTotalHT) || 0;
    const tx = f.TauxTVA1 != null ? Number(f.TauxTVA1) : null;
    let h = hb;
    if (f.MontantHTRemise != null) h = Number(f.MontantHTRemise);
    else if (tx != null && t) { const c = (t - tb) / (1 + tx / 100); h = c <= hb + 0.01 ? c : hb; }
    return acc + h;
  }, 0);

  // ═════════════════════════════════════════════════════════
  // RENDER — Structure principale
  // Layout : Sidebar fixe + Main scrollable
  // ═════════════════════════════════════════════════════════
  return (
    <div style={S.root}>
      <style>{css}</style>

      {/* ════════════════ SIDEBAR ════════════════ */}
      <aside className={`app-sidebar${mobileMenuOpen ? " mobile-open" : ""}`} style={{ ...S.sidebar, width: sidebarCollapsed ? 60 : 224 }}>

        {/* Logo et nom société */}
        <div style={S.brand} onClick={() => { setActiveTab("accueil"); setMobileMenuOpen(false); }}>
          <div style={S.brandMark}>
            {societeLogo
              ? <img src={societeLogo} alt="logo" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 9 }} />
              : <span>⚡</span>}
          </div>
          {!sidebarCollapsed && <span style={S.brandName}>{societeNom}</span>}
        </div>

        {/* Bouton collapse/expand sidebar — masqué en mode mobile (drawer) */}
        <button className="menu-btn sidebar-collapse-btn" style={S.collapseBtn} onClick={() => setSidebarCollapsed(!sidebarCollapsed)}>
  <span className="menu-ico" style={{ transform: sidebarCollapsed ? "rotate(90deg)" : "rotate(0deg)" }}>☰</span>
</button>

        {/* Menu de navigation */}
        <nav style={S.sidebarNav}>
                    {/* Lien Accueil */}
          <div
            className={`nav-item${activeTab === "accueil" ? " active" : ""}`}
            style={{ ...S.navItem, background: activeTab === "accueil" ? "#6c63ff18" : "transparent", color: activeTab === "accueil" ? "#6c63ff" : "#64748b", borderLeft: activeTab === "accueil" ? "3px solid #6c63ff" : "3px solid transparent", "--c": "#6c63ff", "--i": 0 }}
            onClick={() => { setActiveTab("accueil"); setMobileMenuOpen(false); }}>
            <span className="nav-ico" style={S.navIcon}>🏠</span>
            {!sidebarCollapsed && <span style={S.navLabel}>Accueil</span>}
          </div>

          {/* Sections de navigation */}
          {NAV_SECTIONS.map((sec, si) => (
  <div key={sec.id}>
    {!sidebarCollapsed && (
      <div style={{ ...S.sectionLabel, color: sec.color }}>
        <span style={{ ...S.sectionDot, background: sec.color }} />{sec.label}
      </div>
    )}
    {sidebarCollapsed && <div style={S.sectionDivider} />}
    {sec.items.map((item, ii) => {
      if (item.hideIfDas && getCookie("client_type") === "erp") return null;
      const isActive = item.tab ? activeTab === item.tab : location.pathname === item.path;
      return (
        <div key={item.label}
          className={`nav-item${isActive ? " active" : ""}`}
          style={{ ...S.navItem, background: isActive ? sec.color + "18" : "transparent", color: isActive ? sec.color : "#64748b", borderLeft: isActive ? `3px solid ${sec.color}` : "3px solid transparent", "--c": sec.color, "--i": si * 2 + ii + 1 }}
          onClick={() => handleNavClick(item)}
          title={sidebarCollapsed ? item.label : ""}>
          <span className="nav-ico" style={S.navIcon}>{item.icon}</span>
          {!sidebarCollapsed && <span style={S.navLabel}>{item.label}</span>}
        </div>
      );
    })}
  </div>
))}
        </nav>

        {/* Pied de sidebar — infos utilisateur + déconnexion */}
        <div style={S.sidebarFooter}>
          {!sidebarCollapsed && (
            <div style={S.userCard}>
              <div style={S.userInitials}>{(userName || "U").charAt(0).toUpperCase()}</div>
              <div style={S.userMeta}>
                <div style={S.userNameText}>{userName || "Utilisateur"}</div>
                <div style={S.userRoleTag}>👤 Utilisateur</div>
              </div>
            </div>
          )}
          <button style={S.logoutBtn} className="logout-hover" title="Déconnexion" onClick={handleLogout}>
            🚪 {!sidebarCollapsed && "Déconnexion"}
          </button>
        </div>
      </aside>

      {/* Overlay mobile — ferme le menu au clic à l'extérieur */}
      {mobileMenuOpen && (
        <div className="mobile-overlay" onClick={() => setMobileMenuOpen(false)} />
      )}

      {/* ════════════════ MAIN ════════════════ */}
      <main className="app-main" style={{ ...S.main, marginLeft: sidebarCollapsed ? 60 : 224 }}>

        {/* ── HEADER ── */}
        <header className="app-header" style={S.header}>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(true)}>☰</button>
          <div>
            {/* Breadcrumb société */}
            <div style={S.headerEyebrow}>{userName} · {societeNom}</div>

            {/* Bouton retour admin — visible uniquement pour les admins */}
            {getCookie("role") === "admin" && (
              <button
                style={{ background: "none", border: "none", cursor: "pointer", fontSize: "0.75rem", fontWeight: 600, color: "#6c63ff", fontFamily: "inherit", padding: "0 0 4px", display: "flex", alignItems: "center", gap: 4 }}
                onClick={() => {
                  document.cookie = `societe_id=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                  document.cookie = `societe_folder=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
                  window.location.href = "/admin";
                }}>
                ← Retour Admin
              </button>
            )}

            {/* Titre de la page active */}
            <h1 style={S.headerTitle}>
              {activeTab === "factures" && periode && (
                <div style={{ fontSize: "0.72rem", color: "#6c63ff", fontWeight: 600, marginTop: 2 }}>
                  📅 {periode}
                </div>
              )}
              {activeTab === "accueil"    && "Tableau de bord"}
              {activeTab === "factures"   && "Mes Factures"}
              {activeTab === "produits"   && "Mes Produits"}
              {activeTab === "trace"      && "Traçabilité"}
              {activeTab === "dashboard"  && "Dashboard KPIs"}
              {activeTab === "avoirs"     && "Avoirs & Crédits"}
              {activeTab === "offline"    && "Mode Offline & Sync"}
              {activeTab === "certificat" && "Certificat ANCE"}
            </h1>
          </div>

          {/* Indicateur connexion internet */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 12px", borderRadius: 20, background: offlineStatus?.internet_available ? "#f0fdf4" : "#fff0f0", border: `1px solid ${offlineStatus?.internet_available ? "#bbf7d0" : "#fecaca"}`, fontSize: "0.72rem", fontWeight: 700, color: offlineStatus?.internet_available ? "#16a34a" : "#dc2626" }}>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: offlineStatus?.internet_available ? "#16a34a" : "#dc2626", animation: offlineStatus?.internet_available ? "none" : "notifPulse 1.5s infinite" }} />
            {offlineStatus?.internet_available ? "🟢 Online" : "🔴 Offline"}
            {offlineStatus?.queue_stats?.pending > 0 && (
              <span style={{ background: "#f59e0b", color: "#fff", borderRadius: 10, padding: "1px 6px", fontSize: "0.6rem", marginLeft: 4 }}>
                {offlineStatus.queue_stats.pending} en attente
              </span>
            )}
          </div>

          {/* Actions header — notifications + nouvelle facture + actualiser */}
          <div className="header-actions" style={S.headerActions}>

            {/* Cloche notifications */}
            <div style={{ position: "relative" }} ref={notifRef}>
              <button style={S.notifBtn} onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) markAllRead(); }}>
                <span style={{ fontSize: "1.1rem" }}>🔔</span>
                {unreadCount > 0 && <span style={S.notifBadge} className="notif-pulse">{unreadCount > 9 ? "9+" : unreadCount}</span>}
              </button>

              {/* Panel notifications */}
              {notifOpen && (
                <div style={S.notifPanel}>
                  <div style={S.notifHeader}>
                    <span style={{ fontWeight: 700, fontSize: "0.88rem", color: "#0F172A" }}>🔔 Notifications</span>
                    <button style={S.notifAction} onClick={clearNotifications}>Effacer tout</button>
                  </div>
                  <div style={S.notifList}>
                    {notifications.length === 0 ? (
                      <div style={S.notifEmpty}>
                        <div style={{ fontSize: "1.5rem", marginBottom: 6 }}>🔕</div>
                        <div style={{ fontSize: "0.78rem", color: "#94A3B8" }}>Aucune notification</div>
                      </div>
                    ) : notifications.map(n => (
                      <div key={n.id}
                        style={{ ...S.notifItem, background: n.read ? "#fff" : "#f0efff", borderLeft: `3px solid ${n.type === "error" ? "#dc2626" : n.type === "success" ? "#16a34a" : "#6c63ff"}` }}
                        onClick={() => { if (n.factureNum) { setActiveTab("factures"); setNotifOpen(false); } }}>
                        <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                          <span style={{ fontSize: "1rem", flexShrink: 0 }}>{n.type === "error" ? "❌" : n.type === "success" ? "✅" : "ℹ️"}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: 700, fontSize: "0.78rem", color: "#0F172A", marginBottom: 2 }}>{n.title}</div>
                            <div style={{ fontSize: "0.72rem", color: "#64748B" }}>{n.message}</div>
                          </div>
                        </div>
                        <div style={{ fontSize: "0.65rem", color: "#94A3B8", marginTop: 4 }}>
                          {n.time ? new Date(n.time).toLocaleString("fr-FR") : "—"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Nom utilisateur connecté */}
            {userName && (
              <div style={S.topbarUser}>
                <div style={S.topbarAvatar}>{userName.charAt(0).toUpperCase()}</div>
                <span style={{ fontSize: "0.86rem", color: "#555" }}>Bonjour, <strong>{userName}</strong></span>
              </div>
            )}

            <select
  style={{ ...S.btnSecondary, cursor: "pointer", fontWeight: 700 }}
  value={userExercice}
  onChange={e => {
    const val = parseInt(e.target.value);
    setUserExercice(val);
    localStorage.setItem("user_bridge_exercice", val);
  }}
>
  {[2026, 2025, 2024, 2023, 2022].map(year => (
    <option key={year} value={year}>📅 {year}</option>
  ))}
</select>

            {/* Bouton actualiser */}
            <button style={S.btnSecondary} className="btn-hover" onClick={() => { fetchFactures(); fetchDashboard(); }}>
              ↻ Actualiser
            </button>
          </div>
        </header>

{/* ════════════════ CONTENU DES TABS ════════════════ */}
        <div className="content-wrap" style={S.content}>

          {/* ══════════════════════════════════════════════════
              TAB : ACCUEIL — Tableau de bord
              Affiche : hero, statistiques, workflow TEIF,
              dernières factures, activité récente
          ══════════════════════════════════════════════════ */}
          {activeTab === "accueil" && (
            <div style={S.tabContent}>

              {/* ── Hero section ── */}
              <div className="home-hero" style={S.hero}>
                <div className="home-hero-content" style={S.heroContent}>
                  <div style={S.heroTag}>© 2026 {societeNom}</div>
                  <div style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    background: "#f0f0ff",
                    color: "#6c63ff",
                    fontWeight: 600,
                    fontSize: "0.82rem",
                    padding: "6px 12px",
                    borderRadius: 20,
                    marginBottom: 12
                  }}>
                    📅 Période de traitement : du 01/01/{new Date().getFullYear()} au {new Date().toLocaleDateString("fr-FR")}
                  </div>
                  <h2 className="home-hero-title" style={S.heroTitle}>
                    Bienvenue sur <span style={{ color: "#6c63ff" }}>{societeNom}</span>
                  </h2>
                  <p style={S.heroDesc}>
                    Suivez le parcours complet de vos factures électroniques — de DAS ERP Paradox jusqu'à l'archivage légal ANCE Tunisie.
                  </p>
                  <div className="hero-buttons" style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    <button style={S.heroCta} onClick={() => setActiveTab("factures")}>🧾 Mes factures</button>
                    <button style={S.heroCtaSecondary} onClick={() => setActiveTab("trace")}>🔍 Traçabilité</button>
                    {mostUrgentFacture && (
                      <button
                        style={{ ...S.heroCtaSecondary, background: "#fff7ed", borderColor: "#f59e0b", color: "#ea580c" }}
                        onClick={() => openDetail(mostUrgentFacture)}>
                        ⚡ Facture urgente #{mostUrgentFacture.NumfactAffich || mostUrgentFacture.NumFacture}
                      </button>
                    )}
                  </div>
                </div>
                                {/* Cartes flottantes animées — données réelles */}
                <div className="home-hero-visual" style={S.heroVisual}>
                  {(() => {
                    const derniere  = factures[0];
                    const acceptee  = factures.find(f => ["ACCEPTED", "ARCHIVED", "CREDITED"].includes(getStatutField(f)));
                    const archivee  = factures.find(f => getStatutField(f) === "ARCHIVED");
                    return (
                      <>
                        <div style={{ ...S.floatingCard, top: 10,   left: 10,  color: "#6c63ff", animationDelay: "0s" }}>
                          📄 Facture #{derniere ? (derniere.NumfactAffich || derniere.NumFacture) : "1042"}
                        </div>
                        <div style={{ ...S.floatingCard, top: 80,   right: 10, color: "#43b89c", animationDelay: "1s" }}>
                          ✅ {acceptee ? `Facture #${acceptee.NumfactAffich || acceptee.NumFacture} acceptée` : "Acceptée ANCE"}
                        </div>
                        <div style={{ ...S.floatingCard, bottom: 10, left: 40, color: "#f59e0b", animationDelay: "2s" }}>
                          🗄️ {archivee ? `Facture #${archivee.NumfactAffich || archivee.NumFacture} archivée` : "Archivée"}
                        </div>
                      </>
                    );
                  })()}
                </div>
                                </div>

              {/* ── Bannière alertes — factures nécessitant attention ── */}
              {!factLoading && alertFactures.total > 0 && (
                <div className="alert-banner" style={{
                  background: "linear-gradient(135deg, #fff7ed 0%, #fffbeb 100%)",
                  border: "1.5px solid #fde68a",
                  borderRadius: 14,
                  padding: "16px 22px",
                  display: "flex",
                  alignItems: "center",
                  gap: 16,
                  flexWrap: "wrap",
                }}>
                  <div style={{ fontSize: "1.6rem" }}>⚠️</div>
                  <div style={{ flex: 1, minWidth: 240 }}>
                    <div style={{ fontWeight: 800, fontSize: "0.9rem", color: "#92400e" }}>
                      {alertFactures.total} facture{alertFactures.total > 1 ? "s" : ""} nécessite{alertFactures.total > 1 ? "nt" : ""} votre attention
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#b45309", marginTop: 3 }}>
                      {alertFactures.rejected.length > 0 && (
                        <span>❌ {alertFactures.rejected.length} rejetée{alertFactures.rejected.length > 1 ? "s" : ""}{alertFactures.stale.length > 0 ? " · " : ""}</span>
                      )}
                      {alertFactures.stale.length > 0 && (
                        <span>⏳ {alertFactures.stale.length} en attente depuis plus de 25 jours</span>
                      )}
                    </div>
                  </div>
                  <button
                    style={{ ...S.actionBtn, background: "#f59e0b", color: "#fff", border: "none", padding: "9px 18px", fontWeight: 700 }}
                    onClick={() => setActiveTab("factures")}>
                    Voir les factures →
                  </button>
                </div>
              )}

              {/* ── KPIs rapides ── */}
<div className="stats-grid" style={S.statsGrid}>
  {[
        { icon: "🧾", label: "Mes Factures",   value: factLoading ? <Skeleton width={40} height={28} /> : <AnimCount n={quickStats.total} />,    color: "#6c63ff", gradientBg: "linear-gradient(135deg, #f0efff 0%, #ffffff 100%)", trend: trends.total },
    { icon: "🏛️", label: "Acceptées ANCE", value: factLoading ? <Skeleton width={40} height={28} /> : <AnimCount n={quickStats.accepted} />, color: "#43b89c", gradientBg: "linear-gradient(135deg, #e6f7f3 0%, #ffffff 100%)", trend: trends.accepted },
    { icon: "⏳", label: "En cours",        value: factLoading ? <Skeleton width={40} height={28} /> : <AnimCount n={quickStats.pending} />,  color: "#f59e0b", gradientBg: "linear-gradient(135deg, #fff7e6 0%, #ffffff 100%)", trend: trends.pending },
    { icon: "❌", label: "Rejetées",        value: factLoading ? <Skeleton width={40} height={28} /> : <AnimCount n={quickStats.rejected} />, color: "#e74c3c", gradientBg: "linear-gradient(135deg, #fff0f0 0%, #ffffff 100%)", trend: trends.rejected },
  ].map((s, i) => (
    <div key={i} style={{ ...S.statCard, background: s.gradientBg, borderColor: s.color + "25" }} className="stat-hover" onClick={() => setActiveTab("factures")}>
                    <div style={S.statTop}>
                      <span style={S.statLabel}>{s.label}</span>
                      <div style={{ ...S.statDot, background: s.color }} />
                    </div>
                    <div style={{ ...S.statValue, color: s.color }}>{s.value}</div>
                                        <div style={S.statSub}>
                      {s.icon} Cliquer pour voir
                      {!factLoading && (
                        <span style={{ marginLeft: 8, fontWeight: 700, color: s.trend >= 0 ? "#16a34a" : "#dc2626" }}>
                          {s.trend >= 0 ? "▲" : "▼"} {Math.abs(s.trend)}% ce mois
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* ── Distribution des statuts ── */}
              {!factLoading && factures.length > 0 && (
                <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E8ECEF", padding: "20px 24px" }}>
                  <div style={S.sectionTitle}>📊 Distribution des statuts</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12, marginTop: 16 }}>
                    {[
                      { label: "En cours",  value: quickStats.pending,  color: "#6c63ff", bg: "#f0efff", icon: "⏳", desc: "en traitement" },
                      { label: "Acceptées", value: factures.filter(f => ["ACCEPTED", "ARCHIVED", "CREDITED"].includes(getStatutField(f))).length, color: "#43b89c", bg: "#e6f7f3", icon: "🏛️", desc: "validées officiellement" },
                      { label: "Rejetées",  value: quickStats.rejected, color: "#e74c3c", bg: "#fff0f0", icon: "❌", desc: "à corriger" },
                    ].map((s, i) => {
                      const pct = quickStats.total ? Math.round((s.value / quickStats.total) * 100) : 0;
                      return (
                        <div key={i}
                          style={{ background: "#F8FAFC", borderRadius: 12, padding: "16px 18px", border: "1px solid #E8ECEF", position: "relative", overflow: "hidden", cursor: "pointer", transition: "all 0.2s" }}
                          className="stat-hover"
                          onClick={() => setActiveTab("factures")}>
                          {/* Barre de couleur en bas */}
                          <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: s.color, borderRadius: "0 0 12px 12px" }} />
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                            <span style={{ fontSize: "0.68rem", fontWeight: 700, color: "#94A3B8", textTransform: "uppercase", letterSpacing: "0.06em" }}>{s.label}</span>
                            <div style={{ width: 32, height: 32, borderRadius: 8, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.9rem" }}>{s.icon}</div>
                          </div>
                          <div style={{ fontSize: "2rem", fontWeight: 800, color: s.color, letterSpacing: "-0.04em", lineHeight: 1, marginBottom: 10 }}>{s.value}</div>
                          {/* Barre de progression */}
                          <div style={{ background: "#E8ECEF", borderRadius: 99, height: 5, overflow: "hidden", marginBottom: 6 }}>
                            <div style={{ height: "100%", borderRadius: 99, background: s.color, width: `${Math.max(pct, 0.5)}%`, transition: "width 1.2s ease" }} />
                          </div>
                          <div style={{ fontSize: "0.68rem", color: "#94A3B8", fontWeight: 600 }}>{pct}% · {s.desc}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── Workflow TEIF — 6 étapes illustrées ── */}
              <div style={S.workflowSection}>
                <div style={{ ...S.sectionTitle, background: "transparent", marginBottom: 8 }}>
                  ⚡ Workflow Facture Électronique TEIF
                </div>
                <p style={{ ...S.sectionDesc, background: "transparent", marginBottom: 20 }}>
                  Chaque facture suit ce parcours obligatoire — de votre ERP jusqu'à l'archivage légal ANCE.{" "}
                  {factures.length > 0 && (
                    <span style={{ color: "#6c63ff", fontWeight: 700 }}>
                      {factures.length} facture{factures.length > 1 ? "s" : ""} en cours de traitement.
                    </span>
                  )}
                </p>
                <div style={S.workflowTimeline}>
                  {WORKFLOW_STEPS.map((step, idx) => (
                    <div key={step.id} style={S.workflowStep}>
                      {/* Connecteur entre étapes */}
                      {idx < WORKFLOW_STEPS.length - 1 && (
                        <div style={{ ...S.workflowConnector, background: `linear-gradient(90deg, ${step.color}, ${WORKFLOW_STEPS[idx + 1].color})` }} />
                      )}
                      <div style={{ ...S.workflowCircle, background: step.color, boxShadow: `0 4px 16px ${step.color}40` }}>
                        <span style={{ fontSize: "1.3rem" }}>{step.icon}</span>
                        <span style={S.workflowNum}>{idx + 1}</span>
                      </div>
                      <div style={S.workflowLabel}>{step.label}</div>
                      <div style={S.workflowSublabel}>{step.sublabel}</div>
                      <div style={S.workflowDesc}>{step.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* ── 5 dernières factures ── */}
              {!factLoading && factures.length > 0 && (
                <>
                  <div style={S.sectionTitle}>🕐 Mes dernières factures</div>
                  <div style={S.recentList}>
                    {factures.slice(0, 5).map((f, i) => {
                      const st      = getStatut(f.Statut);
                      const stepIdx = getWorkflowStep(f.Statut);
                      return (
                        <div key={i} style={S.recentRow} className="stat-hover">
                          <div style={{ ...S.recentNum, color: "#6c63ff" }}>#{f.NumfactAffich || f.NumFacture}</div>
                          {/* Mini workflow — barres colorées selon l'avancement */}
                          <div style={S.miniWorkflow}>
                            {WORKFLOW_STEPS.map((step, si) => (
                              <div key={si} style={{ ...S.miniStep, background: si <= stepIdx ? step.color : "#e8e8f0", opacity: si <= stepIdx ? 1 : 0.3 }} title={step.label} />
                            ))}
                          </div>
                          <span style={{ ...S.badge, color: st.color, background: st.bg }}>{st.icon} {st.label}</span>
                          <span style={S.recentDate}>{f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—"}</span>
                          <div style={{ display: "flex", gap: 6 }}>
                            <button style={S.actionBtn} onClick={() => openDetail(f)}>👁️ Détails</button>
                            <button style={{ ...S.actionBtn, ...S.actionBtnTrace }} onClick={() => fetchTrace(f.NumFacture)}>🔍 Trace</button>
                            <button style={{ ...S.actionBtn, ...S.actionBtnPdf }} onClick={() => downloadPDF(f.NumfactAffich || f.NumFacture)} disabled={pdfLoading[f.NumFacture]}>
                              {pdfLoading[f.NumFacture] ? "..." : "📄 PDF"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    {factures.length > 5 && (
                      <div style={S.moreLink} onClick={() => setActiveTab("factures")}>
                        Voir toutes les {factures.length} factures →
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* ── Activité récente — timeline ── */}
              {!factLoading && factures.length > 0 && (
                <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #E8ECEF", padding: "20px 24px" }}>
                  <div style={{ ...S.sectionTitle, marginBottom: 16 }}>🕐 Dernière activité</div>
                  <div style={{ display: "flex", flexDirection: "column" }}>
                    {factures.slice(0, 4).map((f, i) => {
                      const st = getStatut(f.Statut);
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 14, padding: "12px 0", position: "relative" }}>
                          {/* Ligne verticale timeline */}
                          {i < 3 && <div style={{ position: "absolute", left: 15, top: 38, bottom: 0, width: 1.5, background: "#F1F5F9", zIndex: 0 }} />}
                          <div style={{ width: 32, height: 32, borderRadius: "50%", background: st.bg, border: `2px solid ${st.color}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem", flexShrink: 0, zIndex: 1 }}>{st.icon}</div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0F172A" }}>
                              Facture <span style={{ color: "#6c63ff" }}>#{f.NumfactAffich || f.NumFacture}</span>
                              {f.Statut === "ARCHIVED" ? " archivée" : f.Statut === "ACCEPTED" ? " acceptée ANCE" : f.Statut === "C" ? " créée" : " mise à jour"}
                            </div>
                            <div style={{ fontSize: "0.7rem", color: "#94A3B8", marginTop: 3, display: "flex", alignItems: "center", gap: 6 }}>
                              <div style={{ width: 5, height: 5, borderRadius: "50%", background: "#CBD5E1" }} />
                              {f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—"}
                              {f.Statut === "ARCHIVED" && " · Workflow complété 6/6 étapes"}
                              {f.Statut === "C"        && " · En attente de validation"}
                            </div>
                          </div>
                          <span style={{ ...S.badge, color: st.color, background: st.bg, fontSize: "0.62rem", padding: "2px 8px", flexShrink: 0 }}>{st.label}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ══════════════════════════════════════════════════
              TAB : FACTURES — Liste et gestion
              Affiche : barre de recherche, filtres statut,
              filtres avancés, export CSV/Excel, liste factures
          ══════════════════════════════════════════════════ */}
          {activeTab === "factures" && (
            <div style={S.tabContent}>

              {/* ── Barre d'outils : recherche + filtres + export ── */}
              <div className="factures-toolbar" style={S.toolbar}>

                {/* Champ de recherche */}
                <div style={S.searchBox}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink: 0 }}>
                    <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    style={S.searchInput}
                    placeholder="Rechercher par N° facture, client..."
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                  {search && <button style={S.searchClear} onClick={() => setSearch("")}>×</button>}
                </div>

                {/* Filtres rapides par statut */}
                <div style={S.filterGroup}>
                  {[
                    { id: "all",       label: "Toutes"              },
                    { id: "C",         label: "📝 Créées"           },
                    { id: "V",         label: "🔍 Validées ERP"        },
                    { id: "SIGNED",    label: "🔏 Signées"          },
                    { id: "ACCEPTED",  label: "🏛️ Validées"         },
                    { id: "REJECTED",  label: "❌ Rejetées"          },
                  ].map(f => (
                    <button key={f.id}
                      style={{ ...S.filterChip, ...(filterStatut === f.id ? S.filterChipActive : {}) }}
                      onClick={() => setFilterStatut(f.id)}>
                      {f.label}
                    </button>
                  ))}
                </div>

                {/* Bouton filtres avancés */}
                          <div style={S.filterGroup}>
            {[
              { id: "all", label: "💰 Tous règlements" },
              { id: "R", label: "✅ Réglées" },
              { id: "P", label: "🟠 Partielles" },
              { id: "", label: "🔴 Non réglées" },
              { id: "A", label: "↩️ Avoirs" },
            ].map(r => {
                            const nb = r.id === "all" ? facturesAvantReg.length : facturesAvantReg.filter(x => String(x.Reg || "").trim().toUpperCase() === r.id).length;
              return (
                <button key={r.id || "none"} onClick={() => setFilterReg(r.id)} style={filterReg === r.id ? { ...S.filterChip, ...S.filterChipActive } : S.filterChip}>
                  {r.label} ({nb})
                </button>
              );
            })}
          </div>
                <button
                  style={{ ...S.btnSecondary, ...(showAdvFilters || hasAdvancedFilters ? { background: "#f0efff", borderColor: "#6c63ff", color: "#6c63ff" } : {}) }}
                  onClick={() => setShowAdvFilters(!showAdvFilters)}>
                  ⚙️ Filtres {hasAdvancedFilters && <span style={S.filterActiveDot} />}
                </button>

                {/* Menu export CSV / Excel */}
                <div style={{ position: "relative" }} ref={exportMenuRef}>
                  <button
                    style={{ ...S.btnSecondary, background: "#f0fdf4", borderColor: "#bbf7d0", color: "#16a34a" }}
                    onClick={() => setExportMenuOpen(!exportMenuOpen)}>
                    📤 Exporter ▾
                  </button>
                  {exportMenuOpen && (
                    <div style={S.exportMenu}>
                      <button style={S.exportMenuItem} onClick={() => { exportCSV(filteredFactures); setExportMenuOpen(false); showToast("📄 CSV exporté !"); }}>
                        <span style={{ fontSize: "1.1rem" }}>📄</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "0.82rem" }}>Export CSV</div>
                          <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{filteredFactures.length} factures</div>
                        </div>
                      </button>
                      <button style={S.exportMenuItem} onClick={() => { exportExcel(filteredFactures); setExportMenuOpen(false); showToast("📊 Excel exporté !"); }}>
                        <span style={{ fontSize: "1.1rem" }}>📊</span>
                        <div>
                          <div style={{ fontWeight: 700, fontSize: "0.82rem" }}>Export Excel (.xls)</div>
                          <div style={{ fontSize: "0.7rem", color: "#94A3B8" }}>{filteredFactures.length} factures</div>
                        </div>
                      </button>
                    </div>
                  )}
                </div>

                {/* Bouton rafraîchir */}
                <button style={S.btnSecondary} onClick={fetchFactures}>↻</button>
              </div>

              {/* ── Panel filtres avancés ── */}
              {showAdvFilters && (
                <div style={S.advFiltersPanel}>
                  <div style={S.advFiltersTitle}>
                    ⚙️ Filtres avancés
                    {hasAdvancedFilters && (
                      <button style={S.resetFiltersBtn} onClick={resetFilters}>✕ Réinitialiser</button>
                    )}
                  </div>
                  <div style={S.advFiltersGrid}>
                    {[
                    { label: "Client (Nom ou Code)",  icon: "👤", type: "text",   value: filterClient,     set: setFilterClient,     ph: "Ex: Ahmed ou CLI001", list: "clients-datalist" },
                    { label: "Date — De",             icon: "📅", type: "date",   value: filterDateFrom,   set: setFilterDateFrom,   ph: "" },
                    { label: "Date — À",              icon: "📅", type: "date",   value: filterDateTo,     set: setFilterDateTo,     ph: "" },
                    { label: "Montant TTC min (TND)", icon: "💰", type: "number", value: filterMontantMin, set: setFilterMontantMin, ph: "0.000" },
                    { label: "Montant TTC max (TND)", icon: "💰", type: "number", value: filterMontantMax, set: setFilterMontantMax, ph: "999999" },
                  ].map(({ label, icon, type, value, set, ph, list }) => (
                    <div key={label} style={S.advFilterGroup}>
                      <label style={S.advFilterLabel}>{label}</label>
                      <div style={S.advFilterInput}>
                        <span style={{ color: "#94A3B8", fontSize: "0.85rem" }}>{icon}</span>
                        <input type={type} list={list} style={S.advFilterInputField} placeholder={ph} value={value} onChange={e => set(e.target.value)} />
                      </div>
                    </div>
                  ))}

                  {/* Autocomplete — liste des noms clients pour la recherche assistée */}
                  <datalist id="clients-datalist">
                    {clients.map((c, i) => (
                      <option key={i} value={c.Nom || c.NomClient || c.RaisonSociale || ""} />
                    ))}
                  </datalist>
                  </div>
                </div>
              )}

                {/* ── Barre résultats — compteur + total TTC ── */}
              <div style={S.resultsBar}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontSize: "0.78rem", color: "#64748B", fontWeight: 600 }}>
                    <strong style={{ color: "#0F172A" }}>{filteredFactures.length}</strong> facture{filteredFactures.length !== 1 ? "s" : ""}
                    {factures.length !== filteredFactures.length && (
                      <span style={{ color: "#94A3B8" }}> sur {factures.length}</span>
                    )}
                  </span>
                  {filteredFactures.length > 0 && (
                    <span style={{ fontSize: "0.78rem", color: "#64748B" }}>
                      Total HT: <strong style={{ color: "#0891b2" }}>{formatMontant(totalHTFiltre)} {getDevise(filteredFactures[0])}</strong> &nbsp;·&nbsp;
                      Total TTC: <strong style={{ color: "#6c63ff" }}>{formatMontant(totalMontantFiltre)} {getDevise(filteredFactures[0])}</strong>
                      {(() => {
                        const reste = filteredFactures.filter(x => String(x.Reg || "").trim().toUpperCase() === "P").reduce((acc, x) => acc + (Number(x.MontantRestant) || 0), 0);
                        return reste > 0 ? (
                          <> &nbsp;·&nbsp; 💸 Reste (partielles): <strong style={{ color: "#b45309" }}>{formatMontant(reste)} {getDevise(filteredFactures[0])}</strong></>
                        ) : null;
                      })()}
                    </span>
                  )}
                </div>
                {(search || hasAdvancedFilters || filterStatut !== "all") && (
                  <button style={S.resetFiltersBtn} onClick={resetFilters}>✕ Effacer filtres</button>
                )}
              </div>

              {/* ── Liste des factures ── */}
              {factLoading ? (
                <StateBox loading />
              ) : filteredFactures.length === 0 ? (
                <div style={S.emptyState}>
                  <div style={{ fontSize: "2.5rem" }}>🔍</div>
                  <p style={{ color: "#64748B", fontSize: "0.9rem", fontWeight: 600 }}>Aucune facture trouvée</p>
                  <button style={{ ...S.btnSecondary, marginTop: 8 }} onClick={resetFilters}>Réinitialiser les filtres</button>
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {filteredFactures.map((f, i) => {
                    const st      = getStatut(f.Statut);
                    const stepIdx = getWorkflowStep(f.Statut);
                    const ttc     = f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : null;
                    const timbre  = f.MontantTimbre    != null ? Number(f.MontantTimbre)
                                   : f.MontantTotalFett != null ? Number(f.MontantTotalFett) : null;
                    const htBrut  = f.MontantTotalHT != null ? Number(f.MontantTotalHT) : null;
                    const taux    = f.TauxTVA1 != null ? Number(f.TauxTVA1) : null;

                    // HT après remise : si MontantHTRemise n'est pas exposé (cas de la liste),
                    // on tente un calcul via TauxTVA1 (valable si toutes les lignes ont le même
                    // taux). Un contrôle de cohérence — HT après remise ne peut jamais dépasser
                    // le HT brut — permet de détecter les cas à taux multiples et de retomber
                    // alors sur le HT brut (sans remise déduite, mais pas de valeur aberrante).
                    let ht;
                    if (f.MontantHTRemise != null) {
                      ht = Number(f.MontantHTRemise);
                    } else if (taux != null && ttc != null) {
                      const htCalc = Math.round(((ttc - (timbre || 0)) / (1 + taux / 100)) * 1000) / 1000;
                      ht = (htBrut == null || htCalc <= htBrut + 0.01) ? htCalc : htBrut;
                    } else {
                      ht = htBrut;
                    }
                    // TVA fiable = déduite de TTC - HT - Timbre (le champ MontantTVA1 de la liste
                    // ne représente qu'une composante, pas le total).
                    const tva = f.MontantTVA != null ? Number(f.MontantTVA)
                              : (ttc != null && ht != null)
                                ? Math.round((ttc - ht - (timbre || 0)) * 1000) / 1000
                                : f.MontantTVA1 != null ? Number(f.MontantTVA1) : null;
                    const isRejected = getStatutField(f) === "REJECTED";
                    const isArchived = ["ARCHIVED", "ACCEPTED"].includes(getStatutField(f));
                    const vr      = verifyResults[f.NumFacture];

                    return (
                      <div key={i} style={S.factureCard} className="facture-hover">

                        {/* ── En-tête carte facture : numéro + client + statut ── */}
                        <div style={S.factureHeader}>
                          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                            <div style={{ width: 44, height: 44, borderRadius: 12, background: st.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.2rem", flexShrink: 0 }}>
                              {st.icon}
                            </div>
                            <div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <span style={{ fontWeight: 800, color: "#6c63ff", fontSize: "1.05rem" }}>
                                  #{f.NumfactAffich || f.NumFacture}
                                </span>
                                {/* N° comptable si différent du N° affiché */}
                                {f.NumFactureComptable && f.NumFactureComptable !== (f.NumfactAffich || f.NumFacture) && (
                                  <span style={{ fontSize: "0.72rem", color: "#94A3B8", background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 6, padding: "2px 8px" }}>
                                    Compt. {f.NumFactureComptable}
                                  </span>
                                )}
                              </div>
                              <div style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 2 }}>
                              👤 {getNomClient(f.CodeClient)} &nbsp;·&nbsp; 📅 {f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—"}
                            </div>
                            </div>
                          </div>
                            <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
                            <span style={{ ...S.badge, color: st.color, background: st.bg, fontSize: "0.76rem", padding: "5px 12px" }}>
                              {st.icon} {st.label}
                            </span>
                            {(() => {
                              const r = String(f.Reg || "").trim().toUpperCase();
                              const rg = r === "R" ? { icon: "✅", label: "Totalement réglée", color: "#16a34a", bg: "#dcfce7" }
                                       : r === "P" ? { icon: "🟠", label: "Partiellement réglée", color: "#d97706", bg: "#fef3c7" }
                                       : r === "A" ? { icon: "↩️", label: "Avoir", color: "#2563eb", bg: "#dbeafe" }
                                       : { icon: "🔴", label: "Non réglée", color: "#dc2626", bg: "#fee2e2" };
                              return (
                                <span style={{ ...S.badge, color: rg.color, background: rg.bg, fontSize: "0.76rem", padding: "5px 12px" }}>
                                  {rg.icon} {rg.label}
                                </span>
                              );
                            })()}
                                                        {String(f.Reg || "").trim().toUpperCase() === "P" && f.MontantRestant != null && (
                              <span style={{ ...S.badge, color: "#b45309", background: "#fff7ed", border: "1px solid #fde68a", fontSize: "0.76rem", padding: "5px 12px" }}>
                                💸 Reste : {formatMontant(Number(f.MontantRestant))} {getDevise(f)}
                              </span>
                            )}

                          </div>
                        </div>

                        {/* ── Montants : HT + TVA + TTC + étape actuelle ── */}
                        <div className="facture-money-row" style={S.factureMoneyRow}>
                          <div style={S.factureMoneyItem}>
                            <span style={S.factureMoneyLabel}>Montant HT</span>
                            <span style={{ ...S.factureMoneyVal, color: "#0891b2" }}>
                              {ht != null ? formatMontant(ht) + " " + getDevise(f) : "—"}
                            </span>
                          </div>
                                                    <div style={S.factureMoneyDivider} />
                          <div style={S.factureMoneyItem}>
                            <span style={S.factureMoneyLabel}>TVA</span>
                            <span style={{ ...S.factureMoneyVal, color: "#f59e0b" }}>
                              {tva != null ? formatMontant(tva) + " " + getDevise(f) : "—"}
                            </span>
                          </div>
                          <div style={S.factureMoneyDivider} />
                          <div style={S.factureMoneyItem}>
                            <span style={S.factureMoneyLabel}>Timbre</span>
                            <span style={{ ...S.factureMoneyVal, color: "#8b5cf6" }}>
                              {timbre != null ? formatMontant(timbre) + " " + getDevise(f) : "—"}
                            </span>
                          </div>
                          <div style={S.factureMoneyDivider} />
                          <div style={{ ...S.factureMoneyItem, flex: 1.5 }}>
                            <span style={S.factureMoneyLabel}>Montant TTC</span>
                            <span style={{ ...S.factureMoneyVal, color: "#6c63ff", fontSize: "1.1rem", fontWeight: 900 }}>
                              {ttc != null ? formatMontant(ttc) + " " + getDevise(f) : "—"}
                            </span>
                          </div>
                          {/* Badge étape actuelle du workflow */}
                          <div style={{ display: "flex", alignItems: "center", gap: 8, background: "#F8FAFC", borderRadius: 10, padding: "10px 16px", flexShrink: 0 }}>
                            <span style={{ fontSize: "1.1rem" }}>{WORKFLOW_STEPS[stepIdx]?.icon}</span>
                            <div>
                              <div style={{ fontSize: "0.65rem", color: "#94A3B8", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Étape actuelle</div>
                              <div style={{ fontSize: "0.8rem", fontWeight: 700, color: "#0F172A" }}>{WORKFLOW_STEPS[stepIdx]?.label}</div>
                            </div>
                            <div style={{ width: 28, height: 28, borderRadius: "50%", background: WORKFLOW_STEPS[stepIdx]?.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.7rem", fontWeight: 800, color: "#fff" }}>
                              {stepIdx + 1}/{WORKFLOW_STEPS.length}
                            </div>
                          </div>
                        </div>

                        {/* ── Parcours TEIF — barre de progression 6 étapes ── */}
                        <div style={S.workflowBar}>
                          <div style={S.workflowBarLabel}>Parcours TEIF</div>
                          <div style={S.workflowBarSteps}>
                            {WORKFLOW_STEPS.map((step, si) => {
                              const done    = si <= stepIdx;
                              const current = si === stepIdx;
                              return (
                                <div key={si} style={S.workflowBarItem} title={step.label}>
                                  {si > 0 && (
                                    <div style={{ ...S.workflowBarLine, background: si <= stepIdx ? step.color : "#e8e8f0" }} />
                                  )}
                                  <div style={{ ...S.workflowBarDot, background: done ? step.color : "#e8e8f0", boxShadow: current ? `0 0 0 3px ${step.color}40` : "none", transform: current ? "scale(1.2)" : "scale(1)" }}>
                                    <span style={{ fontSize: current ? "0.7rem" : "0.6rem" }}>{step.icon}</span>
                                  </div>
                                  <div style={{ ...S.workflowBarDotLabel, color: done ? step.color : "#94A3B8", fontWeight: current ? 700 : 400 }}>
                                    {step.label}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* ── Résultat vérification intégrité SHA-256 ── */}
                        {vr && !vr.loading && (
                          <div style={{
                            background:   vr.error ? "#fff0f0" : vr.valid ? "#f0fdf4" : "#fffbeb",
                            border:       `1px solid ${vr.error ? "#fecaca" : vr.valid ? "#bbf7d0" : "#fde68a"}`,
                            borderRadius: 8, padding: "10px 14px",
                            display: "flex", alignItems: "flex-start", gap: 10
                          }}>
                            <span style={{ fontSize: "1rem" }}>{vr.error ? "❌" : vr.valid ? "✅" : "⚠️"}</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: "0.78rem", fontWeight: 700, color: vr.error ? "#dc2626" : vr.valid ? "#16a34a" : "#d97706" }}>
                                {vr.error ? `Erreur: ${vr.error}` : vr.message}
                              </div>
                              {vr.hash && vr.hash !== "—" && (
                                <div style={{ fontFamily: "monospace", fontSize: "0.65rem", color: "#64748B", marginTop: 4, wordBreak: "break-all" }}>
                                  SHA256: {vr.hash}
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* ── Référence TTN — visible si facture archivée/acceptée ──
                            La référence TTN est le numéro unique retourné par Tunisie Trade Net
                            après validation de la facture électronique
                        ── */}
                        {isArchived && localStorage.getItem(`facture_refttn_${f.NumFacture}`) && (
                          <div style={{ background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "10px 14px", display: "flex", alignItems: "center", gap: 10 }}>
                            <span style={{ fontSize: "1rem" }}>🏛️</span>
                            <div>
                              <div style={{ fontSize: "0.65rem", color: "#94A3B8", fontWeight: 700, textTransform: "uppercase" }}>
                                Référence TTN
                              </div>
                              <div style={{ fontFamily: "monospace", fontSize: "0.82rem", fontWeight: 700, color: "#16a34a", wordBreak: "break-all" }}>
                                {localStorage.getItem(`facture_refttn_${f.NumFacture}`)}
                              </div>
                            </div>
                          </div>
                        )}
                        {/* ── Boutons actions ── */}
                        <div style={S.factureActions}>
                          {/* Actions de base */}
                          <button style={S.actionBtn} onClick={() => openDetail(f)}>👁️ Détails</button>
                          <button style={{ ...S.actionBtn, ...S.actionBtnTrace }} onClick={() => fetchTrace(f.NumFacture)}>🔍 Traçabilité</button>
                          {getCookie("client_type") === "standalone" && getStatutField(f) === "BROUILLON" && (
                            <>
                              <button
                                style={{ ...S.actionBtn, background:"#f0fdf4", color:"#16a34a", border:"1px solid #bbf7d0", fontWeight:700 }}
                                onClick={() => finaliserBrouillon(f)}>
                                ✅ Finaliser
                              </button>
                              <button
                                style={{ ...S.actionBtn, background:"#fffbeb", color:"#d97706", border:"1px solid #fde68a" }}
                                onClick={() => navigate("/facture/create", { state: { editFacture: f } })}>
                                ✏️ Modifier
                              </button>
                              <button
                                style={{ ...S.actionBtn, background:"#fff0f0", color:"#dc2626", border:"1px solid #fecaca" }}
                                onClick={() => handleDeleteFacture(f)}>
                                🗑️ Supprimer
                              </button>
                            </>
                          )}
                          {/* ── NOUVEAU: Retry — visible seulement si facture REJECTED ── */}
                          {isRejected && (
                            <button
                              style={{ ...S.actionBtn, background:"#fff7ed", color:"#ea580c", border:"1px solid #fed7aa", fontWeight:700 }}
                              onClick={() => handleRetry(f.NumFacture)}
                              disabled={retryLoading[f.NumFacture]}
                              title="Resoumettre automatiquement à l'ANCE">
                              {retryLoading[f.NumFacture] ? "⏳ Retry..." : "🔄 Retry ANCE"}
                            </button>
                          )}

                          {/* Workflow actions */}
                            {(!getStatutField(f) || getStatutField(f) === "C") && (
                              <button
                                style={{ ...S.actionBtn, background:"#f0f9ff", color:"#0891b2", border:"1px solid #bae6fd" }}
                                onClick={async () => {
                                  setTeifLoading(true);
                                  const checks = await validateTeifFormat(f);
                                  setTeifModal({ facture: f, checks });
                                  setTeifLoading(false);
                                }}>
                                🔍 Tester Format TEIF
                              </button>
                            )}
                          {getStatutField(f) === "V" && (
                            <button style={{ ...S.actionBtn, background:"#f5f3ff", color:"#7c3aed", border:"1px solid #ddd6fe" }}
                              onClick={() => setPinModal({ numFacture: f.NumFacture, numAffich: f.NumfactAffich, facture: f })}>🔏 Signer</button>
                          )}
                          {getStatutField(f) === "SIGNED" && (
                            <button style={{ ...S.actionBtn, background:"#fffbeb", color:"#d97706", border:"1px solid #fde68a" }}
                              onClick={() => handleAction(f.NumFacture, "submit-dss", "Soumettre")}>🏛️ Soumettre ANCE</button>
                          )}
                          {getStatutField(f) === "SUBMITTED" && (
                            <button style={{ ...S.actionBtn, background:"#f0fdf4", color:"#16a34a", border:"1px solid #bbf7d0" }}
                              onClick={() => handleAction(f.NumFacture, "submit-ttn", "Soumettre TTN")}>
                              📡 Soumettre TTN
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* ══════════════ TAB: TRAÇABILITÉ ══════════════ */}
          {activeTab === "trace" && (
            <div style={S.tabContent}>
              <div style={S.sectionTitle}>🔍 Traçabilité par facture</div>

              {!selectedFact && (
                <div style={S.searchBox}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink:0 }}>
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input style={S.searchInput} placeholder="Rechercher par N° facture..."
                    value={traceSearch} onChange={e => setTraceSearch(e.target.value)} />
                  {traceSearch && <button style={S.searchClear} onClick={() => setTraceSearch("")}>×</button>}
                </div>
              )}

              {!selectedFact && (
                <div style={S.traceSelectList}>
                  {factLoading ? <StateBox loading /> : factures.length === 0 ? (
                    <div style={S.emptyState}><p style={{ color:"#94A3B8" }}>Aucune facture disponible</p></div>
                  ) : factures.filter(f => !traceSearch || String(f.NumfactAffich || f.NumFacture).includes(traceSearch) || String(f.NumFacture).includes(traceSearch)).map((f, i) => {
                    const st = getStatut(f.Statut);
                    return (
                      <div key={i} style={S.traceSelectRow} className="stat-hover" onClick={() => fetchTrace(f.NumFacture)}>
                        <span style={{ fontWeight:700, color:"#6c63ff", minWidth:100 }}>#{f.NumfactAffich || f.NumFacture}</span>
                        <span style={{ color:"#64748B", fontSize:"0.82rem", flex:1 }}>{getNomClient(f.CodeClient)}</span>
                        <span style={{ ...S.badge, color:st.color, background:st.bg }}>{st.icon} {st.label}</span>
                        <span style={{ color:"#94A3B8", fontSize:"0.75rem", minWidth:80 }}>{f.DateFacture ? new Date(f.DateFacture).toLocaleDateString("fr-FR") : "—"}</span>
                        <span style={{ color:"#6c63ff", fontWeight:600, fontSize:"0.8rem" }}>Voir →</span>
                      </div>
                    );
                  })}
                </div>
              )}

              {selectedFact && (
                <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                    <button style={S.btnSecondary} onClick={() => { setSelectedFact(null); setTraceData(null); setVerifyResults(prev => ({ ...prev, [selectedFact]: undefined })); }}>← Retour</button>
                    <span style={{ fontWeight:700, color:"#6c63ff", fontSize:"1rem" }}>
                      Facture #{factures.find(f => f.NumFacture === selectedFact)?.NumfactAffich || selectedFact}
                    </span>

                    {/* ── NOUVEAU: Boutons XML + SHA256 dans la vue traçabilité ── */}
                    <div style={{ display:"flex", gap:8, marginLeft:"auto" }}>
                      <button
                        style={{ ...S.actionBtn, background:"#f0f9ff", color:"#0891b2", border:"1px solid #bae6fd", padding:"7px 14px" }}
                        onClick={() => downloadXML(selectedFact)}
                        disabled={xmlLoading[selectedFact]}
                        title="Télécharger XML TEIF archivé">
                        {xmlLoading[selectedFact] ? "⏳" : "⬇️ XML archivé"}
                      </button>
                      <button
                        style={{ ...S.actionBtn, background:"#faf5ff", color:"#7c3aed", border:"1px solid #c4b5fd", padding:"7px 14px" }}
                        onClick={() => verifyIntegrity(selectedFact)}
                        disabled={verifyResults[selectedFact]?.loading}
                        title="Vérifier intégrité SHA256">
                        {verifyResults[selectedFact]?.loading ? "⏳ Vérif..." :
                         verifyResults[selectedFact]?.valid   ? "✅ SHA256 OK" : "🔐 Vérifier SHA256"}
                      </button>
                    </div>
                  </div>

                  {/* Résultat SHA256 dans traçabilité */}
                  {verifyResults[selectedFact] && !verifyResults[selectedFact].loading && (
                    <div style={{
                      background: verifyResults[selectedFact].error ? "#fff0f0" : verifyResults[selectedFact].valid ? "#f0fdf4" : "#fffbeb",
                      border: `1.5px solid ${verifyResults[selectedFact].error ? "#fecaca" : verifyResults[selectedFact].valid ? "#bbf7d0" : "#fde68a"}`,
                      borderRadius: 10, padding: "14px 18px", display:"flex", alignItems:"flex-start", gap:12
                    }}>
                      <span style={{ fontSize:"1.3rem" }}>
                        {verifyResults[selectedFact].error ? "❌" : verifyResults[selectedFact].valid ? "✅" : "⚠️"}
                      </span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontWeight:700, fontSize:"0.88rem", color: verifyResults[selectedFact].error ? "#dc2626" : verifyResults[selectedFact].valid ? "#16a34a" : "#d97706" }}>
                          {verifyResults[selectedFact].error
                            ? `Erreur vérification: ${verifyResults[selectedFact].error}`
                            : verifyResults[selectedFact].message}
                        </div>
                        {verifyResults[selectedFact].hash && verifyResults[selectedFact].hash !== "—" && (
                          <div style={{ fontFamily:"monospace", fontSize:"0.72rem", color:"#64748B", marginTop:6, wordBreak:"break-all", background:"#F8FAFC", padding:"6px 10px", borderRadius:6 }}>
                            SHA256: {verifyResults[selectedFact].hash}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {traceLoading ? <StateBox loading /> : traceData?.error ? (
                    <div style={S.emptyState}><p style={{ color:"#dc2626" }}>❌ {traceData.error}</p></div>
                  ) : traceData ? (
                    <>
                      {traceData.status && (() => {
                        const s = traceData.status;
                        const st = getStatut(s.current_status);
                        const stepIdx = getWorkflowStep(s.current_status);
                        return (
                          <div style={{ background:"#fff", borderRadius:16, border:"1.5px solid #e8e8f0", padding:24, display:"flex", flexDirection:"column", gap:20 }}>
                            <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                              <div style={{ display:"flex", alignItems:"center", gap:14 }}>
                                <div style={{ width:52, height:52, borderRadius:13, background:st.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.5rem", flexShrink:0 }}>{st.icon}</div>
                                <div>
                                  <div style={{ fontWeight:800, fontSize:"1.1rem", color:"#0F172A" }}>
                                    {factures.find(f => f.NumFacture === selectedFact)?.NomSociete || 
                                    factures.find(f => f.NumFacture === selectedFact)?.NumfactAffich || 
                                    `Facture #${selectedFact}`}
                                  </div>
                                  <div style={{ fontSize:"0.78rem", color:"#94A3B8", marginTop:3 }}>Étape {stepIdx+1} / {WORKFLOW_STEPS.length} — {WORKFLOW_STEPS[stepIdx]?.label}</div>
                                </div>
                              </div>
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <span style={{ background: s.environment==="simulation" ? "#f0f9ff" : "#f0fdf4", color: s.environment==="simulation" ? "#0891b2" : "#16a34a", border:`1px solid ${s.environment==="simulation" ? "#bae6fd" : "#bbf7d0"}`, padding:"4px 12px", borderRadius:20, fontSize:"0.72rem", fontWeight:700 }}>
                                  {s.environment === "simulation" ? "🧪 Simulation" : "🏭 Production"}
                                </span>
                                {/* ── NOUVEAU: Retry dans traçabilité si statut REJECTED ── */}
                                {(s.current_status === "REJECTED") && (
                                  <button
                                    style={{ ...S.actionBtn, background:"#fff7ed", color:"#ea580c", border:"1px solid #fed7aa", padding:"6px 14px", fontWeight:700 }}
                                    onClick={() => handleRetry(selectedFact)}
                                    disabled={retryLoading[selectedFact]}>
                                    {retryLoading[selectedFact] ? "⏳ Retry..." : "🔄 Retry ANCE"}
                                  </button>
                                )}
                              </div>
                            </div>

                            <div style={{ background:"#F8FAFC", borderRadius:10, padding:16 }}>
                              <div style={{ fontSize:"0.65rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12 }}>Parcours TEIF</div>
                              <div style={S.workflowBarSteps}>
                                {WORKFLOW_STEPS.map((step, si) => {
                                  const done = si<=stepIdx, current = si===stepIdx;
                                  return (
                                    <div key={si} style={S.workflowBarItem}>
                                      {si > 0 && <div style={{ ...S.workflowBarLine, background: si<=stepIdx ? step.color : "#e8e8f0" }} />}
                                      <div style={{ ...S.workflowBarDot, background: done ? step.color : "#e8e8f0", boxShadow: current ? `0 0 0 4px ${step.color}30` : "none", width: current ? 36 : 28, height: current ? 36 : 28 }}>
                                        <span style={{ fontSize: current ? "0.85rem" : "0.7rem" }}>{step.icon}</span>
                                      </div>
                                      <div style={{ ...S.workflowBarDotLabel, color: done ? step.color : "#94A3B8", fontWeight: current ? 800 : 400 }}>{step.label}</div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:10 }}>
                              {traceData.status.uuid_dss && (
                                <div style={{ background:"#F8FAFC", borderRadius:10, padding:"12px 14px", gridColumn:"1/-1" }}>
                                  <div style={{ fontSize:"0.65rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:4 }}>UUID DSS</div>
                                  <div style={{ fontFamily:"monospace", fontSize:"0.82rem", fontWeight:700, color:"#6c63ff", wordBreak:"break-all" }}>{traceData.status.uuid_dss}</div>
                                </div>
                              )}
                              {[["📅 Créée le", traceData.status.created_at], ["🔄 Mis à jour le", traceData.status.updated_at]].map(([label, val]) => val && (
                                <div key={label} style={{ background:"#F8FAFC", borderRadius:10, padding:"12px 14px" }}>
                                  <div style={{ fontSize:"0.65rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:4 }}>{label}</div>
                                  <div style={{ fontSize:"0.84rem", fontWeight:700, color:"#0F172A" }}>{new Date(val).toLocaleString("fr-FR")}</div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      <div style={S.sectionTitle}>📋 Historique des événements</div>
                      {!traceData.history || traceData.history.length === 0 ? (
                        <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECEF", padding:28, textAlign:"center" }}>
                          <div style={{ fontSize:"1.5rem", marginBottom:8 }}>📭</div>
                          <p style={{ color:"#94A3B8", fontSize:"0.84rem" }}>Aucun historique disponible</p>
                        </div>
                      ) : (
                        <div style={S.historyList}>
                          {traceData.history.map((h, i) => {
                            const hst = getStatut(h.new_status || h.statut || h.status);
                            return (
                              <div key={i} style={S.historyRow}>
                                <div style={{ ...S.historyDot, background:hst.color }} />
                                <div style={{ flex:1 }}>
                                  <div style={{ fontWeight:700, fontSize:"0.88rem", color:"#0F172A" }}>
                                    {hst.icon} {h.status_label || hst.label || h.new_status || "Événement"}
                                  </div>
                                  {h.old_status && (
                                    <div style={{ fontSize:"0.76rem", color:"#64748B", marginTop:3 }}>
                                      {h.old_status} → {h.new_status}
                                    </div>
                                  )}
                                </div>
                                <div style={{ fontSize:"0.73rem", color:"#94A3B8", whiteSpace:"nowrap" }}>
                                  {h.changed_at || h.date || h.timestamp || "—"}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}

                      <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                        <button style={{ ...S.actionBtn, ...S.actionBtnPdf, padding:"10px 20px", fontSize:"0.85rem" }}
                          onClick={() => downloadPDF(selectedFact)} disabled={pdfLoading[selectedFact]}>
                          {pdfLoading[selectedFact] ? "⏳ Téléchargement..." : "📄 Télécharger PDF Fiscal"}
                        </button>
                       <button style={{ ...S.actionBtn, background:"#f0f9ff", color:"#0891b2", border:"1px solid #bae6fd", padding:"10px 20px", fontSize:"0.85rem" }}
                    onClick={() => downloadXMLRaw(selectedFact)} disabled={xmlLoading[`raw_${selectedFact}`]}>
                    {xmlLoading[`raw_${selectedFact}`] ? "⏳ XML..." : "📄 XML TEIF (brut)"}
                  </button>
                  <button style={{ ...S.actionBtn, background:"#f5f3ff", color:"#7c3aed", border:"1px solid #ddd6fe", padding:"10px 20px", fontSize:"0.85rem" }}
                    onClick={() => downloadXML(selectedFact)} disabled={xmlLoading[selectedFact]}>
                    {xmlLoading[selectedFact] ? "⏳ XML..." : "🔏 XML Signé (archivé)"}
                  </button>
                                        </div>
                                      </>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            )}

                          {/* ══════════════ TAB: DASHBOARD ══════════════ */}
                  {activeTab === "dashboard" && (
                    <div style={S.tabContent}>
                      <div style={S.sectionTitle}>📡 Dashboard KPIs</div>
                      
                      {/* Stats rapides */}
                      <div style={S.statsGrid}>
                        {[
                        { label:"Mes Factures",   value: quickStats.total,    color:"#6c63ff", sub:"total factures" },
                        { label:"Acceptées ANCE", value: quickStats.accepted, color:"#43b89c", sub:"validées officiellement" },
                        { label:"En cours",       value: quickStats.pending,  color:"#f59e0b", sub:"en traitement" },
                        { label:"Rejetées",       value: quickStats.rejected, color:"#e74c3c", sub:"à corriger" },
                      ].map((s, i) => (
                        <div key={i} style={S.statCard} className="stat-hover"
                          onClick={() => setActiveTab("factures")}>
                          <div style={S.statTop}>
                            <span style={S.statLabel}>{s.label}</span>
                            <div style={{ ...S.statDot, background:s.color }} />
                          </div>
                          <div style={{ ...S.statValue, color:s.color }}><AnimCount n={s.value} /></div>
                          <div style={S.statSub}>{s.sub}</div>
                        </div>
                      ))}
                      </div>

                                            {/* ── CA mensuel (année sélectionnée) ── */}
                      {!factLoading && factures.length > 0 && (() => {
                        const MONTHS = ["Jan","Fév","Mar","Avr","Mai","Jun","Jul","Aoû","Sep","Oct","Nov","Déc"];
                        const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0));
                        const monthlyTotals = MONTHS.map((_, i) =>
                          factures
                            .filter(f => {
                              if (!f.DateFacture) return false;
                              const d = new Date(f.DateFacture);
                              return d.getFullYear() === userExercice && d.getMonth() === i;
                            })
                            .reduce((acc, f) => acc + (Number(f.MontantTotalTTC) || 0), 0)
                        );
                        const maxVal = Math.max(...monthlyTotals, 1);
                        const devise = getDevise(factures[0]);

                        return (
                          <div style={{ background:"#fff", borderRadius:14, border:"1px solid #E8ECEF", padding:20 }}>
                            <p style={{ margin:"0 0 16px", fontWeight:700, color:"#334155", fontSize:"0.9rem" }}>
                              CA mensuel — {userExercice}
                            </p>
                            <div style={{ display:"flex", alignItems:"flex-end", gap:8, height:200 }}>
                              {monthlyTotals.map((val, i) => (
                                <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                                  <div
                                    title={fmt(val) + " " + devise}
                                    style={{
                                      width:"100%",
                                      height: Math.max((val / maxVal) * 160, val > 0 ? 4 : 0) + "px",
                                      background: val > 0 ? "linear-gradient(180deg, #8b7ff5, #6c63ff)" : "#eee",
                                      borderRadius:"6px 6px 0 0",
                                      boxShadow: val > 0 ? "0 4px 12px rgba(108,99,255,0.35)" : "none",
                                      transition:"height 0.3s, transform 0.2s",
                                    }}
                                  />
                                  <span style={{ fontSize:11, color:"#94A3B8" }}>{MONTHS[i]}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* ── Répartition par client (top 10) ── */}
                      {!factLoading && factures.length > 0 && (() => {
                        const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0));
                        const factsForYear = factures;
                        const clientTotals = {};
                        factsForYear.forEach(f => {                          const nom = getNomClient(f.CodeClient);
                          clientTotals[nom] = (clientTotals[nom] || 0) + (Number(f.MontantTotalTTC) || 0);
                        });
                        const clientRows = Object.entries(clientTotals)
                          .map(([client, total]) => ({ client, total }))
                          .sort((a, b) => b.total - a.total)
                          .slice(0, 10);
                        const clientTotalSum = Object.values(clientTotals).reduce((a, v) => a + v, 0) || 1;
                        const devise = getDevise(factsForYear[0]);

                        return (
                          <div style={{ background:"#fff", borderRadius:14, border:"1px solid #E8ECEF", padding:20 }}>
                            <p style={{ margin:"0 0 16px", fontWeight:700, color:"#334155", fontSize:"0.9rem" }}>
                              Répartition par client (top 10) — {userExercice}
                            </p>
                            {clientRows.length === 0 ? (
                              <p style={{ color:"#94A3B8", fontSize:13 }}>Aucune donnée disponible.</p>
                            ) : (
                              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                                {clientRows.map((r, i) => {
  const pct = Math.round((r.total / clientTotalSum) * 100);
  return (
    <div key={i}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", fontSize:13, marginBottom:4 }}>
        <div style={{ display:"flex", alignItems:"center", gap:8 }}>
          <div style={{
            width:24, height:24, borderRadius:"50%",
            background:"linear-gradient(135deg,#8b7ff5,#6c63ff)",
            display:"flex", alignItems:"center", justifyContent:"center",
            color:"#fff", fontSize:11, fontWeight:800, flexShrink:0
          }}>
            {r.client.charAt(0).toUpperCase()}
          </div>
          <span>{r.client}</span>
        </div>
        <span style={{ color:"#94A3B8" }}>{fmt(r.total)} {devise} · {pct}%</span>
      </div>
      <div style={{ height:8, background:"#F1F5F9", borderRadius:4, overflow:"hidden", marginLeft:32 }}>
        <div style={{ height:"100%", width:pct+"%", background:"#6c63ff", borderRadius:4 }} />
      </div>
    </div>
  );
})}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* ── Évolution du CA par année ── */}
                      {allYearsLoading ? (
                        <div style={{ background:"#fff", borderRadius:14, border:"1px solid #E8ECEF", padding:20, textAlign:"center", color:"#94A3B8", fontSize:13 }}>
                          Chargement de l'évolution annuelle...
                        </div>
                      ) : allYearsFactures.length > 0 && (() => {
                        const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0));
                        const years = [2022, 2023, 2024, 2025, 2026];
                        const yearlyTotals = years.map(y =>
                          allYearsFactures
                            .filter(f => f.DateFacture && new Date(f.DateFacture).getFullYear() === y)
                            .reduce((acc, f) => acc + (Number(f.MontantTotalTTC) || 0), 0)
                        );
                        const maxVal = Math.max(...yearlyTotals, 1);
                        const devise = getDevise(allYearsFactures[0]);

                        return (
                          <div style={{ background:"#fff", borderRadius:14, border:"1px solid #E8ECEF", padding:20 }}>
                            <p style={{ margin:"0 0 16px", fontWeight:700, color:"#334155", fontSize:"0.9rem" }}>
                              Évolution du CA par année
                            </p>
                            <div style={{ display:"flex", alignItems:"flex-end", gap:16, height:200 }}>
                              {yearlyTotals.map((val, i) => (
                                <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6 }}>
                                  <span style={{ fontSize:11, color:"#64748B", fontWeight:700 }}>
                                    {val > 0 ? fmt(val) : ""}
                                  </span>
                                  <div
                                  title={fmt(val) + " " + devise}
                                  style={{
                                    width:"100%",
                                    height: Math.max((val / maxVal) * 140, val > 0 ? 4 : 0) + "px",
                                    background: val > 0 ? "linear-gradient(180deg, #5cd6b0, #43b89c)" : "#eee",
                                    borderRadius:"6px 6px 0 0",
                                    boxShadow: val > 0 ? "0 4px 12px rgba(67,184,156,0.35)" : "none",
                                    transition:"height 0.3s, transform 0.2s",
                                  }}
                                />
                                  <span style={{ fontSize:12, color:"#94A3B8", fontWeight:700 }}>{years[i]}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}

                      {/* Dashboard Middleware TEIF */}
                      <div style={{ background:"#fff", borderRadius:14, border:"1px solid #E8ECEF", overflow:"hidden" }}>
                        <div style={{ padding:"12px 20px", borderBottom:"1px solid #F1F5F9", background:"#F8FAFC", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                          <span style={{ fontSize:"0.78rem", fontWeight:700, color:"#334155" }}>
                            📊 Dashboard Fiscal TEIF — Middleware
                          </span>
                          <a 
                            href={`http://das-cloud.com:8443/dashboard?societe_id=${getCookie("societe_id")}&token=${getCookie("token")}`}
                            target="_blank"
                            rel="noreferrer"
                            style={{ fontSize:"0.72rem", color:"#6c63ff", fontWeight:600, textDecoration:"none" }}>
                            🔗 Ouvrir en plein écran
                          </a>
                        </div>
                        <iframe
                          src={`http://das-cloud.com:8443/dashboard?societe_id=${getCookie("societe_id")}&token=${getCookie("token")}`}
                          style={{ width:"100%", height:"900px", border:"none", display:"block" }}
                          title="Dashboard Fiscal TEIF"
                        />
                      </div>
                    </div>
                  )}
          {/* ══════════════ TAB: AVOIRS ══════════════ */}
          {activeTab === "avoirs" && (() => {
            const filteredAvoirs = avoirs.filter(a => {
              const q = avoirSearch.toLowerCase();
              const matchQ = !q ||
                String(a.avoir_number || "").toLowerCase().includes(q) ||
                String(a.original_invoice_number || "").toLowerCase().includes(q) ||
                String(a.reason || "").toLowerCase().includes(q);
              const matchStatut = avoirFilterStatut === "all" || (a.status || "DRAFT") === avoirFilterStatut;
              return matchQ && matchStatut;
            });
            const totalAmount = filteredAvoirs.reduce((acc, a) => acc + (a.amount != null ? Number(a.amount) : 0), 0);

            return (
            <div style={S.tabContent}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                <div style={S.sectionTitle}>↩️ Avoirs & Notes de crédit</div>
                <button style={{ ...S.heroCta, padding:"8px 16px", fontSize:"0.8rem" }}
                  onClick={() => setAvoirModal({ mode:"create" })}>
                  + Créer un avoir
                </button>
              </div>

              {/* ── KPIs rapides ── */}
              {!avoirsLoading && avoirs.length > 0 && (
                <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(160px,1fr))", gap:12 }}>
                  {[
                    { label:"Total avoirs", value: avoirs.length, color:"#e11d48", icon:"↩️" },
                    { label:"Brouillons",   value: avoirs.filter(a => (a.status||"DRAFT")==="DRAFT").length, color:"#64748b", icon:"📝" },
                    { label:"Soumis ANCE",  value: avoirs.filter(a => a.status==="SUBMITTED" || a.status==="ACCEPTED").length, color:"#0891b2", icon:"📤" },
                    { label:"Archivés",     value: avoirs.filter(a => a.status==="ARCHIVED").length, color:"#16a34a", icon:"🗄️" },
                  ].map((s,i) => (
                    <div key={i} style={{ background:"#fff", borderRadius:12, padding:"14px 16px", border:"1px solid #E8ECEF" }}>
                      <div style={{ fontSize:"0.68rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase" }}>{s.icon} {s.label}</div>
                      <div style={{ fontSize:"1.5rem", fontWeight:800, color:s.color, marginTop:4 }}>{s.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* ── Barre recherche + filtres ── */}
              {!avoirsLoading && avoirs.length > 0 && (
                <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center" }}>
                  <div style={S.searchBox}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink:0 }}>
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input style={S.searchInput} placeholder="Rechercher par N° avoir, facture, motif..."
                      value={avoirSearch} onChange={e => setAvoirSearch(e.target.value)} />
                    {avoirSearch && <button style={S.searchClear} onClick={() => setAvoirSearch("")}>×</button>}
                  </div>
                  <div style={S.filterGroup}>
                    {[
                      { id:"all",       label:"Toutes" },
                      { id:"DRAFT",     label:"📝 Brouillons" },
                      { id:"SUBMITTED", label:"📤 Soumis" },
                      { id:"ACCEPTED",  label:"🏛️ Acceptés" },
                      { id:"ARCHIVED",  label:"🗄️ Archivés" },
                    ].map(f => (
                      <button key={f.id}
                        style={{ ...S.filterChip, ...(avoirFilterStatut === f.id ? S.filterChipActive : {}) }}
                        onClick={() => setAvoirFilterStatut(f.id)}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                  <span style={{ fontSize:"0.78rem", color:"#64748B", fontWeight:600, marginLeft:"auto" }}>
                    <strong style={{ color:"#0F172A" }}>{filteredAvoirs.length}</strong> avoir{filteredAvoirs.length!==1?"s":""}
                    {totalAmount > 0 && <> · Total: <strong style={{ color:"#e11d48" }}>-{formatMontant(totalAmount)} TND</strong></>}
                  </span>
                </div>
              )}

              {avoirsLoading ? <StateBox loading /> : avoirs.length === 0 ? (
                <div style={S.emptyState}>
                  <div style={{ fontSize:"2rem" }}>↩️</div>
                  <p style={{ color:"#64748B", fontWeight:600 }}>Aucun avoir disponible</p>
                  <p style={{ color:"#94A3B8", fontSize:"0.8rem" }}>Les avoirs créés apparaîtront ici</p>
                </div>
              ) : filteredAvoirs.length === 0 ? (
                <div style={S.emptyState}>
                  <div style={{ fontSize:"2rem" }}>🔍</div>
                  <p style={{ color:"#64748B", fontWeight:600 }}>Aucun avoir trouvé</p>
                  <button style={{ ...S.btnSecondary, marginTop:8 }} onClick={() => { setAvoirSearch(""); setAvoirFilterStatut("all"); }}>Réinitialiser</button>
                </div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                  {filteredAvoirs.map((a, i) => {
                    const st = getAvoirStatut(a.status);
                    return (
                    <div key={i} style={{ ...S.factureCard, padding:"16px 20px" }} className="facture-hover">
                      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:10 }}>
                        <div style={{ display:"flex", alignItems:"center", gap:12 }}>
                          <div style={{ width:40, height:40, borderRadius:10, background:st.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.2rem", flexShrink:0 }}>{st.icon}</div>
                          <div>
                            <div style={{ fontWeight:800, color:"#e11d48", fontSize:"0.95rem" }}>{a.avoir_number}</div>
                            <div style={{ fontSize:"0.72rem", color:"#94A3B8" }}>
                              Facture #{a.original_invoice_number} · {a.reason || "—"}
                              {a.created_at && <> · {new Date(a.created_at).toLocaleDateString("fr-FR")}</>}
                            </div>
                          </div>
                        </div>
                        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                          <span style={{ fontWeight:800, color:"#e11d48", fontSize:"1.05rem" }}>
                            {a.amount != null ? `-${formatMontant(a.amount)} TND` : "—"}
                          </span>
                          <span style={{ ...S.badge, color: st.color, background: st.bg }}>
                            {st.icon} {st.label}
                          </span>
                        </div>
                      </div>
                      <div style={{ display:"flex", gap:8, marginTop:10 }}>
                        {(a.status || "DRAFT") !== "SUBMITTED" && a.status !== "ARCHIVED" && a.status !== "ACCEPTED" && (
                          <button style={{ ...S.actionBtn, background:"#16a34a", color:"#fff", border:"none", fontWeight:700 }}
                            onClick={async () => {
                              const societeId = getCookie("societe_id");
                              const res = await fetch(`${MIDDLEWARE_URL}/avoirs/${a.avoir_number}/submit?societe_id=${societeId}`, { method:"POST", headers:authHeaders() });
                              const data = await res.json();
                              if (res.ok) { showToast("✅ Avoir soumis à l'ANCE"); fetchAvoirs(); }
                              else showToast(data.detail || "Erreur", "error");
                            }}>
                            📤 Soumettre ANCE
                          </button>
                        )}
                        {(a.status === "SUBMITTED" || a.status === "ACCEPTED") && (
                          <button style={{ ...S.actionBtn, background:"#16a34a", color:"#fff", border:"none", fontWeight:700 }}
                            onClick={async () => {
                              const societeId = getCookie("societe_id");
                              const res = await fetch(`${MIDDLEWARE_URL}/avoirs/${a.avoir_number}/submit-ttn?societe_id=${societeId}`, { method:"POST", headers:authHeaders() });
                              const data = await res.json();
                              if (res.ok) { showToast("✅ Avoir archivé — TTN reçu"); fetchAvoirs(); }
                              else showToast(data.detail || "Erreur", "error");
                            }}>
                            📡 Soumettre TTN
                          </button>
                        )}
                        <button style={{ ...S.actionBtn, ...S.actionBtnPdf }} onClick={() => downloadPDF(a.original_invoice_number)}>
                          📄 PDF original
                        </button>
                      </div>
                    </div>
                  );})}
                </div>
              )}

              {avoirModal?.mode === "create" && (() => {
                return (
                  <div style={S.overlay} onClick={() => setAvoirModal(null)}>
                    <div style={{ ...S.modal, maxWidth:420 }} onClick={e => e.stopPropagation()}>
                      <div style={S.modalHead}>
                        <div>
                          <div style={S.modalEyebrow}>Note de crédit</div>
                          <h3 style={S.modalTitle}>Créer un avoir</h3>
                        </div>
                        <button style={S.modalX} onClick={() => setAvoirModal(null)}>×</button>
                      </div>
                      <div style={S.modalBody}>
                        <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                          <div>
                            <label style={S.advFilterLabel}>N° Facture</label>
                            <input style={{ ...S.searchInput, border:"1.5px solid #E2E8F0", borderRadius:8, padding:"10px 14px", width:"100%", marginTop:6 }}
                              placeholder="Ex: 414" onChange={e => setAvoirModal(prev => ({ ...prev, factNum: e.target.value }))} />
                          </div>
                          <div>
                            <label style={S.advFilterLabel}>Motif</label>
                            <input style={{ ...S.searchInput, border:"1.5px solid #E2E8F0", borderRadius:8, padding:"10px 14px", width:"100%", marginTop:6 }}
                              placeholder="Ex: Erreur de facturation" onChange={e => setAvoirModal(prev => ({ ...prev, reason: e.target.value }))} />
                          </div>
                        </div>
                      </div>
                      <div style={S.modalFoot}>
                        <button style={S.btnCancel} onClick={() => setAvoirModal(null)}>Annuler</button>
                        <button style={{ ...S.btnPdf, background:"#e11d48" }} onClick={() => {
                            const num = String(avoirModal.factNum || "").replace(/[^0-9]/g, "");
                            handleCreateAvoir(num, avoirModal.reason || "");
                          }}>
                           Créer l'avoir
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>
            );
          })()}

                        {/* ══ MODAL PIN — Sélection certificat + code PIN ══ */}
              {pinModal && (
                <div style={S.overlay} onClick={() => setPinModal(null)}>
                  <div style={{ ...S.modal, maxWidth:420 }} onClick={e => e.stopPropagation()}>
                    <div style={S.modalHead}>
                      <div>
                        <div style={S.modalEyebrow}>Signature électronique — Digigo (TunTrust)</div>
                        <h3 style={S.modalTitle}>Signer la facture #{pinModal.numAffich || pinModal.numFacture}</h3>
                      </div>
                      <button style={S.modalX} onClick={() => setPinModal(null)}>×</button>
                    </div>
                    <div style={S.modalBody}>
                      <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                        <div style={{ background:"#f5f3ff", borderRadius:8, padding:"12px 14px", fontSize:"0.8rem", color:"#4c1d95", border:"1px solid #ddd6fe", lineHeight:1.6 }}>
                          <div style={{ fontWeight:700, marginBottom:6 }}>🔏 Signature qualifiée via Digigo</div>
                          <div>1. Une page <b>TunTrust / Digigo</b> va s'ouvrir dans un nouvel onglet.</div>
                          <div>2. Le signataire de la société saisit son <b>code PIN</b> puis le <b>code OTP</b> reçu par SMS.</div>
                          <div>3. Retour automatique ici : la facture passe en <b>Signée</b> après validation (XAdES + DSS).</div>
                        </div>
                        {pinModal.facture && (() => {
                          const F = pinModal.facture;
                          const dev = F.DeviseAbrege || "TND";
                          const m = v => (v === null || v === undefined || v === "") ? "—" : Number(v).toFixed(3) + " " + dev;
                          const rows = [
                            ["Client", `${F.NomClient || "—"} (${F.CodeClient || "—"})`],
                            ["Date", String(F.DateFacture || "—").slice(0, 10)],
                            ["Total HT", m(F.MontantHTRemise ?? F.MontantTotalHT)],
                            ["TVA", m(F.MontantTVA ?? ((F.MontantTotalTTC != null && (F.MontantHTRemise ?? F.MontantTotalHT) != null) ? Number(F.MontantTotalTTC) - Number(F.MontantHTRemise ?? F.MontantTotalHT) - Number(F.MontantTimbre ?? F.MontantTotalFett ?? 0) : F.MontantTVA1))],
                            ["Timbre", m(F.MontantTimbre ?? F.MontantTotalFett)],
                            ["Total TTC", m(F.MontantTotalTTC)],
                          ];
                          return (
                            <div style={{ background:"#f8fafc", borderRadius:8, padding:"12px 14px", fontSize:"0.8rem", color:"#0f172a", border:"1px solid #e2e8f0", lineHeight:1.8 }}>
                              <div style={{ fontWeight:700, marginBottom:6 }}>📄 Récapitulatif de la facture (contenu à signer)</div>
                              {rows.map(([k, v]) => (
                                <div key={k} style={{ display:"flex", justifyContent:"space-between", gap:12 }}>
                                  <span style={{ color:"#64748b" }}>{k}</span>
                                  <b style={k === "Total TTC" ? { color:"#7c3aed" } : undefined}>{v}</b>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                        <button type="button" style={{ ...S.btnCancel, width:"100%" }} onClick={async () => {
                          const w = window.open("", "_blank");
                          try {
                            const res = await fetch(`${MIDDLEWARE_URL}/factures/${pinModal.numFacture}/pdf?societe_id=${getCookie("societe_id")}`, { headers: authHeaders() });
                            if (!res.ok) throw new Error(`Erreur ${res.status}`);
                            const blob = new Blob([await res.blob()], { type: "application/pdf" });
                            const url = window.URL.createObjectURL(blob);
                            if (w) w.location.href = url; else window.open(url, "_blank");
                            setTimeout(() => window.URL.revokeObjectURL(url), 60000);
                          } catch (e) {
                            if (w) w.close();
                            showToast(`Erreur aperçu PDF : ${e.message}`, "error");
                          }
                        }}>👁️ Voir le PDF de la facture</button>
                        <div style={{ background:"#fffbeb", borderRadius:8, padding:"10px 14px", fontSize:"0.76rem", color:"#92400e", border:"1px solid #fde68a" }}>
                          ⚠️ Vérifiez le PDF avant de continuer. Ne cliquez qu'une seule fois : la transaction reste valable 60 min.
                        </div>

                      </div>
                    </div>
                    <div style={S.modalFoot}>
                      <button style={S.btnCancel} onClick={() => setPinModal(null)}>Annuler</button>
                      <button
                        style={{ ...S.btnPdf, background:"#7c3aed" }}
                        onClick={() => {
                          setPinModal(null);
                          handleAction(pinModal.numFacture, "sign", "Signer");
                        }}>
                         🔏 Ouvrir Digigo et signer
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {certUploadModal && (
  <div style={S.overlay} onClick={() => !certUploadModal.saving && setCertUploadModal(null)}>
    <div style={{ ...S.modal, maxWidth: 440 }} onClick={e => e.stopPropagation()}>
      <div style={S.modalHead}>
        <div>
          <div style={S.modalEyebrow}>Certificat ANCE</div>
          <h3 style={S.modalTitle}>Charger un certificat</h3>
        </div>
        <button style={S.modalX} onClick={() => setCertUploadModal(null)}>×</button>
      </div>
      <div style={S.modalBody}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div>
            <label style={S.advFilterLabel}>Fichier certificat (.pem / .p12)</label>
            <input type="file" accept=".pem,.p12" style={{ marginTop: 6, fontSize: "0.82rem" }}
              onChange={e => setCertUploadModal(prev => ({ ...prev, file: e.target.files[0] }))} />
          </div>
          <div>
            <label style={S.advFilterLabel}>Code PIN</label>
            <input type="password" style={{ ...S.searchInput, border: "1.5px solid #E2E8F0", borderRadius: 8, padding: "10px 14px", width: "100%", marginTop: 6, boxSizing: "border-box" }}
              placeholder="Entrez le code PIN"
              value={certUploadModal.pin}
              onChange={e => setCertUploadModal(prev => ({ ...prev, pin: e.target.value }))} />
          </div>
        </div>
      </div>
      <div style={S.modalFoot}>
        <button style={S.btnCancel} onClick={() => setCertUploadModal(null)} disabled={certUploadModal.saving}>Annuler</button>
        <button style={S.btnPrimary} onClick={uploadCertificate} disabled={certUploadModal.saving}>
          {certUploadModal.saving ? "⏳ Envoi..." : "📤 Charger"}
        </button>
      </div>
    </div>
  </div>
)}

{activeTab === "produits" && (
  <div style={S.tabContent}>
    <MesProduits apiUrl={MIDDLEWARE_URL} authHeaders={authHeaders} societeId={getCookie("societe_id")} exercice={userExercice} getNomClient={getNomClient} />
  </div>
)}


          {/* ══════════════ TAB: CLIENTS ══════════════ */}
          {activeTab === "clients" && (
            <div style={S.tabContent}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12 }}>
                <div style={S.sectionTitle}>👥 Mes Clients</div>
                <div style={{ display:"flex", gap:8 }}>
                  <div style={S.searchBox}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" style={{ flexShrink:0 }}>
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input style={S.searchInput} placeholder="Rechercher par nom, code, ville..."
                      value={clientSearch} onChange={e => setClientSearch(e.target.value)} />
                    {clientSearch && <button style={S.searchClear} onClick={() => setClientSearch("")}>×</button>}
                  </div>
                  <select 
                  style={{ ...S.searchInput, padding:"8px 12px", borderRadius:8, border:"1.5px solid #e8e8f0", fontSize:"0.82rem", cursor:"pointer" }}
                  value={clientSort} 
                  onChange={e => setClientSort(e.target.value)}>
                  <option value="nom">Trier par Nom</option>
                  <option value="factures">Trier par Nb Factures</option>
                  <option value="ca">Trier par Chiffre d'affaires</option>
                </select>
                <label style={{ ...S.btnSecondary, display:"flex", alignItems:"center", gap:6, cursor:"pointer" }}>
  <input type="checkbox" checked={clientsFactOnly} onChange={e => setClientsFactOnly(e.target.checked)} />
  Clients facturés uniquement
</label>

                  <button style={S.btnSecondary} onClick={fetchClients}>↻</button>
                </div>
              </div>

              {clientsLoading ? <StateBox loading /> : clients.length === 0 ? (
                <div style={S.emptyState}>
                  <div style={{ fontSize:"2rem" }}>👥</div>
                  <p style={{ color:"#64748B", fontWeight:600 }}>Aucun client disponible</p>
                </div>
              ) : (() => {
                  const codesFactures = new Set(factures.map(f => f.CodeClient));
                  const filtered = clients.filter(c => !clientsFactOnly || codesFactures.has(c.Code || c.CodeClient)).filter(c => !clientSearch ||
                    String(c.Code||c.CodeClient||"").toLowerCase().includes(clientSearch.toLowerCase()) ||
                    String(c.Nom||c.NomClient||"").toLowerCase().includes(clientSearch.toLowerCase()) ||
                    String(c.Ville||"").toLowerCase().includes(clientSearch.toLowerCase()) ||
                    String(c.Pays||"").toLowerCase().includes(clientSearch.toLowerCase())
                  ).sort((a, b) => {
                    if (clientSort === "factures") {
                      return factures.filter(f => f.CodeClient === (b.Code||b.CodeClient)).length
                          - factures.filter(f => f.CodeClient === (a.Code||a.CodeClient)).length;
                    }
                    if (clientSort === "ca") {
                      const totalA = factures.filter(f => f.CodeClient === (a.Code||a.CodeClient)).reduce((acc,f) => acc + (f.MontantTotalTTC||0), 0);
                      const totalB = factures.filter(f => f.CodeClient === (b.Code||b.CodeClient)).reduce((acc,f) => acc + (f.MontantTotalTTC||0), 0);
                      return totalB - totalA;
                    }
                    return (a.Nom||a.NomClient||"").localeCompare(b.Nom||b.NomClient||"");
                  });

                  return (
                    <>
                    <div style={{ fontSize:"0.78rem", color:"#64748B", fontWeight:600 }}>
                      <strong style={{ color:"#0F172A" }}>{filtered.length}</strong> {clientsFactOnly ? "client(s) facturé(s)" : "client(s)"}
                      {clients.length !== filtered.length && <span style={{ color:"#94A3B8" }}> sur {clients.length}</span>}
                    </div>
                    <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(320px,1fr))", gap:14 }}>
                      {filtered.map((c, i) => {
                        const code = c.Code || c.CodeClient || "—";
                        const nom  = c.Nom || c.NomClient || c.RaisonSociale || "—";
                        const abrege = c.NomAbrege || "";
                        const type = c.TypeClient || c.Type || "";
                        const pays = c.Pays || "";
                        const ville = c.Ville || c.VilleLivraison || "";
                        const tel  = c.Tel || c.Telephone || "";
                        const email = c.Email || "";
                        const eori = c.EORI || "";
                        const facturesClient = factures.filter(f => f.CodeClient === code);
                        const totalTTC = facturesClient.reduce((acc,f) => acc + (f.MontantTotalTTC != null ? Number(f.MontantTotalTTC) : 0), 0);
                        const totalHT = facturesClient.reduce((acc,f) => { const t = Number(f.MontantTotalTTC) || 0; const tb = Number(f.MontantTimbre ?? f.MontantTotalFett) || 0; const hb = Number(f.MontantTotalHT) || 0; const tx = f.TauxTVA1 != null ? Number(f.TauxTVA1) : null; let h = hb; if (f.MontantHTRemise != null) h = Number(f.MontantHTRemise); else if (tx != null && t) { const c = (t - tb) / (1 + tx / 100); h = c <= hb + 0.01 ? c : hb; } return acc + h; }, 0);

                        return (
                          <div key={i} style={{ background:"#fff", borderRadius:16, border:"1.5px solid #e8e8f0", padding:"20px", display:"flex", flexDirection:"column", gap:14, transition:"all 0.2s", cursor:"pointer" }}
                            className="facture-hover"
                            onClick={() => { setSearch(code); setActiveTab("factures"); }}>

                            {/* ── Header ── */}
                            <div style={{ display:"flex", alignItems:"flex-start", gap:12 }}>
                              <div style={{ width:48, height:48, borderRadius:13, background:"linear-gradient(135deg,#f0efff,#e8f4fd)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.4rem", flexShrink:0 }}>
                                👤
                              </div>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontWeight:800, color:"#6c63ff", fontSize:"0.95rem", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                  {nom}
                                </div>
                                {abrege && abrege !== nom && (
                                  <div style={{ fontSize:"0.7rem", color:"#94A3B8", marginTop:1 }}>{abrege}</div>
                                )}
                                <div style={{ display:"flex", gap:6, marginTop:4, flexWrap:"wrap" }}>
                                  <span style={{ background:"#f0efff", color:"#6c63ff", borderRadius:20, padding:"2px 8px", fontSize:"0.65rem", fontWeight:700 }}>
                                    {code}
                                  </span>
                                  {type && (
                                    <span style={{ background:"#f0fdf4", color:"#16a34a", borderRadius:20, padding:"2px 8px", fontSize:"0.65rem", fontWeight:700 }}>
                                      {type === "R" ? "🏢 Revendeur" : type === "P" ? "🏭 Producteur" : type}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* ── Infos grid ── */}
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                              {(ville || pays) && (
                                <div style={{ background:"#F8FAFC", borderRadius:9, padding:"9px 12px", gridColumn: !tel && !email ? "1/-1" : "auto" }}>
                                  <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em" }}>📍 Localisation</div>
                                  <div style={{ fontSize:"0.78rem", fontWeight:600, color:"#0F172A", marginTop:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>
                                    {[ville, pays].filter(Boolean).join(", ")}
                                  </div>
                                </div>
                              )}
                              {tel && (
                                <div style={{ background:"#F8FAFC", borderRadius:9, padding:"9px 12px" }}>
                                  <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em" }}>📞 Téléphone</div>
                                  <div style={{ fontSize:"0.78rem", fontWeight:600, color:"#0F172A", marginTop:3 }}>{tel}</div>
                                </div>
                              )}
                              {email && (
                                <div style={{ background:"#F8FAFC", borderRadius:9, padding:"9px 12px", gridColumn:"1/-1" }}>
                                  <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em" }}>✉️ Email</div>
                                  <div style={{ fontSize:"0.75rem", fontWeight:600, color:"#0891b2", marginTop:3, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{email}</div>
                                </div>
                              )}
                              {eori && (
                                <div style={{ background:"#fffbeb", borderRadius:9, padding:"9px 12px", gridColumn:"1/-1" }}>
                                  <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", letterSpacing:"0.05em" }}>🔖 EORI</div>
                                  <div style={{ fontSize:"0.72rem", fontWeight:600, color:"#d97706", marginTop:3, fontFamily:"monospace" }}>{eori}</div>
                                </div>
                              )}
                            </div>

                            {/* ── Stats factures ── */}
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, borderTop:"1px solid #F1F5F9", paddingTop:12 }}>
                              <div style={{ background:"#f0efff", borderRadius:9, padding:"9px 12px", textAlign:"center" }}>
                                <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase" }}>🧾 Factures</div>
                                <div style={{ fontSize:"1.3rem", fontWeight:800, color:"#6c63ff", lineHeight:1.2, marginTop:3 }}>{facturesClient.length}</div>
                              </div>
                                <div style={{ background:"#f0fdf4", borderRadius:9, padding:"9px 12px", textAlign:"center" }}>
                                <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase" }}>💰 Total HT</div>
                                <div style={{ fontSize:"0.85rem", fontWeight:800, color:"#0891b2", marginTop:3 }}>
                                  {totalHT > 0 ? formatMontant(totalHT) + " " + getDevise(facturesClient[0]) : "—"}
                                </div>
                                <div style={{ fontSize:"0.58rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", marginTop:6 }}>💵 Total TTC</div>
                                <div style={{ fontSize:"0.85rem", fontWeight:800, color:"#16a34a", marginTop:3 }}>
                                  {totalTTC > 0 ? formatMontant(totalTTC) + " " + getDevise(facturesClient[0]) : "—"}
                                </div>
                              </div>
                            </div>

                            <div style={{ fontSize:"0.7rem", color:"#6c63ff", fontWeight:700, textAlign:"right" }}>
                              Voir {facturesClient.length} facture{facturesClient.length !== 1 ? "s" : ""} →
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
            </div>
          )}

          {/* ══════════════ TAB: OFFLINE / SYNC ══════════════ */}
          {activeTab === "offline" && (
            <div style={S.tabContent}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
                <div style={S.sectionTitle}>📡 Mode Offline & Synchronisation</div>
                <button style={{ ...S.heroCta, padding:"8px 16px", fontSize:"0.8rem" }} onClick={handleManualSync}>
                  🔄 Sync Manuel
                </button>
              </div>

              {offlineLoading ? <StateBox loading /> : (
                <>
                  <div style={S.statsGrid}>
                    {[
                      { label:"Mode",          value: offlineStatus?.mode || "—",                        color: offlineStatus?.internet_available ? "#16a34a" : "#dc2626", icon: offlineStatus?.internet_available ? "🟢" : "🔴" },
                      { label:"En attente",    value: offlineStatus?.queue_stats?.pending  || 0,          color:"#f59e0b", icon:"⏳" },
                      { label:"Synchronisées", value: offlineStatus?.queue_stats?.synced   || 0,          color:"#16a34a", icon:"✅" },
                      { label:"Échouées",      value: offlineStatus?.queue_stats?.failed   || 0,          color:"#dc2626", icon:"❌" },
                    ].map((s, i) => (
                      <div key={i} style={S.statCard}>
                        <div style={S.statTop}><span style={S.statLabel}>{s.label}</span><div style={{ ...S.statDot, background:s.color }} /></div>
                        <div style={{ ...S.statValue, color:s.color, fontSize:"1.6rem" }}>{s.icon} {s.value}</div>
                      </div>
                    ))}
                  </div>

                  {alertes.length > 0 && (
                    <>
                      <div style={S.sectionTitle}>⚠️ Alertes système ({alertes.length})</div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {alertes.map((a, i) => (
                          <div key={i} style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:10, padding:"12px 16px", display:"flex", alignItems:"center", gap:12 }}>
                            <span style={{ fontSize:"1.2rem" }}>⚠️</span>
                            <div style={{ flex:1 }}>
                              <div style={{ fontWeight:700, fontSize:"0.84rem", color:"#92400e" }}>Facture #{a.invoice_number}</div>
                              <div style={{ fontSize:"0.75rem", color:"#b45309", marginTop:2 }}>{a.message || a.alert_type || "—"}</div>
                            </div>
                            <span style={{ fontSize:"0.68rem", color:"#d97706" }}>{a.created_at ? new Date(a.created_at).toLocaleString("fr-FR") : "—"}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  {offlineQueue.length > 0 && (
                    <>
                      <div style={S.sectionTitle}>📋 File d'attente offline ({offlineQueue.length})</div>
                      <div style={S.tableCard}>
                        {offlineQueue.map((q, i) => (
                          <div key={i} style={{ display:"flex", alignItems:"center", gap:16, padding:"12px 20px", borderBottom:"1px solid #F8FAFC" }}>
                            <span style={{ fontWeight:700, color:"#6c63ff", minWidth:70 }}>#{q.invoice_number}</span>
                            <span style={{ flex:1, fontSize:"0.78rem", color:"#64748B" }}>{q.created_at ? new Date(q.created_at).toLocaleDateString("fr-FR") : "—"}</span>
                            <span style={{ ...S.badge, color: q.status==="SYNCED" ? "#16a34a" : q.status==="FAILED" ? "#dc2626" : "#d97706", background: q.status==="SYNCED" ? "#dcfce7" : q.status==="FAILED" ? "#fee2e2" : "#fef3c7" }}>
                              {q.status}
                            </span>
                            <span style={{ fontSize:"0.72rem", color:"#94A3B8" }}>Retry: {q.retry_count || 0}</span>
                            {/* ── NOUVEAU: Retry individuel dans la file offline ── */}
                            {q.status === "FAILED" && (
                              <button
                                style={{ ...S.actionBtn, background:"#fff7ed", color:"#ea580c", border:"1px solid #fed7aa", fontSize:"0.72rem" }}
                                onClick={() => handleRetry(q.invoice_number)}
                                disabled={retryLoading[q.invoice_number]}>
                                {retryLoading[q.invoice_number] ? "⏳" : "🔄 Retry"}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* ══════════════ TAB: CERTIFICAT ══════════════ */}
          {activeTab === "certificat" && (
  <div style={S.tabContent}>
    <div style={S.sectionTitle}>🔒 Certificat ANCE</div>

    {certLoading ? <StateBox loading /> : certInfo ? (() => {
      const status = certInfo.certificate_info?.status;
      const ui = getCertUI(status);
      const days = certInfo.certificate_info?.days_until_expiration;
      const isMissing = status === "NOT_FOUND";
      const isTest = certInfo.environment === "simulation";
      const pct = days != null ? Math.max(4, Math.min(100, (days / DAYS_SCALE) * 100)) : 0;

      return (
        <>
          <div style={{ background: ui.bg, border: `1.5px solid ${ui.color}55`, borderRadius: 14, padding: "24px", display: "flex", alignItems: "center", gap: 20, flexWrap: "wrap" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "1.8rem", flexShrink: 0 }}>
              {ui.icon}
            </div>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontWeight: 800, fontSize: "1.1rem", color: "#0F172A", marginBottom: 4 }}>
                {ui.label}
              </div>
              {certInfo.recommendations?.map((r, i) => (
                <div key={i} style={{ fontSize: "0.82rem", color: "#374151", marginTop: 2 }}>{r}</div>
              ))}
            </div>
            <span style={{ ...S.badge, fontSize: "0.75rem", padding: "5px 12px", color: isTest ? "#d97706" : "#16a34a", background: isTest ? "#fef3c7" : "#dcfce7" }}>
              {isTest ? "🧪 Simulation" : "🏭 Production"}
            </span>
          </div>

          {!isMissing && days != null && (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", padding: "20px" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontSize: "2.4rem", fontWeight: 800, color: ui.color, lineHeight: 1 }}>{days}</span>
                <span style={{ color: "#94A3B8", fontWeight: 600 }}>jour{days > 1 ? "s" : ""} restant{days > 1 ? "s" : ""}</span>
              </div>
              <div style={{ height: 8, background: "#F1F5F9", borderRadius: 99, overflow: "hidden", marginTop: 12 }}>
                <div style={{ width: `${pct}%`, height: "100%", background: ui.color, borderRadius: 99, transition: "width .5s ease" }} />
              </div>
              {certInfo.certificate_info?.expiration_date && (
              <div style={{ marginTop: 8, fontSize: "0.75rem", color: "#94A3B8" }}>
                Expire le {new Date(certInfo.certificate_info.expiration_date).toLocaleDateString("fr-FR")}
              </div>
            )}
            </div>
          )}

          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", padding: "20px" }}>
            <div style={S.detailGrid}>
              {[
                ["Société", societeNom || `Société n°${certInfo.societe_id}`],
                ["Statut", `${ui.icon} ${ui.label}`],
                ["Environnement", isTest ? "Test (simulation)" : "Production"],
                ["Valide jusqu'au", certInfo.certificate_info?.expiration_date
                  ? new Date(certInfo.certificate_info.expiration_date).toLocaleDateString("fr-FR") : "—"],
                ["Détenteur", certInfo.certificate_info?.subject || "—"],
                ["Émetteur", certInfo.certificate_info?.issuer || "—"],
              ].map(([k, v]) => (
                <div key={k} style={S.detailRow}>
                  <span style={S.detailKey}>{k}</span>
                  <span style={S.detailVal}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {isMissing && (
  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", padding: "20px" }}>
    <div style={{ fontWeight: 700, color: "#0F172A", marginBottom: 10 }}>
      Comment obtenir un certificat de signature
    </div>
    <ol style={{ margin: "0 0 16px", paddingLeft: 20, color: "#334155", fontSize: "0.85rem", lineHeight: 1.8 }}>
      <li>Demandez le certificat auprès d'un prestataire agréé (ANCE, ID Trust).</li>
      <li>Chargez le fichier reçu ici, avec son code PIN.</li>
      <li>Vérifiez que le statut passe à « Certificat valide ».</li>
    </ol>
    <button style={S.heroCta} onClick={() => setCertUploadModal({ file: null, pin: "", saving: false })}>
      📤 Charger un certificat
    </button>
  </div>
)}
{!isMissing && (
  <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
    <button style={{ ...S.actionBtn, background: "#f0f9ff", color: "#0891b2", border: "1px solid #bae6fd", padding: "9px 16px" }}
      onClick={testCertificateSignature} disabled={certTesting}>
      {certTesting ? "⏳ Test en cours..." : "🧪 Tester la signature"}
    </button>
  </div>
)}

{certHistory.length > 0 && (
  <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E8ECEF", overflow: "hidden" }}>
    <div style={{ padding: "14px 20px", borderBottom: "1px solid #F1F5F9", fontWeight: 700, fontSize: "0.82rem", color: "#334155" }}>
      📋 Historique ({certHistory.length})
    </div>
    {certHistory.slice(0, 10).map((h, i) => (
      <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 20px", borderBottom: "1px solid #F8FAFC", fontSize: "0.8rem" }}>
        <span>{h.status === "success" ? "✅" : "❌"}</span>
        <span style={{ flex: 1, color: "#0F172A", fontWeight: 600 }}>
          {h.action === "SIGN" ? `Signature facture #${h.invoice_number}` : h.action === "UPLOAD" ? "Certificat chargé" : h.action}
        </span>
        <span style={{ color: "#94A3B8", fontSize: "0.72rem" }}>
          {h.date ? new Date(h.date).toLocaleString("fr-FR") : "—"}
        </span>
      </div>
    ))}
  </div>
)}

          <button style={{ ...S.btnSecondary, alignSelf: "flex-start" }} onClick={fetchCertificat}>
            ↻ Rafraîchir
          </button>
        </>
      );
    })() : (
      <div style={S.emptyState}>
        <div style={{ fontSize: "2rem" }}>🔒</div>
        <p style={{ color: "#64748B", fontWeight: 600 }}>Certificat non disponible</p>
      </div>
    )}
  </div>
)}
        </div>
        {/* ── FIN S.content ── */}

      </main>
          {/* ── FIN main ── */}

         {societeModalOpen && (
      <div style={S.overlay} onClick={() => setSocieteModalOpen(false)}>
        <div style={{ ...S.modal, maxWidth:480 }} onClick={e => e.stopPropagation()}>
          <div style={S.modalHead}>
            <div>
              <div style={{ fontSize:"0.68rem", fontWeight:700, color:"#6c63ff", textTransform:"uppercase" }}>✏️ Modifier ma société</div>
              <h3 style={S.modalTitle}>Ma Société</h3>
            </div>
            <button style={S.modalX} onClick={() => setSocieteModalOpen(false)}>×</button>
          </div>
          <div style={{ padding:"24px", display:"flex", flexDirection:"column", gap:16 }}>
            <div>
              <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", display:"block", marginBottom:10 }}>🖼️ Logo</label>
              <div style={{ display:"flex", alignItems:"center", gap:16 }}>
                <div style={{ width:64, height:64, borderRadius:14, border:"2px solid #e8e8f0", background:"#F8FAFC", display:"flex", alignItems:"center", justifyContent:"center", overflow:"hidden" }}>
                  {societeLogoPreview || societeLogo
                    ? <img src={societeLogoPreview || societeLogo} alt="logo" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                    : <span style={{ fontSize:"1.5rem" }}>🏭</span>}
                </div>
                <label style={{ padding:"8px 16px", background:"#f0efff", color:"#6c63ff", border:"1.5px solid #c7d2fe", borderRadius:9, cursor:"pointer", fontSize:"0.8rem", fontWeight:700 }}>
                  📁 Changer le logo
                  <input type="file" accept="image/*" style={{ display:"none" }} onChange={e => {
                    const file = e.target.files[0];
                    if (!file) return;
                    setSocieteLogoFile(file);
                    const reader = new FileReader();
                    reader.onload = ev => setSocieteLogoPreview(ev.target.result);
                    reader.readAsDataURL(file);
                  }} />
                </label>
              </div>
            </div>
            <div>
              <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", display:"block", marginBottom:6 }}>🏢 Nom</label>
              <div style={{ background:"#F8FAFC", border:"1.5px solid #E2E8F0", borderRadius:10, padding:"11px 16px", color:"#94A3B8", fontWeight:600 }}>{societeNom}</div>
<div style={{ fontSize:"0.65rem", color:"#CBD5E1", marginTop:4 }}>Le nom ne peut pas être modifié</div>
</div>
<div>
  <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", display:"block", marginBottom:6 }}>🧾 Matricule fiscal</label>
  {societeMf.editable ? (
    <input type="text" value={societeMf.mf} onChange={e => setSocieteMf(p => ({ ...p, mf: e.target.value }))} placeholder="Ex : 1234567A/A/M/000"
      style={{ width:"100%", background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:10, padding:"11px 16px", fontSize:"0.88rem", fontFamily:"inherit", outline:"none", boxSizing:"border-box" }} />
  ) : (
    <div style={{ background:"#F8FAFC", border:"1.5px solid #E2E8F0", borderRadius:10, padding:"11px 16px", color:"#94A3B8", fontWeight:600 }}>
      {societeMf.loading ? "Chargement..." : (societeMf.mf_normalise || societeMf.mf || "—")}
    </div>
  )}
  <div style={{ fontSize:"0.65rem", color:"#CBD5E1", marginTop:4 }}>
    {societeMf.editable ? "Modifiable (société standalone)" : `Issu de ${societeMf.source === "bridge" ? "DAS ERP / SaiSoc" : "la fiche société"} — non modifiable`}
  </div>
</div>

            <div>
              <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", display:"block", marginBottom:6 }}>📍 Adresse</label>
              <input style={{ width:"100%", background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:10, padding:"11px 16px", fontSize:"0.88rem", fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
                placeholder="Ex: Tunis, Tunisie" value={societeForm.adresse}
                onChange={e => setSocieteForm(prev => ({ ...prev, adresse: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", display:"block", marginBottom:6 }}>📞 Téléphone</label>
              <input style={{ width:"100%", background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:10, padding:"11px 16px", fontSize:"0.88rem", fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
                placeholder="Ex: +216 XX XXX XXX" value={societeForm.telephone}
                onChange={e => setSocieteForm(prev => ({ ...prev, telephone: e.target.value }))} />
            </div>
          </div>
          <div style={{ ...S.modalFoot, background:"#F8FAFC" }}>
            <button style={S.btnCancel} onClick={() => setSocieteModalOpen(false)}>Annuler</button>
            <button style={{ ...S.heroCta, padding:"10px 24px" }} disabled={societeSaving}
              onClick={async () => {
                setSocieteSaving(true);
                try {
                  const societeId = getCookie("societe_id");
                  if (societeLogoPreview) { localStorage.setItem(`logo_${societeId}`, societeLogoPreview); setSocieteLogo(societeLogoPreview); }
                  if (societeForm.adresse)   localStorage.setItem(`adresse_${societeId}`,   societeForm.adresse);
         if (societeForm.telephone) localStorage.setItem(`telephone_${societeId}`, societeForm.telephone);
if (societeMf.editable && societeMf.mf && societeMf.mf.trim()) {
  fetch(`${MIDDLEWARE_URL}/companies/${societeId}?tax_id=${encodeURIComponent(societeMf.mf.trim())}`, { method: "PUT", headers: authHeaders() })
    .then(async r => {
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.detail || `Erreur ${r.status}`); }
      showToast("✅ Matricule fiscal enregistré !");
    })
    .catch(err => showToast(`❌ Matricule fiscal : ${err.message}`));
}
showToast("✅ Société mise à jour !");

                  setSocieteModalOpen(false);
                } catch { showToast("❌ Erreur sauvegarde", "error"); }
                setSocieteSaving(false);
              }}>
              {societeSaving ? "⏳..." : "💾 Sauvegarder"}
            </button>
          </div>
        </div>
      </div>
    )}

    {profileModalOpen && (
  <div style={S.overlay} onClick={() => setProfileModalOpen(false)}>
    <div style={{ ...S.modal, maxWidth:520, maxHeight:"85vh", overflowY:"auto" }} onClick={e => e.stopPropagation()}>
      <div style={S.modalHead}>
        <div>
          <div style={{ fontSize:"0.68rem", fontWeight:700, color:"#6c63ff", textTransform:"uppercase" }}>👤 Modifier mon profil</div>
          <h3 style={S.modalTitle}>Mon Profil</h3>
        </div>
        <button style={S.modalX} onClick={() => setProfileModalOpen(false)}>×</button>
      </div>

      <div style={{ padding:"24px", display:"flex", flexDirection:"column", gap:20 }}>

        {/* ── Avatar + badges ── */}
        <div style={{ display:"flex", alignItems:"center", gap:16 }}>
          <div style={{ position:"relative" }}>
            <div style={{ width:64, height:64, borderRadius:"50%", background:"linear-gradient(135deg,#6c63ff,#7c3aed)", display:"flex", alignItems:"center", justifyContent:"center", color:"#fff", fontSize:"1.4rem", fontWeight:800, flexShrink:0, overflow:"hidden" }}>
              {profilePhotoPreview
                ? <img src={profilePhotoPreview} alt="avatar" style={{ width:"100%", height:"100%", objectFit:"cover" }} />
                : (userName || "U").charAt(0).toUpperCase()}
            </div>
            <label style={{ position:"absolute", bottom:-2, right:-2, width:24, height:24, borderRadius:"50%", background:"#fff", border:"2px solid #f0efff", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", fontSize:"0.7rem" }}>
              📷
              <input type="file" accept="image/*" style={{ display:"none" }} onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => setProfilePhotoPreview(ev.target.result);
                reader.readAsDataURL(file);
              }} />
            </label>
          </div>
          <div>
            <div style={{ fontWeight:800, fontSize:"1.1rem", color:"#1a1a2e" }}>{userName || "Utilisateur"}</div>
            <div style={{ display:"flex", gap:6, marginTop:6, flexWrap:"wrap" }}>
              <span style={{ background:"#F8FAFC", color:"#64748B", border:"1px solid #E2E8F0", borderRadius:20, padding:"3px 10px", fontSize:"0.68rem", fontWeight:700 }}>
                ○ Utilisateur
              </span>
              <span style={{ background:"#f0efff", color:"#6c63ff", border:"1px solid #c7d2fe", borderRadius:20, padding:"3px 10px", fontSize:"0.68rem", fontWeight:700 }}>
                🏢 {societeNom}
              </span>
              <span style={{ background:"#f0fdf4", color:"#16a34a", border:"1px solid #bbf7d0", borderRadius:20, padding:"3px 10px", fontSize:"0.68rem", fontWeight:700 }}>
                ✅ Actif
              </span>
            </div>
          </div>
        </div>

        {/* ── Infos personnelles ── */}
        <div>
          <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", marginBottom:10, paddingBottom:8, borderBottom:"1.5px solid #f0efff" }}>
            ✏️ Informations personnelles
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
            <div>
              <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", display:"block", marginBottom:6 }}>👤 Nom complet</label>
              <input style={{ width:"100%", background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:10, padding:"11px 16px", fontSize:"0.88rem", fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
                placeholder="Votre nom complet" value={profileForm.nom}
                onChange={e => setProfileForm(prev => ({ ...prev, nom: e.target.value }))} />
            </div>
            <div>
              <label style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", display:"block", marginBottom:6 }}>📞 Téléphone</label>
              <input style={{ width:"100%", background:"#fff", border:"1.5px solid #E2E8F0", borderRadius:10, padding:"11px 16px", fontSize:"0.88rem", fontFamily:"inherit", outline:"none", boxSizing:"border-box" }}
                placeholder="Ex: +216 XX XXX XXX" value={profileForm.tel}
                onChange={e => setProfileForm(prev => ({ ...prev, tel: e.target.value }))} />
            </div>
          </div>
        </div>

        {/* ── Sécurité ── */}
        <div>
          <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", marginBottom:10, paddingBottom:8, borderBottom:"1.5px solid #f0efff" }}>
            🔐 Sécurité
          </div>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ fontWeight:700, fontSize:"0.86rem", color:"#1a1a2e" }}>Mot de passe</div>
              <div style={{ fontSize:"0.76rem", color:"#94A3B8" }}>Modifier votre mot de passe de connexion</div>
            </div>
            <button style={{ padding:"8px 16px", borderRadius:9, background:"#f0efff", color:"#6c63ff", border:"1.5px solid #c7d2fe", cursor:"pointer", fontSize:"0.78rem", fontWeight:700, fontFamily:"inherit" }}
              onClick={() => { setProfileModalOpen(false); navigate("/forgot-password"); }}>
              Changer →
            </button>
          </div>
        </div>

        {/* ── Infos du compte ── */}
        <div>
          <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", marginBottom:10, paddingBottom:8, borderBottom:"1.5px solid #f0efff" }}>
            🏢 Informations du compte
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
            {[
              ["Société",     societeNom],
              ["Identifiant", `ID-${getCookie("societe_id") || "—"}`],
              ["Rôle",        "Utilisateur"],
              ["Statut",      "✅ Actif"],
            ].map(([k, v]) => (
              <div key={k} style={{ background:"#F8FAFC", borderRadius:9, padding:"10px 14px" }}>
                <div style={{ fontSize:"0.62rem", color:"#94A3B8", fontWeight:700, textTransform:"uppercase", marginBottom:3 }}>{k}</div>
                <div style={{ fontSize:"0.84rem", fontWeight:700, color:"#0F172A" }}>{v}</div>
              </div>
            ))}
          </div>
        </div>

      </div>

      <div style={{ ...S.modalFoot, background:"#F8FAFC" }}>
        <button style={S.btnCancel} onClick={() => setProfileModalOpen(false)}>Annuler</button>
        <button style={{ ...S.heroCta, padding:"10px 24px" }} disabled={profileSaving}
          onClick={async () => {
            setProfileSaving(true);
            const sid = getCookie("societe_id");
            localStorage.setItem(`tel_${sid}`, profileForm.tel);
            if (profilePhotoPreview) localStorage.setItem(`avatar_${sid}`, profilePhotoPreview);
            if (profileForm.nom) {
  const res = await fetch(`${BACKEND_URL}/api/auth/update-profile`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getCookie("token")}`,
    },
    body: JSON.stringify({ nom: profileForm.nom }),
  });

  if (!res.ok) {
    alert("Erreur lors de la sauvegarde du nom");
    setProfileSaving(false);
    return;
  }

  const data = await res.json();
  document.cookie = `name=${encodeURIComponent(data.nom)};path=/`;
  setUserName(data.nom);
}
            showToast("✅ Profil mis à jour !");
            setProfileModalOpen(false);
            setProfileSaving(false);
          }}>
          {profileSaving ? "⏳..." : "💾 Sauvegarder"}
        </button>
      </div>
    </div>
  </div>
)}

        {/* ════════════════ MODAL DÉTAIL FACTURE ════════════════ */}
        {detailModal && (
          <div style={S.overlay} onClick={() => setDetailModal(null)}>
            <div style={{ ...S.modal, maxWidth: "98vw", width: "98vw", margin: "0 auto" }} onClick={e => e.stopPropagation()}>
              <div style={S.modalHead}>
                <div>
                  <div style={S.modalEyebrow}>Facture électronique</div>
                  <h3 style={S.modalTitle}>#{detailModal.NumfactAffich || detailModal.NumFacture}</h3>
                </div>
                <button style={S.modalX} onClick={() => setDetailModal(null)}>×</button>
              </div>
              <div style={S.modalBody}>
                {(() => {
                  const st = getStatut(detailModal.Statut);
                  return <div style={{ ...S.badge, color:st.color, background:st.bg, marginBottom:16, fontSize:"0.82rem", padding:"6px 14px" }}>{st.icon} {st.label}</div>;
                })()}
                                <div style={S.detailGrid}>
                  {factureFull && buildDetailRows(factureFull, getNomClient).map(([k, v]) => (
                    <div key={k} style={S.detailRow}>
                      <span style={S.detailKey}>{k}</span>
                      <span style={S.detailVal}>{v || "—"}</span>
                    </div>
                  ))}
                  {/* ── LIGNES DETFACT ── */}
                  <div style={{ marginTop: 20, gridColumn: "1 / -1" }}>
                  <button
                    onClick={() => setShowLignes(v => !v)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      background: showLignes ? "#f0efff" : "#fff",
                      border: "1.5px solid #e8e8f0", borderRadius: 10,
                      padding: "10px 18px", cursor: "pointer",
                      fontSize: "0.82rem", fontWeight: 700,
                      color: "#6c63ff", width: "100%",
                      justifyContent: "space-between", marginBottom: 12
                    }}>
                    <span>📋 Lignes de la facture
                      {detailData?.total_lignes > 0 &&
                        <span style={{ marginLeft: 8, background: "#6c63ff", color: "#fff", borderRadius: 20, padding: "2px 10px", fontSize: "0.72rem" }}>
                          {detailData.total_lignes}
                        </span>
                      }
                    </span>
                    <span>{showLignes ? "▲ Masquer" : "▼ Voir les lignes"}</span>
                  </button>
                  {showLignes && (
                  <>
                  {detailLoading ? (
                    <div style={{ display: "flex", justifyContent: "center", padding: 24 }}>
                      <div style={{ width: 28, height: 28, border: "3px solid #f0efff", borderTop: "3px solid #6c63ff", borderRadius: "50%" }} className="spin" />
                    </div>
                  ) : detailData?.error ? (
                    <p style={{ color: "#dc2626", fontSize: "0.82rem" }}>❌ {detailData.error}</p>
                  ) : detailData?.lignes?.length > 0 ? (
                    <div style={{ overflowX: "auto", borderRadius: 10, border: "1.5px solid #e8e8f0", width: "100%" }}>
                      <table className="lignes-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.74rem", tableLayout: "fixed" }}>
                    <colgroup>
                      <col style={{ width: "22%" }} />
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "9%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "11%" }} />
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "7%" }} />
                      <col style={{ width: "8%" }} />
                      <col style={{ width: "10.5%" }} />
                      <col style={{ width: "10.5%" }} />
                    </colgroup>
                      <thead>
                  <tr style={{ background: "#f8f7ff" }}>
                    {["Désignation", "Qté", "P.U HT", "Remise", "Taux TVA", "Montant TVA", "DC", "FODEC", "Timbre", "Montant HT", "Montant TTC"].map((h, hi) => (
                      <th key={h} style={{ padding: "9px 12px", textAlign: hi === 0 ? "left" : "right", fontWeight: 700, color: "#6c63ff", fontSize: "0.68rem", textTransform: "uppercase", borderBottom: "1.5px solid #e8e8f0", whiteSpace: "nowrap" }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                      </thead>
                      <tbody>
                        {detailData.lignes.map((ligne, i) => (
                          <tr key={i} className="ligne-row" style={{ background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: "1px solid #f0f0f8" }}>
                            <td style={{ padding: "9px 12px", fontWeight: 600, color: "#1a1a2e", overflowWrap: "break-word", wordBreak: "break-word" }}>
                              {ligne.DesignationArticle || ligne.ReferenceArticle || "—"}
                            </td>
                            <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                              {ligne.QuantiteCommandee != null ? formatMontant(ligne.QuantiteCommandee) : "—"}
                            </td>
                            <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                              {ligne.PrixUnitaire != null ? formatMontant(ligne.PrixUnitaire) : "—"}
                            </td>
                            <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                              {ligne.Remise != null ? Number(ligne.Remise).toFixed(2) + "%" : "—"}
                            </td>
                            <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
                              {(() => { const ht = Number(ligne.PrixHTRemise ?? ligne.PrixTotal ?? 0); const ttc = ligne.PrixTTC != null ? Number(ligne.PrixTTC) : null; const mt = ligne.MontantTVA != null ? Number(ligne.MontantTVA) : null; let t = null; if (ht > 0 && ttc != null) t = (ttc / ht - 1) * 100; else if (ht > 0 && mt != null) t = mt / ht * 100; return t != null && isFinite(t) ? Number(t.toFixed(2)) + "%" : "—"; })()}
                            </td>
                            <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
{ligne.MontantTVA != null ? formatMontant(ligne.MontantTVA) : "—"}
                            </td>
                            <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
  {ligne.DroitConsommation != null ? formatMontant(ligne.DroitConsommation) : ligne.MontantDC != null ? formatMontant(ligne.MontantDC) : "—"}
</td>
                            <td style={{ padding: "9px 12px", color: "#64748b", textAlign: "right" }}>
  {ligne.FODEC != null ? formatMontant(ligne.FODEC) : ligne.MontantFodec != null ? formatMontant(ligne.MontantFodec) : "—"}
</td>
<td style={{ padding: "9px 12px", color: "#CBD5E1", textAlign: "right", fontStyle: "italic", verticalAlign: "middle" }}>
  —
</td>
<td style={{ padding: "9px 12px", fontWeight: 700, color: "#1a1a2e", textAlign: "right" }}>
  {ligne.PrixHTRemise != null ? formatMontant(ligne.PrixHTRemise) + " " + devFact : "—"}
</td>
                            <td style={{ padding: "9px 12px", fontWeight: 700, color: "#6c63ff", textAlign: "right" }}>
                              {ligne.PrixTTC != null ? formatMontant(ligne.PrixTTC) + " " + devFact : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
  <tr style={{ background: "#f0efff", borderTop: "2px solid #c7d2fe" }}>
        <td colSpan={5} style={{ padding: "10px 12px", fontWeight: 700, color: "#6c63ff", fontSize: "0.8rem", verticalAlign: "middle" }}>
      TOTAL — {detailData.total_lignes} ligne{detailData.total_lignes !== 1 ? "s" : ""}
    </td>
    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#6c63ff", textAlign: "right", verticalAlign: "middle", whiteSpace: "nowrap" }}>
     {formatMontant(detailData.lignes.reduce((acc, l) => acc + Number(l.MontantTVA || 0), 0)) + " " + devFact}
    </td>
    <td colSpan={2}></td>
    <td style={{ padding: "10px 12px", fontWeight: 700, color: "#8b5cf6", textAlign: "right", verticalAlign: "middle", whiteSpace: "nowrap" }}>
      {(() => {
        const t = factureFull?.MontantTimbre != null ? Number(factureFull.MontantTimbre)
                : factureFull?.MontantTotalFett != null ? Number(factureFull.MontantTotalFett) : null;
        return t != null ? formatMontant(t) + " " + devFact : "—";
      })()}
    </td>
    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#6c63ff", textAlign: "right", verticalAlign: "middle", whiteSpace: "nowrap" }}>
      {factureFull?.MontantHTRemise != null ? formatMontant(factureFull.MontantHTRemise) + " " + devFact
 : factureFull?.MontantTotalHT != null ? formatMontant(factureFull.MontantTotalHT) + " " + devFact : "—"}
    </td>
    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#6c63ff", textAlign: "right", verticalAlign: "middle", whiteSpace: "nowrap" }}>
      {factureFull?.MontantTotalTTC != null ? formatMontant(factureFull.MontantTotalTTC) + " " + devFact : "—"}
    </td>
  </tr>
</tfoot>
                      </table>
                    </div>
                  ) : detailData && detailData.lignes?.length === 0 ? (
                    <p style={{ color: "#94a3b8", fontSize: "0.82rem", textAlign: "center", padding: "16px 0" }}>
                      Aucune ligne de détail disponible
                    </p>
                  ) : null}
                  </>
                  )}
                </div>
                </div>
                <div style={{ marginTop:20 }}>
                  <div style={{ fontSize:"0.72rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:12 }}>Parcours TEIF</div>
                  <div style={S.workflowBarSteps}>
                    {WORKFLOW_STEPS.map((step, si) => {
                      const stepIdx = getWorkflowStep(detailModal.Statut);
                      const done = si<=stepIdx, current = si===stepIdx;
                      return (
                        <div key={si} style={S.workflowBarItem}>
                          {si > 0 && <div style={{ ...S.workflowBarLine, background: done ? step.color : "#e8e8f0" }} />}
                          <div style={{ ...S.workflowBarDot, background: done ? step.color : "#e8e8f0", boxShadow: current ? `0 0 0 3px ${step.color}40` : "none" }}>
                            <span style={{ fontSize:"0.6rem" }}>{step.icon}</span>
                          </div>
                          <div style={{ ...S.workflowBarDotLabel, color: done ? step.color : "#94A3B8", fontWeight: current ? 700 : 400 }}>{step.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div style={S.modalFoot}>
                <button style={S.btnCancel} onClick={() => setDetailModal(null)}>Fermer</button>
                {/* ── XML — visible seulement si facture validée ou plus ── */}
                {getStatutField(detailModal) !== "C" && (
                  <button style={{ ...S.btnCancel, background:"#f0f9ff", color:"#0891b2", border:"1px solid #bae6fd" }}
                    onClick={() => { downloadXML(detailModal.NumFacture); }}
                    disabled={xmlLoading[detailModal.NumFacture]}>
                    {xmlLoading[detailModal.NumFacture] ? "⏳" : "⬇️ XML"}
                  </button>
                )}
                <button style={S.btnPdf} onClick={() => { downloadPDF(detailModal.NumFacture); setDetailModal(null); }} disabled={pdfLoading[detailModal.NumFacture]}>
                  {pdfLoading[detailModal.NumFacture] ? "⏳..." : "📄 Télécharger PDF"}
                </button>
                <button style={S.btnTrace} onClick={() => { setDetailModal(null); fetchTrace(detailModal.NumFacture); }}>🔍 Traçabilité</button>
                {/* ── NOUVEAU: Retry dans modal si REJECTED ── */}
                {getStatutField(detailModal) === "REJECTED" && (
                  <button style={{ padding:"9px 16px", borderRadius:9, background:"#fff7ed", cursor:"pointer", fontSize:"0.82rem", fontWeight:700, color:"#ea580c", fontFamily:"inherit", border:"1px solid #fed7aa" }}
                    onClick={() => { handleRetry(detailModal.NumFacture); setDetailModal(null); }}
                    disabled={retryLoading[detailModal.NumFacture]}>
                    {retryLoading[detailModal.NumFacture] ? "⏳ Retry..." : "🔄 Retry ANCE"}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ════════ MODAL VALIDATION TEIF ════════ */}
    {teifModal && (
      <div style={S.overlay} onClick={() => setTeifModal(null)}>
        <div style={{ ...S.modal, maxWidth: 500 }} onClick={e => e.stopPropagation()}>

          <div style={S.modalHead}>
            <div>
              <div style={S.modalEyebrow}>Validation Format TEIF</div>
              <h3 style={S.modalTitle}>Facture #{teifModal.facture.NumfactAffich || teifModal.facture.NumFacture}</h3>
            </div>
            <button style={S.modalX} onClick={() => setTeifModal(null)}>×</button>
          </div>

          <div style={{ ...S.modalBody, overflowY: "auto", maxHeight: "60vh" }}>
            {/* Résumé global */}
            {(() => {
              const allOk = teifModal.checks.every(c => c.ok);
              const failCount = teifModal.checks.filter(c => !c.ok).length;
              return (
                <div style={{
                  background: allOk ? "#f0fdf4" : "#fff0f0",
                  border: `1.5px solid ${allOk ? "#bbf7d0" : "#fecaca"}`,
                  borderRadius: 10, padding: "14px 18px",
                  display: "flex", alignItems: "center", gap: 12, marginBottom: 16
                }}>
                  <span style={{ fontSize: "1.5rem" }}>{allOk ? "✅" : "❌"}</span>
                  <div>
                    <div style={{ fontWeight: 800, fontSize: "0.92rem", color: allOk ? "#16a34a" : "#dc2626" }}>
                      {allOk ? "Format TEIF valide — prêt pour signature" : `${failCount} champ(s) manquant(s)`}
                    </div>
                    <div style={{ fontSize: "0.75rem", color: "#64748B", marginTop: 3 }}>
                      {allOk
                        ? "Tous les champs obligatoires sont présents"
                        : "Corrigez les champs manquants avant de continuer"}
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* Liste des champs */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {teifModal.checks.map((c, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", borderRadius: 9,
                  background: c.ok ? "#f0fdf4" : "#fff0f0",
                  border: `1px solid ${c.ok ? "#bbf7d0" : "#fecaca"}`
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <span style={{ fontSize: "1rem" }}>{c.ok ? "✅" : "🔴"}</span>
                    <div>
                      <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "#0F172A" }}>
                        {c.label}
                      </div>
                <div style={{ fontSize:"0.68rem", color:"#94A3B8", fontFamily:"monospace", display:"flex", gap:6 }}>
                  <span>{c.champ}</span>
                  <span style={{
                    background: c.source === "SaiFact" ? "#f0efff" :
                                c.source === "SaiClie" ? "#f0fdf4" : "#fff7ed",
                    color:      c.source === "SaiFact" ? "#6c63ff" :
                                c.source === "SaiClie" ? "#16a34a" : "#d97706",
                    borderRadius: 4, padding: "0px 5px", fontSize: "0.6rem", fontWeight: 700
                  }}>
                    {c.source}
                  </span>
                </div>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                 {c.ok ? (
                  <span style={{ fontSize: "0.78rem", fontWeight: 600, color: "#16a34a" }}>
                    {String(c.value).slice(0, 20)}{String(c.value).length > 20 ? "..." : ""}
                  </span>
                ) : (
                  <input
                    placeholder={`Entrer ${c.label}`}
                    value={teifFixes[c.champ] || ""}
                    onChange={e => setTeifFixes(prev => ({ ...prev, [c.champ]: e.target.value }))}
                    style={{
                      border:"1.5px solid #fecaca", borderRadius:8,
                      padding:"4px 10px", fontSize:"0.78rem",
                      fontFamily:"inherit", outline:"none",
                      background:"#fff0f0", color:"#dc2626",
                      width:140
                    }}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.modalFoot}>
        <button style={S.btnCancel} onClick={() => setTeifModal(null)}>Fermer</button>

        {/* Bouton Signer — visible seulement si tout OK */}
        {teifModal.checks.every(c => c.ok || teifFixes[c.champ]) && (
          <button
            style={{ ...S.btnPrimary, background: "#7c3aed" }}
            disabled={teifLoading}
            onClick={async () => {
              setTeifLoading(true);
              await handleAction(teifModal.facture.NumFacture, "validate", "Valider");
              setTeifModal(null);
              setTeifLoading(false);
            }}>
            {teifLoading ? "⏳ Validation..." : "✅ Valider & Continuer"}
          </button>
        )}
      </div>
    </div>
  </div>
)}

      {/* ════════════════ TOAST ════════════════ */}
      {toast && (
        <div style={{ ...S.toast, ...(toast.type==="error" ? S.toastErr : S.toastOk) }} className="toast-in">
          <span style={S.toastDot} />
          {toast.msg}
        </div>
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// SUB-COMPOSANT — StateBox (loading / empty)
// ─────────────────────────────────────────────────────────────
function StateBox({ loading }) {
  return (
    <div style={{ background:"#fff", borderRadius:12, border:"1px solid #E8ECEF", padding:48, display:"flex", flexDirection:"column", alignItems:"center", gap:12 }}>
      {loading && <div style={{ width:32, height:32, border:"3px solid #F1F5F9", borderTop:"3px solid #6c63ff", borderRadius:"50%" }} className="spin" />}
      <p style={{ fontSize:"0.84rem", color:"#94A3B8", fontWeight:500 }}>{loading ? "Chargement..." : "Aucun résultat"}</p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// STYLES
// ─────────────────────────────────────────────────────────────
const S = {
  root: { display:"flex", minHeight:"100vh", background:"#F8FAFC", fontFamily:"'Outfit','Segoe UI',sans-serif", color:"#0F172A" },
  sidebar:        { flexShrink:0, background:"#fff", borderRight:"1.5px solid #ededf5", display:"flex", flexDirection:"column", position:"fixed", top:0, left:0, bottom:0, zIndex:100, transition:"width 0.25s ease", overflow:"hidden", boxShadow:"4px 0 20px rgba(108,99,255,0.06)" },
  brand:          { display:"flex", alignItems:"center", gap:10, padding:"20px 16px 12px", cursor:"pointer", borderBottom:"1px solid #F1F5F9", flexShrink:0 },
  brandMark:      { width:34, height:34, background:"#6c63ff", borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.1rem", flexShrink:0, color:"#fff" },
  brandName:      { fontSize:"1.05rem", fontWeight:800, color:"#6c63ff", whiteSpace:"nowrap" },
  collapseBtn:    { margin:"6px 10px 4px", alignSelf:"flex-end", flexShrink:0 },
  sidebarNav:     { flex:1, overflowY:"auto", padding:"4px 8px" },
  sectionLabel:   { display:"flex", alignItems:"center", gap:6, fontSize:"0.63rem", fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", padding:"10px 8px 3px", whiteSpace:"nowrap" },
  sectionDot:     { width:5, height:5, borderRadius:"50%", flexShrink:0 },
  sectionDivider: { height:1, background:"#ededf5", margin:"6px" },
  navItem:        { display:"flex", alignItems:"center", gap:10, padding:"8px 10px", borderRadius:8, cursor:"pointer", transition:"all 0.15s", marginBottom:1, fontSize:"0.84rem", fontWeight:500, whiteSpace:"nowrap", borderLeft:"3px solid transparent" },
  navIcon:        { fontSize:"0.95rem", width:20, textAlign:"center", flexShrink:0 },
  navLabel:       { overflow:"hidden", textOverflow:"ellipsis" },
  sidebarFooter:  { borderTop:"1.5px solid #ededf5", padding:"10px", flexShrink:0 },
  userCard:       { display:"flex", alignItems:"center", gap:8, padding:"8px 10px", borderRadius:10, background:"#F8FAFC", marginBottom:6 },
  userInitials:   { width:30, height:30, borderRadius:"50%", background:"#6c63ff", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.82rem", fontWeight:700, flexShrink:0 },
  userMeta:       { overflow:"hidden" },
  userNameText:   { fontSize:"0.8rem", fontWeight:600, color:"#1a1a2e", whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" },
  userRoleTag:    { fontSize:"0.65rem", color:"#94a3b8" },
  logoutBtn:      { display:"flex", alignItems:"center", gap:6, width:"100%", padding:"7px 10px", borderRadius:8, border:"none", background:"#fff0f0", cursor:"pointer", fontSize:"0.78rem", fontWeight:600, color:"#dc2626", fontFamily:"inherit", transition:"all 0.15s" },
  main:          { flex:1, display:"flex", flexDirection:"column", minHeight:"100vh", transition:"margin-left 0.25s ease" },
  header:        { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"16px 32px", background:"#fff", borderBottom:"1.5px solid #ededf5", position:"sticky", top:0, zIndex:50, boxShadow:"0 2px 12px rgba(108,99,255,0.05)" },
  headerEyebrow: { fontSize:"0.68rem", fontWeight:600, letterSpacing:"0.08em", color:"#94A3B8", textTransform:"uppercase", marginBottom:3 },
  headerTitle:   { fontSize:"1.2rem", fontWeight:800, color:"#1a1a2e" },
  headerActions: { display:"flex", gap:10, alignItems:"center" },
  topbarUser:    { display:"flex", alignItems:"center", gap:8 },
  topbarAvatar:  { width:32, height:32, borderRadius:"50%", background:"linear-gradient(135deg,#6c63ff,#5c8df6)", color:"#fff", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"0.88rem", fontWeight:700 },
  notifBtn:    { position:"relative", background:"#F8FAFC", border:"1.5px solid #E2E8F0", borderRadius:10, width:40, height:40, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", transition:"all 0.15s" },
  notifBadge:  { position:"absolute", top:-4, right:-4, background:"#dc2626", color:"#fff", fontSize:"0.55rem", fontWeight:800, minWidth:18, height:18, borderRadius:9, display:"flex", alignItems:"center", justifyContent:"center", border:"2px solid #fff" },
  notifPanel:  { position:"absolute", top:"calc(100% + 10px)", right:0, width:340, background:"#fff", borderRadius:14, border:"1.5px solid #E2E8F0", boxShadow:"0 16px 48px rgba(0,0,0,0.12)", zIndex:200, overflow:"hidden" },
  notifHeader: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"14px 16px", borderBottom:"1px solid #F1F5F9", background:"#F8FAFC" },
  notifAction: { background:"none", border:"none", cursor:"pointer", fontSize:"0.72rem", color:"#94A3B8", fontFamily:"inherit", fontWeight:600 },
  notifList:   { maxHeight:360, overflowY:"auto" },
  notifItem:   { padding:"12px 16px", borderBottom:"1px solid #F8FAFC", cursor:"pointer", transition:"background 0.1s" },
  notifEmpty:  { padding:"32px", display:"flex", flexDirection:"column", alignItems:"center", gap:4 },
  tabsBar:     { display:"flex", padding:"0 32px", background:"#fff", borderBottom:"1.5px solid #ededf5" },
  tabBtn:      { display:"flex", alignItems:"center", gap:6, padding:"12px 18px", border:"none", borderBottom:"2px solid transparent", background:"none", cursor:"pointer", fontSize:"0.83rem", fontWeight:600, color:"#64748B", fontFamily:"inherit", transition:"all 0.15s" },
  tabBtnActive:{ color:"#6c63ff", borderBottomColor:"#6c63ff" },
  tabCount:    { background:"#6c63ff", color:"#fff", fontSize:"0.6rem", fontWeight:800, padding:"1px 5px", borderRadius:20 },
  content:     { padding:"24px 32px", flex:1 },
  tabContent:  { display:"flex", flexDirection:"column", gap:20 },
  sectionTitle:{ fontSize:"0.8rem", fontWeight:700, color:"#64748B", textTransform:"uppercase", letterSpacing:"0.07em" },
  sectionDesc: { fontSize:"0.82rem", color:"#94A3B8", marginTop:-12, marginBottom:4 },
  hero:             { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"36px 32px", background:"linear-gradient(135deg,#f0efff 0%,#e0e7ff 35%,#dbeafe 70%,#e0f2fe 100%)", borderRadius:16, gap:32, overflow:"hidden", boxShadow:"0 8px 32px rgba(108,99,255,0.12)" },
  heroContent:      { flex:1, maxWidth:480 },
  heroTag:          { display:"inline-block", background:"#f0efff", color:"#6c63ff", border:"1px solid #c7d2fe", borderRadius:20, padding:"3px 12px", fontSize:"0.72rem", fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:12 },
  heroTitle:        { fontSize:"2rem", fontWeight:800, color:"#1a1a2e", lineHeight:1.2, marginBottom:10 },
  heroDesc:         { fontSize:"0.9rem", color:"#64748b", lineHeight:1.7, marginBottom:20 },
  heroCta:          { background:"#6c63ff", color:"#fff", border:"none", padding:"11px 22px", borderRadius:10, fontSize:"0.88rem", fontWeight:700, cursor:"pointer", boxShadow:"0 8px 24px rgba(108,99,255,0.3)", fontFamily:"inherit" },
  heroCtaSecondary: { background:"transparent", color:"#6c63ff", border:"2px solid #6c63ff", padding:"11px 22px", borderRadius:10, fontSize:"0.88rem", fontWeight:700, cursor:"pointer", fontFamily:"inherit" },
  heroVisual:       { flex:1, position:"relative", height:180, display:"flex", alignItems:"center", justifyContent:"center" },
  floatingCard:     { position:"absolute", background:"#fff", borderRadius:12, padding:"10px 16px", fontWeight:600, fontSize:"0.82rem", boxShadow:"0 8px 24px rgba(108,99,255,0.15)", border:"1.5px solid rgba(108,99,255,0.1)", animation:"float 3s ease-in-out infinite" },
  statsGrid: { display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 },
  statCard:  { background:"#fff", borderRadius:12, padding:"18px 20px", border:"1px solid #E8ECEF", transition:"all 0.2s", cursor:"pointer" },
  statTop:   { display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:10 },
  statLabel: { fontSize:"0.7rem", fontWeight:600, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.06em" },
  statDot:   { width:7, height:7, borderRadius:"50%" },
  statValue: { fontSize:"2rem", fontWeight:800, letterSpacing:"-0.04em", lineHeight:1 },
  statSub:   { fontSize:"0.7rem", color:"#CBD5E1", marginTop:4, fontWeight:500 },
  workflowSection:   { background:"#fff", borderRadius:16, border:"1px solid #E8ECEF", padding:"28px 32px" },
  workflowTimeline:  { display:"flex", alignItems:"flex-start", gap:0, marginTop:24, position:"relative", overflowX:"auto", paddingBottom:8 },
  workflowStep:      { display:"flex", flexDirection:"column", alignItems:"center", flex:1, minWidth:110, position:"relative", textAlign:"center" },
  workflowConnector: { position:"absolute", top:28, left:"50%", width:"100%", height:3, zIndex:0 },
  workflowCircle:    { width:56, height:56, borderRadius:"50%", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", position:"relative", zIndex:1, marginBottom:10 },
  workflowNum:       { fontSize:"0.55rem", fontWeight:800, color:"rgba(255,255,255,0.8)", position:"absolute", bottom:4, right:4 },
  workflowLabel:     { fontSize:"0.78rem", fontWeight:700, color:"#0F172A", marginBottom:2 },
  workflowSublabel:  { fontSize:"0.65rem", color:"#94A3B8", marginBottom:6 },
  workflowDesc:      { fontSize:"0.65rem", color:"#64748B", lineHeight:1.4, maxWidth:100 },
  workflowBar:         { background:"#F8FAFC", borderRadius:10, padding:"14px 16px" },
  workflowBarLabel:    { fontSize:"0.65rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:12 },
  workflowBarSteps:    { display:"flex", alignItems:"flex-start", justifyContent:"space-between" },
  workflowBarItem:     { display:"flex", flexDirection:"column", alignItems:"center", flex:1, position:"relative" },
  workflowBarLine:     { position:"absolute", top:14, right:"50%", width:"100%", height:2, zIndex:0 },
  workflowBarDot:      { width:28, height:28, borderRadius:"50%", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", zIndex:1, transition:"all 0.3s", marginBottom:6 },
  workflowBarDotLabel: { fontSize:"0.58rem", fontWeight:600, textAlign:"center", maxWidth:60, lineHeight:1.2 },
  recentList:  { background:"#fff", borderRadius:12, border:"1px solid #E8ECEF", overflow:"hidden" },
  recentRow:   { display:"flex", alignItems:"center", gap:12, padding:"13px 16px", borderBottom:"1px solid #F8FAFC", transition:"background 0.15s", cursor:"default" },
  recentNum:   { fontWeight:800, fontSize:"0.88rem", minWidth:70 },
  miniWorkflow:{ display:"flex", gap:3, flex:1 },
  miniStep:    { height:4, flex:1, borderRadius:2, transition:"all 0.2s" },
  recentDate:  { fontSize:"0.75rem", color:"#94A3B8", whiteSpace:"nowrap" },
  moreLink:    { padding:"12px 16px", fontSize:"0.78rem", color:"#6c63ff", fontWeight:600, cursor:"pointer", textAlign:"center" },
  factureCard:         { background:"#fff", borderRadius:14, border:"1.5px solid #e8e8f0", padding:"22px", display:"flex", flexDirection:"column", gap:16, transition:"all 0.2s" },
  factureHeader:       { display:"flex", justifyContent:"space-between", alignItems:"center" },
  factureMoneyRow:     { display:"flex", alignItems:"center", gap:0, background:"#F8FAFC", borderRadius:12, padding:"16px 20px" },
  factureMoneyItem:    { display:"flex", flexDirection:"column", gap:4, flex:1 },
  factureMoneyLabel:   { fontSize:"0.65rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.05em" },
  factureMoneyVal:     { fontSize:"0.9rem", fontWeight:800, color:"#0F172A" },
  factureMoneyDivider: { width:1, height:36, background:"#E2E8F0", margin:"0 16px", flexShrink:0 },
  factureActions:      { display:"flex", gap:8, flexWrap:"wrap" },
  advFiltersPanel:      { background:"#fff", borderRadius:12, border:"1.5px solid #c7d2fe", padding:"20px 24px", display:"flex", flexDirection:"column", gap:16 },
  advFiltersTitle:      { fontSize:"0.8rem", fontWeight:700, color:"#6c63ff", display:"flex", alignItems:"center", justifyContent:"space-between" },
  advFiltersGrid:       { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:12 },
  advFilterGroup:       { display:"flex", flexDirection:"column", gap:6 },
  advFilterLabel:       { fontSize:"0.65rem", fontWeight:700, color:"#94A3B8", textTransform:"uppercase", letterSpacing:"0.05em" },
  advFilterInput:       { display:"flex", alignItems:"center", gap:8, background:"#F8FAFC", border:"1.5px solid #E2E8F0", borderRadius:8, padding:"0 10px", height:38 },
  advFilterInputField:  { flex:1, border:"none", outline:"none", fontSize:"0.82rem", background:"transparent", color:"#0F172A", fontFamily:"inherit" },
  filterActiveDot:      { display:"inline-block", width:6, height:6, borderRadius:"50%", background:"#6c63ff", marginLeft:4, verticalAlign:"middle" },
  resetFiltersBtn:      { background:"none", border:"none", cursor:"pointer", fontSize:"0.72rem", color:"#94A3B8", fontFamily:"inherit", fontWeight:600, display:"flex", alignItems:"center", gap:4 },
  resultsBar: { display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:8 },
  exportMenu:     { position:"absolute", top:"calc(100% + 8px)", right:0, background:"#fff", borderRadius:12, border:"1.5px solid #E2E8F0", boxShadow:"0 12px 32px rgba(0,0,0,0.1)", zIndex:100, overflow:"hidden", minWidth:200 },
  exportMenuItem: { display:"flex", alignItems:"center", gap:12, padding:"12px 16px", width:"100%", background:"none", border:"none", cursor:"pointer", fontFamily:"inherit", textAlign:"left", transition:"background 0.1s", borderBottom:"1px solid #F1F5F9" },
  toolbar:          { display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" },
  searchBox:        { flex:1, minWidth:220, display:"flex", alignItems:"center", gap:8, background:"#fff", border:"1.5px solid #e8e8f0", borderRadius:9, padding:"0 12px", height:40 },
  searchInput:      { flex:1, border:"none", outline:"none", fontSize:"0.84rem", background:"transparent", color:"#0F172A", fontFamily:"inherit" },
  searchClear:      { background:"none", border:"none", cursor:"pointer", color:"#94A3B8", fontSize:"1.1rem" },
  filterGroup:      { display:"flex", gap:6, flexWrap:"wrap" },
  filterChip:       { padding:"7px 12px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", fontSize:"0.75rem", fontWeight:600, color:"#64748B", fontFamily:"inherit", transition:"all 0.15s" },
  filterChipActive: { background:"#6c63ff", color:"#fff", borderColor:"#6c63ff" },
  traceSelectList: { display:"flex", flexDirection:"column", gap:8 },
  traceSelectRow:  { display:"flex", alignItems:"center", gap:16, padding:"14px 20px", background:"#fff", borderRadius:10, border:"1px solid #E8ECEF", cursor:"pointer", transition:"all 0.15s" },
  historyList:     { display:"flex", flexDirection:"column", background:"#fff", borderRadius:12, border:"1px solid #E8ECEF", overflow:"hidden" },
  historyRow:      { display:"flex", alignItems:"flex-start", gap:12, padding:"14px 20px", borderBottom:"1px solid #F8FAFC" },
  historyDot:      { width:8, height:8, borderRadius:"50%", flexShrink:0, marginTop:5 },
  btnSecondary:  { display:"flex", alignItems:"center", gap:6, padding:"8px 14px", borderRadius:8, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", fontSize:"0.8rem", fontWeight:600, color:"#334155", fontFamily:"inherit", transition:"all 0.15s" },
  actionBtn:     { padding:"6px 12px", borderRadius:7, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", fontSize:"0.75rem", fontWeight:600, color:"#334155", fontFamily:"inherit", transition:"all 0.15s" },
  actionBtnPdf:  { background:"#fff0f0", color:"#dc2626", border:"1px solid #fecaca" },
  actionBtnTrace:{ background:"#f0f9ff", color:"#0891b2", border:"1px solid #bae6fd" },
  badge:         { display:"inline-flex", alignItems:"center", gap:4, padding:"3px 10px", borderRadius:20, fontSize:"0.72rem", fontWeight:700, whiteSpace:"nowrap" },
  tableCard:  { background:"#fff", borderRadius:12, border:"1px solid #E8ECEF", overflow:"hidden" },
  emptyState: { background:"#fff", borderRadius:12, border:"1px solid #E8ECEF", padding:"40px", display:"flex", flexDirection:"column", alignItems:"center", gap:10 },
  overlay:      { position:"fixed", inset:0, background:"rgba(15,23,42,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", backdropFilter:"blur(4px)" },
  modal:        { background:"#fff", borderRadius:16, width:"90%", maxWidth:560, boxShadow:"0 24px 64px rgba(0,0,0,0.15)", overflow:"hidden", maxHeight:"90vh", overflowY:"auto" },
  modalHead:    { display:"flex", justifyContent:"space-between", alignItems:"flex-start", padding:"20px 24px", borderBottom:"1px solid #F1F5F9", position:"sticky", top:0, background:"#fff", zIndex:10 },
  modalEyebrow: { fontSize:"0.68rem", fontWeight:700, letterSpacing:"0.1em", color:"#94A3B8", textTransform:"uppercase", marginBottom:3 },
  modalTitle:   { fontSize:"1.1rem", fontWeight:800, color:"#0F172A" },
  modalX:       { background:"#F8FAFC", border:"1px solid #E2E8F0", width:28, height:28, borderRadius:7, cursor:"pointer", fontSize:"1rem", display:"flex", alignItems:"center", justifyContent:"center", color:"#64748B", flexShrink:0 },
  modalBody:    { padding:"20px 24px" },
  modalFoot:    { display:"flex", gap:8, padding:"16px 24px", borderTop:"1px solid #F1F5F9", justifyContent:"flex-end", flexWrap:"wrap" },
  btnCancel:    { padding:"9px 16px", borderRadius:9, border:"1px solid #E2E8F0", background:"#fff", cursor:"pointer", fontSize:"0.82rem", fontWeight:600, color:"#64748B", fontFamily:"inherit" },
  btnPdf:       { padding:"9px 16px", borderRadius:9, border:"none", background:"#dc2626", cursor:"pointer", fontSize:"0.82rem", fontWeight:700, color:"#fff", fontFamily:"inherit" },
  btnTrace:     { padding:"9px 16px", borderRadius:9, border:"none", background:"#0ea5e9", cursor:"pointer", fontSize:"0.82rem", fontWeight:700, color:"#fff", fontFamily:"inherit" },
  btnPrimary: { padding:"9px 16px", borderRadius:9, border:"none", background:"#6c63ff", cursor:"pointer", fontSize:"0.82rem", fontWeight:700, color:"#fff", fontFamily:"inherit" },
  detailGrid:   { display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 },
  detailRow:    { background:"#F8FAFC", borderRadius:8, padding:"10px 14px" },
  detailKey:    { display:"block", fontSize:"0.65rem", color:"#94A3B8", fontWeight:600, textTransform:"uppercase", letterSpacing:"0.06em", marginBottom:2 },
  detailVal:    { display:"block", fontSize:"0.86rem", fontWeight:700, color:"#0F172A" },
  toast:   { position:"fixed", bottom:24, right:24, display:"flex", alignItems:"center", gap:10, padding:"12px 20px", borderRadius:10, fontSize:"0.84rem", fontWeight:600, boxShadow:"0 8px 32px rgba(0,0,0,0.12)", zIndex:2000 },
  toastOk: { background:"#0F172A", color:"#fff" },
  toastErr:{ background:"#EF4444", color:"#fff" },
  toastDot:{ width:6, height:6, borderRadius:"50%", background:"rgba(255,255,255,0.5)", flexShrink:0 },
};

// ─────────────────────────────────────────────────────────────
// CSS GLOBAL
// ─────────────────────────────────────────────────────────────
const css = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body {
    font-family: 'Outfit', sans-serif;
    overflow-x: hidden;
    width: 100%;
    max-width: 100vw;
  }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: #f1f1f1; }
  ::-webkit-scrollbar-thumb { background: #c7d2fe; border-radius: 3px; }
    /* ══ MENU — animations style vidéo ══ */
  .menu-btn {
    width: 36px; height: 36px; border: none; border-radius: 12px;
    background: #6c63ff; color: #fff; font-size: 1rem; cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    box-shadow: 0 6px 18px rgba(108,99,255,0.45);
    transition: transform .3s cubic-bezier(.2,1.4,.4,1), box-shadow .25s;
  }
  .menu-btn:hover  { transform: rotate(-8deg) scale(1.08); box-shadow: 0 8px 24px rgba(108,99,255,0.6); }
  .menu-btn:active { transform: rotate(-14deg) scale(.92); }
  .menu-ico { display: inline-block; transition: transform .35s cubic-bezier(.2,1.4,.4,1); }

  .nav-item { position: relative; overflow: hidden; animation: navIn .4s cubic-bezier(.2,.9,.3,1) backwards; animation-delay: calc(var(--i) * 35ms); }
  .nav-item::before {
    content: ""; position: absolute; inset: 0; background: var(--c);
    transform: scaleX(0); transform-origin: left;
    transition: transform .3s cubic-bezier(.2,.9,.3,1);
  }
      .ligne-row td {
    border-right: 1px solid #f4f4fa;
  }
  .ligne-row td:last-child {
    border-right: none;
  }
  .nav-item > * { position: relative; z-index: 1; }
  .nav-item:not(.active):hover::before { transform: scaleX(1); }
  .nav-item:not(.active):hover { color: #fff !important; }
  .nav-ico { transition: transform .25s cubic-bezier(.2,1.4,.4,1); }
  .nav-item:hover .nav-ico { transform: translateX(3px) scale(1.15); }
  @keyframes navIn { from { opacity: 0; transform: translateX(-14px); } to { opacity: 1; transform: none; } }
  @media (prefers-reduced-motion: reduce) {
    .nav-item, .nav-item::before, .nav-ico, .menu-btn, .menu-ico { animation: none !important; transition: none !important; }
  }
  .btn-hover:hover     { background: #F8FAFC !important; border-color: #CBD5E1 !important; }
  .logout-hover:hover  { background: #fee2e2 !important; }
  .stat-hover:hover    { box-shadow: 0 4px 20px rgba(0,0,0,0.08); transform: translateY(-2px); }
  .facture-hover:hover { border-color: #6c63ff !important; box-shadow: 0 8px 24px rgba(108,99,255,0.1) !important; }
  .toast-in    { animation: toastIn 0.3s ease; }
  .notif-pulse { animation: notifPulse 2s ease-in-out infinite; }
  @keyframes notifPulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.15); } }
  @keyframes toastIn    { from { opacity:0; transform:translateY(10px); } to { opacity:1; transform:translateY(0); } }
    @keyframes skeletonPulse { 0%,100% { opacity: 0.5; } 50% { opacity: 1; } }
  .skeleton {
    display: inline-block;
    background: linear-gradient(90deg, #E8ECEF 25%, #F1F5F9 50%, #E8ECEF 75%);
    background-size: 200% 100%;
    border-radius: 6px;
    animation: skeletonPulse 1.4s ease-in-out infinite;
  }
  @keyframes float      { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
  input[type="date"]::-webkit-calendar-picker-indicator { opacity: 0.5; cursor: pointer; }

  /* ══ MENU MOBILE — bouton hamburger + overlay ══ */
    .mobile-menu-btn {
    display: none;
    align-items: center; justify-content: center;
    width: 38px; height: 38px; border-radius: 12px; border: none;
    background: #6c63ff; color: #fff; font-size: 1.1rem; cursor: pointer; flex-shrink: 0;
    box-shadow: 0 6px 18px rgba(108,99,255,0.45);
    transition: transform .3s cubic-bezier(.2,1.4,.4,1);
  }
  .mobile-menu-btn:active { transform: rotate(-14deg) scale(.92); }
  .mobile-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15,23,42,0.55);
    z-index: 998;
    animation: toastIn 0.2s ease;
  }

  /* ══ RESPONSIVE MOBILE ══ */
  @media (max-width: 900px) {
    .mobile-menu-btn { display: flex !important; }
    .sidebar-collapse-btn { display: none !important; }

    .app-sidebar {
      width: 240px !important;
      transform: translateX(-100%);
            transition: transform 0.35s cubic-bezier(.2,.9,.3,1);
      z-index: 999 !important;
      box-shadow: 8px 0 32px rgba(0,0,0,0.2) !important;
    }
    .app-sidebar.mobile-open {
      transform: translateX(0);
    }
    .app-sidebar:not(.mobile-open) .nav-item { animation: none; }
    .app-sidebar .nav-label,
    .app-sidebar span[style*="font-size: 1.05rem"] {
      display: inline !important;
    }

    .app-main {
      margin-left: 0 !important;
    }
    .app-header {
      padding: 12px 16px !important;
      flex-wrap: wrap !important;
      gap: 10px !important;
    }
    .header-actions {
      gap: 6px !important;
      flex-wrap: wrap !important;
      width: 100% !important;
      justify-content: flex-start !important;
    }
    .header-actions button span,
    .header-actions > div > span {
      display: none !important;
    }
    .home-hero {
    flex-direction: column !important;
    padding: 20px 16px !important;
    text-align: center !important;
    width: 100% !important;
    box-sizing: border-box !important;
  }
    .stats-grid {
      grid-template-columns: repeat(2,1fr) !important;
    }
    .factures-toolbar {
      flex-direction: column !important;
      align-items: stretch !important;
    }
        .facture-money-row {
      flex-wrap: wrap !important;
      gap: 10px !important;
    }

    /* Bannière alerte + boutons hero — empiler sur mobile */
    .alert-banner {
      flex-direction: column !important;
      align-items: stretch !important;
    }
    .alert-banner button {
      width: 100% !important;
    }
    .hero-buttons {
      flex-direction: column !important;
      width: 100% !important;
    }
    .hero-buttons button {
      width: 100% !important;
      justify-content: center !important;
    }
    .content-wrap {
      padding: 16px !important;
      width: 100% !important;
      box-sizing: border-box !important;
      overflow-x: hidden !important;
    }
  }

@media (max-width: 480px) {
  .stats-grid {
    grid-template-columns: 1fr !important;
  }
  .app-header h1 {
    font-size: 1rem !important;
  }
  .home-hero-visual {
    display: none !important;
  }
  .home-hero {
    padding: 20px 18px !important;
  }
  .home-hero-content {
    max-width: 100% !important;
  }
  .app-header {
    padding: 10px 12px !important;
  }
  .app-header > div:first-child {
    min-width: 0 !important;
    overflow: hidden !important;
  }
  .app-header h1 {
    white-space: normal !important;
  }
  .home-hero-title {
    font-size: 1.3rem !important;
  }
}
`;

export default Home;